import { zipSync, unzipSync } from 'fflate';
import {
  exportMarketingSeoOverrides,
  mergeImportedMarketingSeoBundle,
  type MarketingSeoExportBundle,
} from './marketingSeoExport.js';
import type { MarketingSeoPreviewBranding } from './marketingSeoPreview.js';
import { buildTenantLlmsTxtPreview } from './marketingLlmsTxt.js';
import {
  buildTenantRobotsTxtPreview,
  resolveBlockedAiCrawlerUserAgents,
  resolveRobotsAiCrawlerPolicy,
} from './marketingRobotsTxt.js';
import { buildTenantMarketingSitemapPreview } from './marketingSitemapXml.js';
import type {
  PublicMarketingConfig,
  RobotsAiCrawlerPolicy,
  TenantSeoBundleHistoryEntry,
  TenantSeoBundleHistoryPinNoteAuditAction,
  TenantSeoBundleHistoryPinNoteAuditEntry,
  TenantSeoBundleRollbackSnapshot,
} from './publicMarketingConfig.js';
import {
  PUBLIC_MARKETING_AUTO_APPLY_SEO_BUNDLE_TEMPLATE_KEY,
  PUBLIC_MARKETING_PLATFORM_SEO_BUNDLE_TEMPLATE_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_PRESETS_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_BY_SOURCE_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_BY_SOURCE_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_PLACEHOLDER_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_MAX_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_SAVED_VIEWS_LIMIT_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_PIN_POLICY_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_DEFAULT_PIN_NOTE_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_PIN_NOTE_BY_SOURCE_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESETS_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PLATFORM_PRESET_LIMIT_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_LABEL_SEARCH_MIN_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_GROUP_BY_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_EXPAND_LIMIT_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_SIZE_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_PERSIST_ENABLED_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_JUMP_AMBIGUOUS_LIMIT_KEY,
  PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_PRUNE_CRON_KEY,
  fetchPlatformPublicMarketingDefault,
  parsePublicMarketingConfig,
  resolvePublicMarketingConfig,
  serializePublicMarketingConfig,
} from './publicMarketingConfig.js';
import { supabase } from '../lib/supabase.js';
import {
  exportScheduledJobRunLog,
  fetchScheduledJobRunList,
  type ScheduledJobRunLogRow,
} from './enquiryRecommendationAnalytics.js';

export const TENANT_SEO_BUNDLE_HISTORY_PRUNE_JOB_KEY = 'tenant_seo_bundle_history_prune';

export const TENANT_SEO_BUNDLE_HISTORY_SOURCES: readonly TenantSeoBundleHistoryEntry['source'][] = [
  'import',
  'platform_template',
  'org_signup',
  'rollback',
  'reapply',
] as const;

export type TenantSeoBundleHistorySourceFilter = 'all' | TenantSeoBundleHistoryEntry['source'];

export type TenantSeoBundleHistoryBundleFilter = 'all' | 'with_bundle' | 'orphan';

export type TenantSeoBundleHistorySnapshotFilter = 'all' | 'with_snapshot' | 'without_snapshot';

export type TenantSeoBundleHistoryQueryScope = 'all' | 'pin_note' | 'pin_note_audit';

export type TenantSeoBundleHistoryPinNoteAuditActionFilter = 'all' | TenantSeoBundleHistoryPinNoteAuditAction;

export type TenantSeoBundleHistoryFilter = {
  source?: TenantSeoBundleHistorySourceFilter;
  bundle?: TenantSeoBundleHistoryBundleFilter;
  snapshot?: TenantSeoBundleHistorySnapshotFilter;
  /** Case-insensitive match against label, note, source, branding, timestamps. */
  query?: string;
  /** When `pin_note`, query only matches the entry pin note field. */
  queryScope?: TenantSeoBundleHistoryQueryScope;
  pinned?: 'all' | 'pinned' | 'unpinned';
  /** Match entries whose latest pin note audit action equals this value. */
  pinNoteAuditAction?: TenantSeoBundleHistoryPinNoteAuditActionFilter;
};

export const DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_PLACEHOLDER =
  'Optional note (e.g. campaign, ticket, reason)';

export const TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_PRESET_LIMIT = 20;

export type TenantSeoBundleHistorySourceAuditNotes = Partial<
  Record<TenantSeoBundleHistoryEntry['source'], string>
>;

export type TenantSeoBundleHistorySourceAuditLabels = Partial<
  Record<TenantSeoBundleHistoryEntry['source'], string>
>;

export type TenantSeoBundleHistorySourcePinNotes = Partial<
  Record<TenantSeoBundleHistoryEntry['source'], string>
>;

export const DEFAULT_TENANT_SEO_BUNDLE_HISTORY_PIN_NOTE_BY_SOURCE: TenantSeoBundleHistorySourcePinNotes = {
  import: '{source} retention · {changes} changes',
  platform_template: 'Platform template hold · {changes} changes',
  org_signup: 'Org signup retention · {changes} changes',
  rollback: 'Rollback hold · {changes} changes',
  reapply: 'Re-apply retention · {changes} changes',
};

export const TENANT_SEO_BUNDLE_HISTORY_PIN_NOTE_AUDIT_LIMIT = 5;

export const DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_BY_SOURCE: TenantSeoBundleHistorySourceAuditNotes =
  {
    import: 'Manual bundle import',
    platform_template: 'Applied platform SEO template',
    org_signup: 'Auto-applied on organization signup',
    rollback: 'Restored from prior rollback snapshot',
    reapply: 'Re-applied stored SEO bundle',
  };

export const DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_BY_SOURCE: TenantSeoBundleHistorySourceAuditLabels =
  {
    import: 'Manual import · {changes} changes',
    platform_template: 'Platform template · {changes} changes',
    org_signup: 'Org signup auto-apply · {changes} changes',
    rollback: 'Rollback recovery · {changes} changes',
    reapply: 'Re-apply bundle · {changes} changes',
  };

export const DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_PRESETS = [
  'Q2 SEO refresh',
  'Platform template apply',
  'Rollback recovery',
] as const;

export function normalizeTenantSeoBundleHistoryAuditNotesBySource(
  value: unknown,
): TenantSeoBundleHistorySourceAuditNotes {
  const raw =
    value && typeof value === 'object'
      ? ((value as Record<string, unknown>).notes_by_source ??
        (value as Record<string, unknown>).notesBySource ??
        value)
      : {};
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_BY_SOURCE };
  }
  const notes: TenantSeoBundleHistorySourceAuditNotes = {
    ...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_BY_SOURCE,
  };
  for (const source of TENANT_SEO_BUNDLE_HISTORY_SOURCES) {
    const note = String((raw as Record<string, unknown>)[source] ?? '').trim();
    if (note) notes[source] = note.slice(0, 500);
    else delete notes[source];
  }
  return notes;
}

export function resolveTenantSeoBundleHistoryAuditNote(
  source: TenantSeoBundleHistoryEntry['source'],
  platformNotes?: TenantSeoBundleHistorySourceAuditNotes,
  orgNotes?: TenantSeoBundleHistorySourceAuditNotes,
): string | undefined {
  const orgNote = orgNotes?.[source]?.trim();
  if (orgNote) return orgNote.slice(0, 500);
  const platformNote = platformNotes?.[source]?.trim();
  if (platformNote) return platformNote.slice(0, 500);
  return undefined;
}

export function normalizeTenantSeoBundleHistoryAuditLabelsBySource(
  value: unknown,
): TenantSeoBundleHistorySourceAuditLabels {
  const raw =
    value && typeof value === 'object'
      ? ((value as Record<string, unknown>).labels_by_source ??
        (value as Record<string, unknown>).labelsBySource ??
        value)
      : {};
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_BY_SOURCE };
  }
  const labels: TenantSeoBundleHistorySourceAuditLabels = {
    ...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_BY_SOURCE,
  };
  for (const source of TENANT_SEO_BUNDLE_HISTORY_SOURCES) {
    const label = String((raw as Record<string, unknown>)[source] ?? '').trim();
    if (label) labels[source] = label.slice(0, 200);
    else delete labels[source];
  }
  return labels;
}

export function resolveTenantSeoBundleHistoryAuditLabel(
  source: TenantSeoBundleHistoryEntry['source'],
  changeCount: number,
  platformLabels?: TenantSeoBundleHistorySourceAuditLabels,
  orgLabels?: TenantSeoBundleHistorySourceAuditLabels,
): string {
  const template = orgLabels?.[source]?.trim() || platformLabels?.[source]?.trim();
  if (template) {
    return template.replaceAll('{changes}', String(changeCount)).slice(0, 200);
  }
  return formatTenantSeoBundleHistoryAuditLabel(source, changeCount);
}

export function bulkApplyTenantSeoBundleHistoryAuditLabel(
  config: PublicMarketingConfig,
  entryIndices: readonly number[],
  auditLabel: string,
): PublicMarketingConfig {
  const label = auditLabel.trim();
  if (!label || entryIndices.length === 0) return config;
  const indexSet = new Set(entryIndices);
  return {
    ...config,
    seoBundleHistory: config.seoBundleHistory.map((entry, index) =>
      indexSet.has(index) ? { ...entry, auditLabel: label } : entry,
    ),
  };
}

export function bulkApplyTenantSeoBundleHistoryAuditNote(
  config: PublicMarketingConfig,
  entryIndices: readonly number[],
  auditNote: string,
): PublicMarketingConfig {
  const note = auditNote.trim().slice(0, 500);
  if (!note || entryIndices.length === 0) return config;
  const indexSet = new Set(entryIndices);
  return {
    ...config,
    seoBundleHistory: config.seoBundleHistory.map((entry, index) =>
      indexSet.has(index) ? { ...entry, auditNote: note } : entry,
    ),
  };
}

export function bulkApplyTenantSeoBundleHistoryAuditBySource(
  config: PublicMarketingConfig,
  entryIndices: readonly number[],
  platformNotes: TenantSeoBundleHistorySourceAuditNotes,
  platformLabels: TenantSeoBundleHistorySourceAuditLabels,
  options?: { applyNote?: boolean; applyLabel?: boolean },
): PublicMarketingConfig {
  if (entryIndices.length === 0) return config;
  const applyNote = options?.applyNote !== false;
  const applyLabel = options?.applyLabel === true;
  if (!applyNote && !applyLabel) return config;
  const indexSet = new Set(entryIndices);
  const orgNotes = config.seoBundleHistoryDefaultAuditNotes;
  const orgLabels = config.seoBundleHistoryDefaultAuditLabels;
  return {
    ...config,
    seoBundleHistory: config.seoBundleHistory.map((entry, index) => {
      if (!indexSet.has(index)) return entry;
      const next = { ...entry };
      if (applyNote) {
        const note = resolveTenantSeoBundleHistoryAuditNote(entry.source, platformNotes, orgNotes);
        if (note) next.auditNote = note;
      }
      if (applyLabel) {
        next.auditLabel = resolveTenantSeoBundleHistoryAuditLabel(
          entry.source,
          entry.changeCount,
          platformLabels,
          orgLabels,
        );
      }
      return next;
    }),
  };
}

export function isTenantSeoBundleHistoryEntryPinned(entry: TenantSeoBundleHistoryEntry): boolean {
  return entry.pinned === true;
}

export type TenantSeoBundleHistoryPinPolicy = {
  orgPinEnabled: boolean;
  platformPinEnabled: boolean;
  /** When true, pinning requires a non-empty pin note (platform super-admin config). */
  pinNoteRequired: boolean;
};

export const DEFAULT_TENANT_SEO_BUNDLE_HISTORY_PIN_POLICY: TenantSeoBundleHistoryPinPolicy = {
  orgPinEnabled: true,
  platformPinEnabled: true,
  pinNoteRequired: false,
};

export type TenantSeoBundleHistoryPinnedRetentionSummary = {
  pinnedCount: number;
  totalCount: number;
  maxEntries: number;
  unpinnedSlots: number;
  pinnedExceedsCap: boolean;
};

export function normalizeTenantSeoBundleHistoryPinPolicy(value: unknown): TenantSeoBundleHistoryPinPolicy {
  if (value && typeof value === 'object') {
    const raw = value as Record<string, unknown>;
    const orgPinEnabled = raw.org_pin_enabled ?? raw.orgPinEnabled ?? raw.org_enabled ?? raw.orgEnabled;
    const platformPinEnabled =
      raw.platform_pin_enabled ?? raw.platformPinEnabled ?? raw.platform_enabled ?? raw.platformEnabled;
    const pinNoteRequired = raw.pin_note_required ?? raw.pinNoteRequired;
    return {
      orgPinEnabled:
        orgPinEnabled === undefined
          ? DEFAULT_TENANT_SEO_BUNDLE_HISTORY_PIN_POLICY.orgPinEnabled
          : orgPinEnabled === true ||
            orgPinEnabled === 'true' ||
            orgPinEnabled === 1 ||
            orgPinEnabled === '1',
      platformPinEnabled:
        platformPinEnabled === undefined
          ? DEFAULT_TENANT_SEO_BUNDLE_HISTORY_PIN_POLICY.platformPinEnabled
          : platformPinEnabled === true ||
            platformPinEnabled === 'true' ||
            platformPinEnabled === 1 ||
            platformPinEnabled === '1',
      pinNoteRequired:
        pinNoteRequired === undefined
          ? DEFAULT_TENANT_SEO_BUNDLE_HISTORY_PIN_POLICY.pinNoteRequired
          : pinNoteRequired === true ||
            pinNoteRequired === 'true' ||
            pinNoteRequired === 1 ||
            pinNoteRequired === '1',
    };
  }
  if (value === true || value === 'true') {
    return {
      orgPinEnabled: true,
      platformPinEnabled: true,
      pinNoteRequired: DEFAULT_TENANT_SEO_BUNDLE_HISTORY_PIN_POLICY.pinNoteRequired,
    };
  }
  if (value === false || value === 'false') {
    return {
      orgPinEnabled: false,
      platformPinEnabled: false,
      pinNoteRequired: DEFAULT_TENANT_SEO_BUNDLE_HISTORY_PIN_POLICY.pinNoteRequired,
    };
  }
  return { ...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_PIN_POLICY };
}

export function summarizeTenantSeoBundleHistoryPinnedRetention(
  history: readonly TenantSeoBundleHistoryEntry[],
  maxEntries: number,
): TenantSeoBundleHistoryPinnedRetentionSummary {
  const limit = normalizeTenantSeoBundleHistoryMaxEntries(maxEntries);
  const pinnedCount = history.filter(isTenantSeoBundleHistoryEntryPinned).length;
  const unpinnedSlots = Math.max(limit - pinnedCount, 0);
  return {
    pinnedCount,
    totalCount: history.length,
    maxEntries: limit,
    unpinnedSlots,
    pinnedExceedsCap: pinnedCount >= limit && pinnedCount > 0,
  };
}

export function resolveTenantSeoBundleHistoryOrgPinEnabled(
  platformPolicy: TenantSeoBundleHistoryPinPolicy,
  orgPinOverride?: boolean,
): boolean {
  if (orgPinOverride === true || orgPinOverride === false) return orgPinOverride;
  return platformPolicy.orgPinEnabled;
}

export function resolveTenantSeoBundleHistoryPinNoteRequired(
  platformPolicy: TenantSeoBundleHistoryPinPolicy,
  orgPinNoteRequiredOverride?: boolean,
): boolean {
  if (orgPinNoteRequiredOverride === true || orgPinNoteRequiredOverride === false) {
    return orgPinNoteRequiredOverride;
  }
  return platformPolicy.pinNoteRequired;
}

export function normalizeTenantSeoBundleHistoryPinNotesBySource(
  value: unknown,
): TenantSeoBundleHistorySourcePinNotes {
  const raw =
    value && typeof value === 'object'
      ? ((value as Record<string, unknown>).pin_notes_by_source ??
        (value as Record<string, unknown>).pinNotesBySource ??
        value)
      : {};
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_PIN_NOTE_BY_SOURCE };
  }
  const notes: TenantSeoBundleHistorySourcePinNotes = {
    ...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_PIN_NOTE_BY_SOURCE,
  };
  for (const source of TENANT_SEO_BUNDLE_HISTORY_SOURCES) {
    const note = String((raw as Record<string, unknown>)[source] ?? '').trim();
    if (note) notes[source] = note.slice(0, 500);
    else delete notes[source];
  }
  return notes;
}

export function resolveTenantSeoBundleHistoryPinNote(
  source: TenantSeoBundleHistoryEntry['source'],
  platformPinNotes?: TenantSeoBundleHistorySourcePinNotes,
  orgPinNotes?: TenantSeoBundleHistorySourcePinNotes,
  defaultPinNote?: string,
  changeCount = 0,
): string | undefined {
  const orgTemplate = orgPinNotes?.[source]?.trim();
  const platformTemplate = platformPinNotes?.[source]?.trim();
  const fallback = normalizeTenantSeoBundleHistoryPinNote(defaultPinNote ?? '');
  const template = orgTemplate || platformTemplate || fallback;
  if (!template) return undefined;
  return formatTenantSeoBundleHistoryPinNoteTemplate(template, source, changeCount);
}

export function formatTenantSeoBundleHistoryPinNoteTemplate(
  template: string,
  source: TenantSeoBundleHistoryEntry['source'],
  changeCount: number,
): string {
  return template
    .replaceAll('{source}', source.replaceAll('_', ' '))
    .replaceAll('{changes}', String(changeCount))
    .replaceAll('{change_count}', String(changeCount))
    .trim()
    .slice(0, 500);
}

export function tenantSeoBundleHistoryPinNoteTemplateHasPlaceholders(template: string): boolean {
  return /\{source\}|\{changes\}|\{change_count\}/.test(template);
}

export function getTenantSeoBundleHistoryLatestPinNoteAudit(
  entry: TenantSeoBundleHistoryEntry,
): TenantSeoBundleHistoryPinNoteAuditEntry | undefined {
  const audit = entry.pinNoteAudit;
  if (!audit || audit.length === 0) return undefined;
  return audit[audit.length - 1];
}

export function previewTenantSeoBundleHistoryPinNoteTemplatesBySource(
  templates: TenantSeoBundleHistorySourcePinNotes,
  sampleChangeCount = 3,
): Partial<Record<TenantSeoBundleHistoryEntry['source'], string>> {
  const previews: Partial<Record<TenantSeoBundleHistoryEntry['source'], string>> = {};
  for (const source of TENANT_SEO_BUNDLE_HISTORY_SOURCES) {
    const template = templates[source]?.trim();
    if (!template) continue;
    previews[source] = formatTenantSeoBundleHistoryPinNoteTemplate(template, source, sampleChangeCount);
  }
  return previews;
}

export function appendTenantSeoBundleHistoryPinNoteAudit(
  entry: TenantSeoBundleHistoryEntry,
  pinNote: string,
  action: TenantSeoBundleHistoryPinNoteAuditAction,
  updatedAt = new Date().toISOString(),
): TenantSeoBundleHistoryEntry {
  const note = normalizeTenantSeoBundleHistoryPinNote(pinNote);
  if (!note) {
    const { pinNote: _removed, pinNoteAudit: _audit, ...rest } = entry;
    return rest;
  }
  const prior = entry.pinNoteAudit ?? [];
  const pinNoteAudit = [...prior, { updatedAt, pinNote: note, action }].slice(
    -TENANT_SEO_BUNDLE_HISTORY_PIN_NOTE_AUDIT_LIMIT,
  );
  return { ...entry, pinNote: note, pinNoteAudit };
}

export function bulkUnpinTenantSeoBundleHistoryEntries(
  config: PublicMarketingConfig,
  entryIndices: readonly number[],
): PublicMarketingConfig {
  if (entryIndices.length === 0) return config;
  const indexSet = new Set(entryIndices);
  return {
    ...config,
    seoBundleHistory: config.seoBundleHistory.map((entry, index) => {
      if (!indexSet.has(index) || !entry.pinned) return entry;
      const { pinned: _pinned, pinNote: _pinNote, pinNoteAudit: _audit, ...rest } = entry;
      return rest;
    }),
  };
}

export function normalizeTenantSeoBundleHistoryPinNote(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim().slice(0, 500);
}

export const TENANT_SEO_BUNDLE_HISTORY_PIN_NOTE_REQUIRED_MESSAGE =
  'A pin note is required when pinning history entries (configure default bulk pin note or enter a note).';

export function validateTenantSeoBundleHistoryPinNote(
  pinNote: string | undefined,
  pinNoteRequired: boolean,
): string | null {
  if (!pinNoteRequired) return null;
  if (normalizeTenantSeoBundleHistoryPinNote(pinNote ?? '')) return null;
  return TENANT_SEO_BUNDLE_HISTORY_PIN_NOTE_REQUIRED_MESSAGE;
}

export function resolveTenantSeoBundleHistoryPinNoteForPin(
  explicitPinNote?: string,
  defaultPinNote?: string,
  context?: {
    source?: TenantSeoBundleHistoryEntry['source'];
    changeCount?: number;
    platformPinNotes?: TenantSeoBundleHistorySourcePinNotes;
    orgPinNotes?: TenantSeoBundleHistorySourcePinNotes;
  },
): string {
  const explicit = normalizeTenantSeoBundleHistoryPinNote(explicitPinNote ?? '');
  if (explicit) return explicit;
  if (context?.source) {
    const fromSource = resolveTenantSeoBundleHistoryPinNote(
      context.source,
      context.platformPinNotes,
      context.orgPinNotes,
      defaultPinNote,
      context.changeCount ?? 0,
    );
    if (fromSource) return fromSource;
  }
  return normalizeTenantSeoBundleHistoryPinNote(defaultPinNote ?? '');
}

export function splitTenantSeoBundleHistorySearchHighlight(
  text: string,
  query: string,
): Array<{ text: string; match: boolean }> {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return [{ text, match: false }];
  const lowerText = text.toLowerCase();
  const lowerQuery = trimmedQuery.toLowerCase();
  const segments: Array<{ text: string; match: boolean }> = [];
  let cursor = 0;
  while (cursor < text.length) {
    const matchIndex = lowerText.indexOf(lowerQuery, cursor);
    if (matchIndex < 0) {
      segments.push({ text: text.slice(cursor), match: false });
      break;
    }
    if (matchIndex > cursor) {
      segments.push({ text: text.slice(cursor, matchIndex), match: false });
    }
    segments.push({
      text: text.slice(matchIndex, matchIndex + trimmedQuery.length),
      match: true,
    });
    cursor = matchIndex + trimmedQuery.length;
  }
  return segments.length > 0 ? segments : [{ text, match: false }];
}

export function tenantSeoBundleHistoryPinNoteMatchesQuery(
  entry: TenantSeoBundleHistoryEntry,
  query: string,
): boolean {
  const trimmedQuery = query.trim().toLowerCase();
  if (!trimmedQuery) return false;
  return (entry.pinNote ?? '').toLowerCase().includes(trimmedQuery);
}

export function tenantSeoBundleHistoryPinNoteAuditMatchesQuery(
  entry: TenantSeoBundleHistoryEntry,
  query: string,
): boolean {
  const trimmedQuery = query.trim().toLowerCase();
  if (!trimmedQuery) return false;
  return (entry.pinNoteAudit ?? []).some(
    (row) =>
      row.pinNote.toLowerCase().includes(trimmedQuery) ||
      row.action.toLowerCase().includes(trimmedQuery) ||
      row.updatedAt.toLowerCase().includes(trimmedQuery),
  );
}

export function bulkPinTenantSeoBundleHistoryEntries(
  config: PublicMarketingConfig,
  entryIndices: readonly number[],
  pinNote?: string,
  context?: {
    defaultPinNote?: string;
    platformPinNotes?: TenantSeoBundleHistorySourcePinNotes;
    orgPinNotes?: TenantSeoBundleHistorySourcePinNotes;
    auditAction?: TenantSeoBundleHistoryPinNoteAuditAction;
  },
): PublicMarketingConfig {
  if (entryIndices.length === 0) return config;
  const indexSet = new Set(entryIndices);
  const sharedNote = normalizeTenantSeoBundleHistoryPinNote(pinNote ?? '');
  const auditAction = context?.auditAction ?? 'bulk_pin';
  return {
    ...config,
    seoBundleHistory: config.seoBundleHistory.map((entry, index) => {
      if (!indexSet.has(index) || entry.pinned) return entry;
      const note = sharedNote
        ? sharedNote
        : resolveTenantSeoBundleHistoryPinNoteForPin(undefined, context?.defaultPinNote, {
            source: entry.source,
            changeCount: entry.changeCount,
            platformPinNotes: context?.platformPinNotes,
            orgPinNotes: context?.orgPinNotes ?? config.seoBundleHistoryDefaultPinNotesBySource,
          });
      const pinnedEntry = {
        ...entry,
        pinned: true as const,
        ...(note ? { pinNote: note } : {}),
      };
      return note ? appendTenantSeoBundleHistoryPinNoteAudit(pinnedEntry, note, auditAction) : pinnedEntry;
    }),
  };
}

export function bulkPinTenantSeoBundleHistoryEntriesBySource(
  config: PublicMarketingConfig,
  entryIndices: readonly number[],
  platformPinNotes: TenantSeoBundleHistorySourcePinNotes,
  defaultPinNote?: string,
): PublicMarketingConfig {
  return bulkPinTenantSeoBundleHistoryEntries(config, entryIndices, undefined, {
    defaultPinNote,
    platformPinNotes,
    orgPinNotes: config.seoBundleHistoryDefaultPinNotesBySource,
    auditAction: 'bulk_pin_by_source',
  });
}

export function updateTenantSeoBundleHistoryPinNote(
  config: PublicMarketingConfig,
  entryIndex: number,
  pinNote: string,
): PublicMarketingConfig {
  const note = normalizeTenantSeoBundleHistoryPinNote(pinNote);
  return {
    ...config,
    seoBundleHistory: config.seoBundleHistory.map((entry, index) => {
      if (index !== entryIndex || !entry.pinned) return entry;
      if (!note) {
        const { pinNote: _removed, pinNoteAudit: _audit, ...rest } = entry;
        return rest;
      }
      return appendTenantSeoBundleHistoryPinNoteAudit(entry, note, 'manual');
    }),
  };
}

export function toggleTenantSeoBundleHistoryPin(
  config: PublicMarketingConfig,
  entryIndex: number,
  pinNote?: string,
  context?: {
    defaultPinNote?: string;
    platformPinNotes?: TenantSeoBundleHistorySourcePinNotes;
    orgPinNotes?: TenantSeoBundleHistorySourcePinNotes;
  },
): PublicMarketingConfig {
  const entry = config.seoBundleHistory[entryIndex];
  const note = entry
    ? resolveTenantSeoBundleHistoryPinNoteForPin(pinNote, context?.defaultPinNote, {
        source: entry.source,
        changeCount: entry.changeCount,
        platformPinNotes: context?.platformPinNotes,
        orgPinNotes: context?.orgPinNotes ?? config.seoBundleHistoryDefaultPinNotesBySource,
      })
    : normalizeTenantSeoBundleHistoryPinNote(pinNote ?? '');
  const history = config.seoBundleHistory.map((item, index) => {
    if (index !== entryIndex) return item;
    if (item.pinned) {
      const { pinned: _pinned, pinNote: _pinNote, pinNoteAudit: _audit, ...rest } = item;
      return rest;
    }
    const pinnedEntry = {
      ...item,
      pinned: true as const,
      ...(note ? { pinNote: note } : {}),
    };
    return note ? appendTenantSeoBundleHistoryPinNoteAudit(pinnedEntry, note, 'manual') : pinnedEntry;
  });
  return { ...config, seoBundleHistory: history };
}

export function bulkUpdateTenantSeoBundleHistoryPinNotes(
  config: PublicMarketingConfig,
  entryIndices: readonly number[],
  pinNote: string,
): PublicMarketingConfig {
  const note = normalizeTenantSeoBundleHistoryPinNote(pinNote);
  if (!note || entryIndices.length === 0) return config;
  const indexSet = new Set(entryIndices);
  return {
    ...config,
    seoBundleHistory: config.seoBundleHistory.map((entry, index) => {
      if (!indexSet.has(index) || !entry.pinned) return entry;
      return appendTenantSeoBundleHistoryPinNoteAudit(entry, note, 'bulk_update');
    }),
  };
}

export function bulkApplyTenantSeoBundleHistoryPinNoteBySource(
  config: PublicMarketingConfig,
  entryIndices: readonly number[],
  platformPinNotes: TenantSeoBundleHistorySourcePinNotes,
  defaultPinNote?: string,
): PublicMarketingConfig {
  if (entryIndices.length === 0) return config;
  const indexSet = new Set(entryIndices);
  return {
    ...config,
    seoBundleHistory: config.seoBundleHistory.map((entry, index) => {
      if (!indexSet.has(index) || !entry.pinned) return entry;
      const note = resolveTenantSeoBundleHistoryPinNote(
        entry.source,
        platformPinNotes,
        config.seoBundleHistoryDefaultPinNotesBySource,
        defaultPinNote,
        entry.changeCount,
      );
      if (!note) return entry;
      return appendTenantSeoBundleHistoryPinNoteAudit(entry, note, 'bulk_by_source');
    }),
  };
}

export function trimTenantSeoBundleHistoryEntries(
  history: readonly TenantSeoBundleHistoryEntry[],
  maxEntries: number,
): TenantSeoBundleHistoryEntry[] {
  const limit = normalizeTenantSeoBundleHistoryMaxEntries(maxEntries);
  if (history.length <= limit) return [...history];
  const kept = [...history];
  while (kept.length > limit) {
    let dropIndex = -1;
    for (let i = kept.length - 1; i >= 0; i--) {
      if (!isTenantSeoBundleHistoryEntryPinned(kept[i])) {
        dropIndex = i;
        break;
      }
    }
    if (dropIndex < 0) break;
    kept.splice(dropIndex, 1);
  }
  return kept;
}

export type TenantSeoBundleHistoryPreviewFileDiffRow = {
  filename: string;
  changed: boolean;
  beforeLines: number;
  afterLines: number;
  diffPreview?: string;
};

export type TenantSeoBundleHistoryPreviewExport = {
  entryIndex: number;
  previewType: 'rollback' | 'reapply';
  appliedAt: string;
  source: TenantSeoBundleHistoryEntry['source'];
  configDiff: Array<{ field: string; before: string; after: string }>;
  changedFiles: string[];
  fileDiff: TenantSeoBundleHistoryPreviewFileDiffRow[];
};

export function serializeTenantSeoBundleHistoryPreviewCsv(
  preview: TenantSeoBundleHistoryPreviewExport,
): string {
  const header = [
    'entry_index',
    'preview_type',
    'applied_at',
    'source',
    'field',
    'before',
    'after',
    'filename',
    'file_changed',
    'file_before_lines',
    'file_after_lines',
  ];
  const configLines =
    preview.configDiff.length > 0
      ? preview.configDiff.map((row) =>
          [
            String(preview.entryIndex),
            preview.previewType,
            preview.appliedAt,
            preview.source,
            row.field,
            row.before,
            row.after,
            '',
            '',
            '',
            '',
          ]
            .map((cell) => escapeCsvCell(String(cell)))
            .join(','),
        )
      : [
          [
            String(preview.entryIndex),
            preview.previewType,
            preview.appliedAt,
            preview.source,
            '',
            '',
            '',
            '',
            '',
            '',
            '',
          ]
            .map((cell) => escapeCsvCell(String(cell)))
            .join(','),
        ];
  const fileLines = preview.fileDiff.map((row) =>
    [
      String(preview.entryIndex),
      preview.previewType,
      preview.appliedAt,
      preview.source,
      '',
      '',
      '',
      row.filename,
      row.changed ? 'yes' : 'no',
      String(row.beforeLines),
      String(row.afterLines),
    ]
      .map((cell) => escapeCsvCell(String(cell)))
      .join(','),
  );
  return [header.join(','), ...configLines, ...fileLines].join('\n');
}

export function serializeTenantSeoBundleHistoryPreviewJson(
  preview: TenantSeoBundleHistoryPreviewExport,
): string {
  return JSON.stringify(
    {
      entry_index: preview.entryIndex,
      preview_type: preview.previewType,
      applied_at: preview.appliedAt,
      source: preview.source,
      changed_files: preview.changedFiles,
      config_diff: preview.configDiff.map((row) => ({
        field: row.field,
        before: row.before,
        after: row.after,
      })),
      file_diff: preview.fileDiff.map((row) => ({
        filename: row.filename,
        changed: row.changed,
        before_lines: row.beforeLines,
        after_lines: row.afterLines,
        ...(row.diffPreview ? { diff_preview: row.diffPreview } : {}),
      })),
    },
    null,
    2,
  );
}

export type PlatformTenantSeoBundleHistoryPreviewExport = {
  organizationId: string;
  organizationName: string;
  entryIndex: number;
  previewType: 'snapshot' | 'rollback' | 'reapply';
  appliedAt: string;
  source: TenantSeoBundleHistoryEntry['source'];
  configDiff: Array<{ field: string; before: string; after: string }>;
  changedFiles: string[];
};

export function serializePlatformTenantSeoBundleHistoryPreviewCsv(
  preview: PlatformTenantSeoBundleHistoryPreviewExport,
): string {
  const header = [
    'organization_id',
    'organization_name',
    'entry_index',
    'preview_type',
    'applied_at',
    'source',
    'field',
    'before',
    'after',
    'changed_files',
  ];
  const changedFiles = preview.changedFiles.join('; ');
  const lines =
    preview.configDiff.length > 0
      ? preview.configDiff.map((row) =>
          [
            preview.organizationId,
            preview.organizationName,
            String(preview.entryIndex),
            preview.previewType,
            preview.appliedAt,
            preview.source,
            row.field,
            row.before,
            row.after,
            changedFiles,
          ]
            .map((cell) => escapeCsvCell(String(cell)))
            .join(','),
        )
      : [
          [
            preview.organizationId,
            preview.organizationName,
            String(preview.entryIndex),
            preview.previewType,
            preview.appliedAt,
            preview.source,
            '',
            '',
            '',
            changedFiles,
          ]
            .map((cell) => escapeCsvCell(String(cell)))
            .join(','),
        ];
  return [header.join(','), ...lines].join('\n');
}

export function serializePlatformTenantSeoBundleHistoryPreviewJson(
  preview: PlatformTenantSeoBundleHistoryPreviewExport,
): string {
  return JSON.stringify(
    {
      organization_id: preview.organizationId,
      organization_name: preview.organizationName,
      entry_index: preview.entryIndex,
      preview_type: preview.previewType,
      applied_at: preview.appliedAt,
      source: preview.source,
      changed_files: preview.changedFiles,
      config_diff: preview.configDiff.map((row) => ({
        field: row.field,
        before: row.before,
        after: row.after,
      })),
    },
    null,
    2,
  );
}

export function filterTenantSeoBundleHistoryEntries(
  history: readonly TenantSeoBundleHistoryEntry[],
  filter?: TenantSeoBundleHistoryFilter,
): TenantSeoBundleHistoryEntry[] {
  if (!filter) return [...history];
  return history.filter((entry) => matchesTenantSeoBundleHistoryFilter(entry, filter));
}

function matchesTenantSeoBundleHistoryFilter(
  entry: TenantSeoBundleHistoryEntry,
  filter: TenantSeoBundleHistoryFilter,
): boolean {
  if (filter.source && filter.source !== 'all' && entry.source !== filter.source) return false;
  if (filter.bundle === 'with_bundle' && !entry.appliedBundle) return false;
  if (filter.bundle === 'orphan' && entry.appliedBundle) return false;
  if (filter.snapshot === 'with_snapshot' && !entry.rollbackSnapshot) return false;
  if (filter.snapshot === 'without_snapshot' && entry.rollbackSnapshot) return false;
  if (filter.pinned === 'pinned' && !isTenantSeoBundleHistoryEntryPinned(entry)) return false;
  if (filter.pinned === 'unpinned' && isTenantSeoBundleHistoryEntryPinned(entry)) return false;
  if (filter.pinNoteAuditAction && filter.pinNoteAuditAction !== 'all') {
    const latest = getTenantSeoBundleHistoryLatestPinNoteAudit(entry);
    if (!latest || latest.action !== filter.pinNoteAuditAction) return false;
  }
  const query = filter.query?.trim().toLowerCase();
  if (query) {
    if (filter.queryScope === 'pin_note') {
      if (!tenantSeoBundleHistoryPinNoteMatchesQuery(entry, query)) return false;
    } else if (filter.queryScope === 'pin_note_audit') {
      if (!tenantSeoBundleHistoryPinNoteAuditMatchesQuery(entry, query)) return false;
    } else {
      const label =
        entry.auditLabel ?? formatTenantSeoBundleHistoryAuditLabel(entry.source, entry.changeCount);
      const latestPinNoteAudit = getTenantSeoBundleHistoryLatestPinNoteAudit(entry);
      const haystack = [
        label,
        entry.auditNote ?? '',
        entry.pinNote ?? '',
        latestPinNoteAudit?.action ?? '',
        latestPinNoteAudit?.pinNote ?? '',
        entry.source,
        entry.brandingCompanyName ?? '',
        entry.appliedAt,
        entry.exportedAt,
        String(entry.changeCount),
      ]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(query)) return false;
    }
  }
  return true;
}

export function normalizeTenantSeoBundleHistoryAuditLabelPresets(value: unknown): string[] {
  const rawPresets = Array.isArray(value)
    ? value
    : value && typeof value === 'object'
      ? (value as Record<string, unknown>).presets
      : [];
  if (!Array.isArray(rawPresets)) return [...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_PRESETS];
  const presets = rawPresets
    .map((item) => String(item ?? '').trim())
    .filter(Boolean)
    .slice(0, TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_PRESET_LIMIT);
  return presets.length > 0 ? presets : [...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_PRESETS];
}

export function normalizeTenantSeoBundleHistoryAuditNotePlaceholder(value: unknown): string {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_PLACEHOLDER;
  }
  if (value && typeof value === 'object') {
    const placeholder = String((value as Record<string, unknown>).placeholder ?? '').trim();
    return placeholder || DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_PLACEHOLDER;
  }
  return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_PLACEHOLDER;
}

export function listTenantSeoBundleHistoryEntriesWithIndex(
  history: readonly TenantSeoBundleHistoryEntry[],
  filter?: TenantSeoBundleHistoryFilter,
): Array<{ entry: TenantSeoBundleHistoryEntry; index: number }> {
  return history
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => !filter || matchesTenantSeoBundleHistoryFilter(entry, filter));
}

export function filterPlatformTenantSeoBundleHistoryAuditRows(
  rows: readonly PlatformTenantSeoBundleHistoryAuditRow[],
  filter?: TenantSeoBundleHistoryFilter,
): PlatformTenantSeoBundleHistoryAuditRow[] {
  if (!filter) return [...rows];
  return rows
    .map((row) => ({
      ...row,
      config: {
        ...row.config,
        seoBundleHistory: filterTenantSeoBundleHistoryEntries(row.config.seoBundleHistory, filter),
      },
    }))
    .filter((row) => row.config.seoBundleHistory.length > 0);
}

export type TenantSeoBundleHistoryExportPreset = {
  label: string;
  pinned?: 'all' | 'pinned' | 'unpinned';
  source?: TenantSeoBundleHistorySourceFilter;
  bundle?: TenantSeoBundleHistoryBundleFilter;
  snapshot?: TenantSeoBundleHistorySnapshotFilter;
  pinNoteAuditAction?: TenantSeoBundleHistoryPinNoteAuditActionFilter;
};

/** Org admin export preset draft textarea parse cap (platform DB presets use platform limit). */
export const TENANT_SEO_BUNDLE_HISTORY_EXPORT_ORG_DRAFT_PRESET_LIMIT = 12;

/** @deprecated Use TENANT_SEO_BUNDLE_HISTORY_EXPORT_ORG_DRAFT_PRESET_LIMIT for org drafts. */
export const TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_LIMIT =
  TENANT_SEO_BUNDLE_HISTORY_EXPORT_ORG_DRAFT_PRESET_LIMIT;

export const DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PLATFORM_PRESET_LIMIT = 650;
export const MIN_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PLATFORM_PRESET_LIMIT = 12;
export const MAX_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PLATFORM_PRESET_LIMIT = 1000;

export const DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_LABEL_SEARCH_MIN = 50;
export const MIN_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_LABEL_SEARCH_MIN = 1;
export const MAX_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_LABEL_SEARCH_MIN = 1000;

export const DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_EXPAND_LIMIT = 24;
export const MIN_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_EXPAND_LIMIT = 8;
export const MAX_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_EXPAND_LIMIT = 200;

export const DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_SIZE = 48;
export const MIN_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_SIZE = 24;
export const MAX_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_SIZE = 200;

export const DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_PERSIST_ENABLED = true;

export const DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_JUMP_AMBIGUOUS_LIMIT = 24;
export const MIN_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_JUMP_AMBIGUOUS_LIMIT = 8;
export const MAX_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_JUMP_AMBIGUOUS_LIMIT = 200;

export const DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESETS: TenantSeoBundleHistoryExportPreset[] = [
  { label: 'All pinned', pinned: 'pinned', source: 'all', bundle: 'all', snapshot: 'all' },
  { label: 'Pinned imports', pinned: 'pinned', source: 'import', bundle: 'all', snapshot: 'all' },
  {
    label: 'Pinned imports with stored bundle',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'all',
  },
  {
    label: 'Pinned imports with rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'with_snapshot',
  },
  {
    label: 'Pinned imports without rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'without_snapshot',
  },
  {
    label: 'Pinned imports with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
  },
  {
    label: 'Pinned imports with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
  },
  {
    label: 'Pinned imports manual pin notes',
    pinned: 'pinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned imports bulk pin holds',
    pinned: 'pinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned imports bulk pin by source',
    pinned: 'pinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned imports bulk by source notes',
    pinned: 'pinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned imports bulk update notes',
    pinned: 'pinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned imports manual pin notes with rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned imports manual pin notes without rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned imports bulk pin holds with rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned imports bulk pin holds without rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned imports bulk pin by source with rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned imports bulk pin by source without rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned imports bulk by source notes with rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned imports bulk by source notes without rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned imports bulk update notes with rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned imports bulk update notes without rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned imports manual pin notes with stored bundle',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned imports bulk pin holds with stored bundle',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned imports bulk pin by source with stored bundle',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned imports bulk by source notes with stored bundle',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned imports bulk update notes with stored bundle',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned imports manual pin notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned imports manual pin notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned imports bulk pin holds with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned imports bulk pin holds with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned imports bulk pin by source with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned imports bulk pin by source with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned imports bulk by source notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned imports bulk by source notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned imports bulk update notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned imports bulk update notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned imports manual pin notes orphans',
    pinned: 'pinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned imports bulk pin holds orphans',
    pinned: 'pinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned imports bulk pin by source orphans',
    pinned: 'pinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned imports bulk by source notes orphans',
    pinned: 'pinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned imports bulk update notes orphans',
    pinned: 'pinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned imports manual pin notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned imports manual pin notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned imports bulk pin holds orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned imports bulk pin holds orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned imports bulk pin by source orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned imports bulk pin by source orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned imports bulk by source notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned imports bulk by source notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned imports bulk update notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned imports bulk update notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned platform templates',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'all',
  },
  {
    label: 'Pinned platform templates with stored bundle',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'all',
  },
  {
    label: 'Pinned platform templates with rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'with_snapshot',
  },
  {
    label: 'Pinned platform templates without rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'without_snapshot',
  },
  {
    label: 'Pinned platform templates with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
  },
  {
    label: 'Pinned platform templates with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
  },
  {
    label: 'Pinned platform templates manual pin notes',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned platform templates bulk pin holds',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned platform templates bulk pin by source',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned platform templates bulk by source notes',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned platform templates bulk update notes',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned platform templates manual pin notes with rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned platform templates manual pin notes without rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned platform templates bulk pin holds with rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned platform templates bulk pin holds without rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned platform templates bulk pin by source with rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned platform templates bulk pin by source without rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned platform templates bulk by source notes with rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned platform templates bulk by source notes without rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned platform templates bulk update notes with rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned platform templates bulk update notes without rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned platform templates manual pin notes with stored bundle',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned platform templates bulk pin holds with stored bundle',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned platform templates bulk pin by source with stored bundle',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned platform templates bulk by source notes with stored bundle',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned platform templates bulk update notes with stored bundle',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned platform templates manual pin notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned platform templates manual pin notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned platform templates bulk pin holds with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned platform templates bulk pin holds with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned platform templates bulk pin by source with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned platform templates bulk pin by source with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned platform templates bulk by source notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned platform templates bulk by source notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned platform templates bulk update notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned platform templates bulk update notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned platform templates manual pin notes orphans',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned platform templates bulk pin holds orphans',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned platform templates bulk pin by source orphans',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned platform templates bulk by source notes orphans',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned platform templates bulk update notes orphans',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned platform templates manual pin notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned platform templates manual pin notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned platform templates bulk pin holds orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned platform templates bulk pin holds orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned platform templates bulk pin by source orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned platform templates bulk pin by source orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned platform templates bulk by source notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned platform templates bulk by source notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned platform templates bulk update notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned platform templates bulk update notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  { label: 'Pinned rollbacks', pinned: 'pinned', source: 'rollback', bundle: 'all', snapshot: 'all' },
  {
    label: 'Pinned rollbacks with stored bundle',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'all',
  },
  {
    label: 'Pinned rollbacks with rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'with_snapshot',
  },
  {
    label: 'Pinned rollbacks without rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'without_snapshot',
  },
  {
    label: 'Pinned rollbacks with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
  },
  {
    label: 'Pinned rollbacks with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
  },
  {
    label: 'Pinned rollbacks manual pin notes',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned rollbacks bulk pin holds',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned rollbacks bulk pin by source',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned rollbacks bulk by source notes',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned rollbacks bulk update notes',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned rollbacks manual pin notes with rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned rollbacks manual pin notes without rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned rollbacks bulk pin holds with rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned rollbacks bulk pin holds without rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned rollbacks bulk pin by source with rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned rollbacks bulk pin by source without rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned rollbacks bulk by source notes with rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned rollbacks bulk by source notes without rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned rollbacks bulk update notes with rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned rollbacks bulk update notes without rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned rollbacks manual pin notes with stored bundle',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned rollbacks bulk pin holds with stored bundle',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned rollbacks bulk pin by source with stored bundle',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned rollbacks bulk by source notes with stored bundle',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned rollbacks bulk update notes with stored bundle',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned rollbacks manual pin notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned rollbacks manual pin notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned rollbacks bulk pin holds with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned rollbacks bulk pin holds with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned rollbacks bulk pin by source with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned rollbacks bulk pin by source with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned rollbacks bulk by source notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned rollbacks bulk by source notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned rollbacks bulk update notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned rollbacks bulk update notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned rollbacks manual pin notes orphans',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned rollbacks bulk pin holds orphans',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned rollbacks bulk pin by source orphans',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned rollbacks bulk by source notes orphans',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned rollbacks bulk update notes orphans',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned rollbacks manual pin notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned rollbacks manual pin notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned rollbacks bulk pin holds orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned rollbacks bulk pin holds orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned rollbacks bulk pin by source orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned rollbacks bulk pin by source orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned rollbacks bulk by source notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned rollbacks bulk by source notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned rollbacks bulk update notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned rollbacks bulk update notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  { label: 'Pinned re-applies', pinned: 'pinned', source: 'reapply', bundle: 'all', snapshot: 'all' },
  {
    label: 'Pinned re-applies with stored bundle',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'all',
  },
  {
    label: 'Pinned re-applies with rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'with_snapshot',
  },
  {
    label: 'Pinned re-applies without rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'without_snapshot',
  },
  {
    label: 'Pinned re-applies with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
  },
  {
    label: 'Pinned re-applies with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
  },
  {
    label: 'Pinned re-applies manual pin notes',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned re-applies bulk pin holds',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned re-applies bulk pin by source',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned re-applies bulk by source notes',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned re-applies bulk update notes',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned re-applies manual pin notes with rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned re-applies manual pin notes without rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned re-applies bulk pin holds with rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned re-applies bulk pin holds without rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned re-applies bulk pin by source with rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned re-applies bulk pin by source without rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned re-applies bulk by source notes with rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned re-applies bulk by source notes without rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned re-applies bulk update notes with rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned re-applies bulk update notes without rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned re-applies manual pin notes with stored bundle',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned re-applies bulk pin holds with stored bundle',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned re-applies bulk pin by source with stored bundle',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned re-applies bulk by source notes with stored bundle',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned re-applies bulk update notes with stored bundle',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned re-applies manual pin notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned re-applies manual pin notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned re-applies bulk pin holds with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned re-applies bulk pin holds with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned re-applies bulk pin by source with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned re-applies bulk pin by source with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned re-applies bulk by source notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned re-applies bulk by source notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned re-applies bulk update notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned re-applies bulk update notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned re-applies manual pin notes orphans',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned re-applies bulk pin holds orphans',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned re-applies bulk pin by source orphans',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned re-applies bulk by source notes orphans',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned re-applies bulk update notes orphans',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned re-applies manual pin notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned re-applies manual pin notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned re-applies bulk pin holds orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned re-applies bulk pin holds orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned re-applies bulk pin by source orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned re-applies bulk pin by source orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned re-applies bulk by source notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned re-applies bulk by source notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned re-applies bulk update notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned re-applies bulk update notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned org signup',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'all',
  },
  {
    label: 'Pinned org signup with stored bundle',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'all',
  },
  {
    label: 'Pinned org signup with rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'with_snapshot',
  },
  {
    label: 'Pinned org signup without rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'without_snapshot',
  },
  {
    label: 'Pinned org signup with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
  },
  {
    label: 'Pinned org signup with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
  },
  {
    label: 'Pinned org signup manual pin notes',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned org signup bulk pin holds',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned org signup bulk pin by source',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned org signup bulk by source notes',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned org signup bulk update notes',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned org signup manual pin notes with rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned org signup manual pin notes without rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned org signup bulk pin holds with rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned org signup bulk pin holds without rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned org signup bulk pin by source with rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned org signup bulk pin by source without rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned org signup bulk by source notes with rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned org signup bulk by source notes without rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned org signup bulk update notes with rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned org signup bulk update notes without rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned org signup manual pin notes with stored bundle',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned org signup bulk pin holds with stored bundle',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned org signup bulk pin by source with stored bundle',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned org signup bulk by source notes with stored bundle',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned org signup bulk update notes with stored bundle',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned org signup manual pin notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned org signup manual pin notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned org signup bulk pin holds with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned org signup bulk pin holds with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned org signup bulk pin by source with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned org signup bulk pin by source with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned org signup bulk by source notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned org signup bulk by source notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned org signup bulk update notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned org signup bulk update notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned org signup manual pin notes orphans',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned org signup bulk pin holds orphans',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned org signup bulk pin by source orphans',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned org signup bulk by source notes orphans',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned org signup bulk update notes orphans',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned org signup manual pin notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned org signup manual pin notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned org signup bulk pin holds orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned org signup bulk pin holds orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned org signup bulk pin by source orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned org signup bulk pin by source orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned org signup bulk by source notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned org signup bulk by source notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned org signup bulk update notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned org signup bulk update notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned with rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'with_snapshot',
  },
  {
    label: 'Pinned without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'without_snapshot',
  },
  { label: 'Pinned orphans', pinned: 'pinned', source: 'all', bundle: 'orphan', snapshot: 'all' },
  {
    label: 'Pinned orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
  },
  {
    label: 'Pinned orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
  },
  {
    label: 'Pinned with stored bundle',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'all',
  },
  {
    label: 'Pinned with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
  },
  {
    label: 'Pinned with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
  },
  {
    label: 'Pinned manual pin notes',
    pinned: 'pinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned manual pin notes with rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned manual pin notes without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned manual pin notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned manual pin notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned manual pin notes with stored bundle',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned manual pin notes orphans',
    pinned: 'pinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned manual pin notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned manual pin notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Pinned bulk pin holds',
    pinned: 'pinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned bulk pin holds with rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned bulk pin holds without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned bulk pin holds with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned bulk pin holds with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned bulk pin holds with stored bundle',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned bulk pin holds orphans',
    pinned: 'pinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned bulk pin holds orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned bulk pin holds orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Pinned bulk pin by source',
    pinned: 'pinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned bulk pin by source with rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned bulk pin by source without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned bulk pin by source with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned bulk pin by source with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned bulk pin by source with stored bundle',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned bulk pin by source orphans',
    pinned: 'pinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned bulk pin by source orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned bulk pin by source orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Pinned bulk by source notes',
    pinned: 'pinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned bulk by source notes with rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned bulk by source notes without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned bulk by source notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned bulk by source notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned bulk by source notes with stored bundle',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned bulk by source notes orphans',
    pinned: 'pinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned bulk by source notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned bulk by source notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Pinned bulk update notes',
    pinned: 'pinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned bulk update notes with rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned bulk update notes without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned bulk update notes with stored bundle and rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned bulk update notes with stored bundle without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned bulk update notes with stored bundle',
    pinned: 'pinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned bulk update notes orphans',
    pinned: 'pinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned bulk update notes orphans with rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Pinned bulk update notes orphans without rollback snapshot',
    pinned: 'pinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned with rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned orphans',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned with stored bundle',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned imports',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned imports with stored bundle',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned imports with rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned imports without rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned imports with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned imports with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned imports manual pin notes',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned imports bulk pin holds',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned imports bulk pin by source',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned imports bulk by source notes',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned imports bulk update notes',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned imports manual pin notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned imports manual pin notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned imports bulk pin holds with rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned imports bulk pin holds without rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned imports bulk pin by source with rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned imports bulk pin by source without rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned imports bulk by source notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned imports bulk by source notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned imports bulk update notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned imports bulk update notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned imports manual pin notes with stored bundle',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned imports bulk pin holds with stored bundle',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned imports bulk pin by source with stored bundle',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned imports bulk by source notes with stored bundle',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned imports bulk update notes with stored bundle',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned imports manual pin notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned imports manual pin notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned imports bulk pin holds with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned imports bulk pin holds with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned imports bulk pin by source with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned imports bulk pin by source with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned imports bulk by source notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned imports bulk by source notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned imports bulk update notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned imports bulk update notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned imports manual pin notes orphans',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned imports bulk pin holds orphans',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned imports bulk pin by source orphans',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned imports bulk by source notes orphans',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned imports bulk update notes orphans',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned imports manual pin notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned imports manual pin notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned imports bulk pin holds orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned imports bulk pin holds orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned imports bulk pin by source orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned imports bulk pin by source orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned imports bulk by source notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned imports bulk by source notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned imports bulk update notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned imports bulk update notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'import',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned rollbacks',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned rollbacks with stored bundle',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned rollbacks with rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned rollbacks without rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned rollbacks with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned rollbacks with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned rollbacks manual pin notes',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned rollbacks bulk pin holds',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned rollbacks bulk pin by source',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk by source notes',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk update notes',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned rollbacks manual pin notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned rollbacks manual pin notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned rollbacks bulk pin holds with rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned rollbacks bulk pin holds without rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned rollbacks bulk pin by source with rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk pin by source without rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk by source notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk by source notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk update notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned rollbacks bulk update notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned rollbacks manual pin notes with stored bundle',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned rollbacks bulk pin holds with stored bundle',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned rollbacks bulk pin by source with stored bundle',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk by source notes with stored bundle',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk update notes with stored bundle',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned rollbacks manual pin notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned rollbacks manual pin notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned rollbacks bulk pin holds with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned rollbacks bulk pin holds with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned rollbacks bulk pin by source with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk pin by source with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk by source notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk by source notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk update notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned rollbacks bulk update notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned rollbacks manual pin notes orphans',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned rollbacks bulk pin holds orphans',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned rollbacks bulk pin by source orphans',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk by source notes orphans',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk update notes orphans',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned rollbacks manual pin notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned rollbacks manual pin notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned rollbacks bulk pin holds orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned rollbacks bulk pin holds orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned rollbacks bulk pin by source orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk pin by source orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk by source notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk by source notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned rollbacks bulk update notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned rollbacks bulk update notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'rollback',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned re-applies',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned re-applies with stored bundle',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned re-applies with rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned re-applies without rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned re-applies with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned re-applies with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned re-applies manual pin notes',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned re-applies bulk pin holds',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned re-applies bulk pin by source',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned re-applies bulk by source notes',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned re-applies bulk update notes',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned re-applies manual pin notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned re-applies manual pin notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned re-applies bulk pin holds with rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned re-applies bulk pin holds without rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned re-applies bulk pin by source with rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned re-applies bulk pin by source without rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned re-applies bulk by source notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned re-applies bulk by source notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned re-applies bulk update notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned re-applies bulk update notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned re-applies manual pin notes with stored bundle',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned re-applies bulk pin holds with stored bundle',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned re-applies bulk pin by source with stored bundle',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned re-applies bulk by source notes with stored bundle',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned re-applies bulk update notes with stored bundle',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned re-applies manual pin notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned re-applies manual pin notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned re-applies bulk pin holds with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned re-applies bulk pin holds with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned re-applies bulk pin by source with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned re-applies bulk pin by source with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned re-applies bulk by source notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned re-applies bulk by source notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned re-applies bulk update notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned re-applies bulk update notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned re-applies manual pin notes orphans',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned re-applies bulk pin holds orphans',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned re-applies bulk pin by source orphans',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned re-applies bulk by source notes orphans',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned re-applies bulk update notes orphans',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned re-applies manual pin notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned re-applies manual pin notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned re-applies bulk pin holds orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned re-applies bulk pin holds orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned re-applies bulk pin by source orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned re-applies bulk pin by source orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned re-applies bulk by source notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned re-applies bulk by source notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned re-applies bulk update notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned re-applies bulk update notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'reapply',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned platform templates',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned platform templates with stored bundle',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned platform templates with rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned platform templates without rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned platform templates with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned platform templates with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned platform templates manual pin notes',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned platform templates bulk pin holds',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned platform templates bulk pin by source',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned platform templates bulk by source notes',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned platform templates bulk update notes',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned platform templates manual pin notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned platform templates manual pin notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned platform templates bulk pin holds with rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned platform templates bulk pin holds without rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned platform templates bulk pin by source with rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned platform templates bulk pin by source without rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned platform templates bulk by source notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned platform templates bulk by source notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned platform templates bulk update notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned platform templates bulk update notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned platform templates manual pin notes with stored bundle',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned platform templates bulk pin holds with stored bundle',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned platform templates bulk pin by source with stored bundle',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned platform templates bulk by source notes with stored bundle',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned platform templates bulk update notes with stored bundle',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned platform templates manual pin notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned platform templates manual pin notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned platform templates bulk pin holds with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned platform templates bulk pin holds with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned platform templates bulk pin by source with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned platform templates bulk pin by source with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned platform templates bulk by source notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned platform templates bulk by source notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned platform templates bulk update notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned platform templates bulk update notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned platform templates manual pin notes orphans',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned platform templates bulk pin holds orphans',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned platform templates bulk pin by source orphans',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned platform templates bulk by source notes orphans',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned platform templates bulk update notes orphans',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned platform templates manual pin notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned platform templates manual pin notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned platform templates bulk pin holds orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned platform templates bulk pin holds orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned platform templates bulk pin by source orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned platform templates bulk pin by source orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned platform templates bulk by source notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned platform templates bulk by source notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned platform templates bulk update notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned platform templates bulk update notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'platform_template',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned org signup',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned org signup with stored bundle',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned org signup with rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned org signup without rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned org signup with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned org signup with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'all',
  },
  {
    label: 'Unpinned org signup manual pin notes',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned org signup bulk pin holds',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned org signup bulk pin by source',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned org signup bulk by source notes',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned org signup bulk update notes',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned org signup manual pin notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned org signup manual pin notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned org signup bulk pin holds with rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned org signup bulk pin holds without rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned org signup bulk pin by source with rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned org signup bulk pin by source without rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned org signup bulk by source notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned org signup bulk by source notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned org signup bulk update notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned org signup bulk update notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned org signup manual pin notes with stored bundle',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned org signup bulk pin holds with stored bundle',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned org signup bulk pin by source with stored bundle',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned org signup bulk by source notes with stored bundle',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned org signup bulk update notes with stored bundle',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned org signup manual pin notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned org signup manual pin notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned org signup bulk pin holds with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned org signup bulk pin holds with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned org signup bulk pin by source with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned org signup bulk pin by source with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned org signup bulk by source notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned org signup bulk by source notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned org signup bulk update notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned org signup bulk update notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned org signup manual pin notes orphans',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned org signup bulk pin holds orphans',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned org signup bulk pin by source orphans',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned org signup bulk by source notes orphans',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned org signup bulk update notes orphans',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned org signup manual pin notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned org signup manual pin notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned org signup bulk pin holds orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned org signup bulk pin holds orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned org signup bulk pin by source orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned org signup bulk pin by source orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned org signup bulk by source notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned org signup bulk by source notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned org signup bulk update notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned org signup bulk update notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'org_signup',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned manual pin notes',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned manual pin notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned manual pin notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned manual pin notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned manual pin notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned manual pin notes with stored bundle',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned manual pin notes orphans',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned manual pin notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned manual pin notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'manual',
  },
  {
    label: 'Unpinned bulk pin holds',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned bulk pin holds with rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned bulk pin holds without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned bulk pin holds with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned bulk pin holds with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned bulk pin holds with stored bundle',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned bulk pin holds orphans',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned bulk pin holds orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned bulk pin holds orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin',
  },
  {
    label: 'Unpinned bulk pin by source',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned bulk pin by source with rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned bulk pin by source without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned bulk pin by source with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned bulk pin by source with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned bulk pin by source with stored bundle',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned bulk pin by source orphans',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned bulk pin by source orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned bulk pin by source orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_pin_by_source',
  },
  {
    label: 'Unpinned bulk by source notes',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned bulk by source notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned bulk by source notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned bulk by source notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned bulk by source notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned bulk by source notes with stored bundle',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned bulk by source notes orphans',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned bulk by source notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned bulk by source notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_by_source',
  },
  {
    label: 'Unpinned bulk update notes',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned bulk update notes with rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned bulk update notes without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned bulk update notes with stored bundle and rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned bulk update notes with stored bundle without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned bulk update notes with stored bundle',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'with_bundle',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned bulk update notes orphans',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'all',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned bulk update notes orphans with rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'with_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned bulk update notes orphans without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'orphan',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'bulk_update',
  },
  {
    label: 'Unpinned without rollback snapshot',
    pinned: 'unpinned',
    source: 'all',
    bundle: 'all',
    snapshot: 'without_snapshot',
    pinNoteAuditAction: 'all',
  },
];

export function normalizeTenantSeoBundleHistoryExportPlatformPresetLimit(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PLATFORM_PRESET_LIMIT;
  }
  return Math.min(
    MAX_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PLATFORM_PRESET_LIMIT,
    Math.max(MIN_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PLATFORM_PRESET_LIMIT, Math.round(parsed)),
  );
}

export function normalizeTenantSeoBundleHistoryExportPresetLabelSearchMin(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_LABEL_SEARCH_MIN;
  }
  return Math.min(
    MAX_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_LABEL_SEARCH_MIN,
    Math.max(MIN_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_LABEL_SEARCH_MIN, Math.round(parsed)),
  );
}

export function shouldShowTenantSeoBundleHistoryExportPresetLabelSearch(
  presetCount: number,
  labelSearchMin: number,
): boolean {
  return presetCount >= normalizeTenantSeoBundleHistoryExportPresetLabelSearchMin(labelSearchMin);
}

export function filterTenantSeoBundleHistoryExportPresetsByLabelQuery<T extends { label: string }>(
  presets: readonly T[],
  query: string,
): T[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return [...presets];
  return presets.filter((preset) => preset.label.toLowerCase().includes(normalizedQuery));
}

export function filterTenantSeoBundleHistoryExportPresetScopeCountsByLabelQuery<
  T extends { preset: TenantSeoBundleHistoryExportPreset },
>(rows: readonly T[], query: string): T[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return [...rows];
  return rows.filter(({ preset }) => preset.label.toLowerCase().includes(normalizedQuery));
}

export function formatTenantSeoBundleHistoryExportPresetLabelSearchSummary(
  visibleCount: number,
  totalCount: number,
  query: string,
): string | null {
  const normalizedQuery = query.trim();
  if (!normalizedQuery) return null;
  return `Showing ${visibleCount} of ${totalCount} presets matching "${normalizedQuery}"`;
}

export const TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_GROUP_BY_OPTIONS = [
  'none',
  'source',
  'pin_note_audit',
  'pinned',
  'bundle',
  'snapshot',
] as const;

export type TenantSeoBundleHistoryExportPresetGroupBy =
  (typeof TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_GROUP_BY_OPTIONS)[number];

export const DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_GROUP_BY: TenantSeoBundleHistoryExportPresetGroupBy =
  'source';

export function normalizeTenantSeoBundleHistoryExportPresetGroupBy(
  value: unknown,
): TenantSeoBundleHistoryExportPresetGroupBy {
  const raw = String(value ?? DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_GROUP_BY).trim();
  return TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_GROUP_BY_OPTIONS.includes(raw as never)
    ? (raw as TenantSeoBundleHistoryExportPresetGroupBy)
    : DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_GROUP_BY;
}

export function formatTenantSeoBundleHistoryExportPresetGroupByOptionLabel(
  groupBy: TenantSeoBundleHistoryExportPresetGroupBy,
): string {
  switch (groupBy) {
    case 'none':
      return 'None (flat list)';
    case 'source':
      return 'History source';
    case 'pin_note_audit':
      return 'Pin note audit action';
    case 'pinned':
      return 'Pinned state';
    case 'bundle':
      return 'Stored bundle';
    case 'snapshot':
      return 'Rollback snapshot';
    default:
      return groupBy;
  }
}

export function resolveTenantSeoBundleHistoryExportPresetGroupKey(
  preset: TenantSeoBundleHistoryExportPreset,
  groupBy: TenantSeoBundleHistoryExportPresetGroupBy,
): string {
  switch (groupBy) {
    case 'source':
      return preset.source ?? 'all';
    case 'pin_note_audit':
      return preset.pinNoteAuditAction ?? 'all';
    case 'pinned':
      return preset.pinned ?? 'all';
    case 'bundle':
      return preset.bundle ?? 'all';
    case 'snapshot':
      return preset.snapshot ?? 'all';
    default:
      return 'all';
  }
}

export function formatTenantSeoBundleHistoryExportPresetGroupLabel(
  groupKey: string,
  groupBy: TenantSeoBundleHistoryExportPresetGroupBy,
): string {
  if (groupKey === 'all') {
    switch (groupBy) {
      case 'source':
        return 'All sources';
      case 'pin_note_audit':
        return 'All audit actions';
      case 'pinned':
        return 'Any pinned state';
      case 'bundle':
        return 'Any bundle state';
      case 'snapshot':
        return 'Any snapshot state';
      default:
        return 'All';
    }
  }
  switch (groupBy) {
    case 'source':
      return groupKey.replaceAll('_', ' ');
    case 'pin_note_audit':
      return groupKey.replaceAll('_', ' ');
    case 'pinned':
      return groupKey;
    case 'bundle':
      if (groupKey === 'with_bundle') return 'With stored bundle';
      if (groupKey === 'orphan') return 'Orphans';
      return groupKey;
    case 'snapshot':
      if (groupKey === 'with_snapshot') return 'With rollback snapshot';
      if (groupKey === 'without_snapshot') return 'Without snapshot';
      return groupKey;
    default:
      return groupKey;
  }
}

export type TenantSeoBundleHistoryExportPresetGroupSummary = {
  key: string;
  label: string;
  presetCount: number;
};

const TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_GROUP_ORDER: Record<
  Exclude<TenantSeoBundleHistoryExportPresetGroupBy, 'none'>,
  readonly string[]
> = {
  source: ['all', ...TENANT_SEO_BUNDLE_HISTORY_SOURCES],
  pin_note_audit: ['all', 'manual', 'bulk_update', 'bulk_by_source', 'bulk_pin', 'bulk_pin_by_source'],
  pinned: ['all', 'pinned', 'unpinned'],
  bundle: ['all', 'with_bundle', 'orphan'],
  snapshot: ['all', 'with_snapshot', 'without_snapshot'],
};

export function summarizeTenantSeoBundleHistoryExportPresetGroups<
  T extends { preset: TenantSeoBundleHistoryExportPreset },
>(
  rows: readonly T[],
  groupBy: TenantSeoBundleHistoryExportPresetGroupBy,
): TenantSeoBundleHistoryExportPresetGroupSummary[] {
  if (groupBy === 'none') return [];
  const counts = new Map<string, number>();
  for (const row of rows) {
    const key = resolveTenantSeoBundleHistoryExportPresetGroupKey(row.preset, groupBy);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const order = TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_GROUP_ORDER[groupBy];
  const keys = [...counts.keys()].sort((left, right) => {
    const leftIndex = order.indexOf(left);
    const rightIndex = order.indexOf(right);
    if (leftIndex >= 0 && rightIndex >= 0) return leftIndex - rightIndex;
    if (leftIndex >= 0) return -1;
    if (rightIndex >= 0) return 1;
    return left.localeCompare(right);
  });
  return keys.map((key) => ({
    key,
    label: formatTenantSeoBundleHistoryExportPresetGroupLabel(key, groupBy),
    presetCount: counts.get(key) ?? 0,
  }));
}

export function filterTenantSeoBundleHistoryExportPresetScopeCountsByGroup<
  T extends { preset: TenantSeoBundleHistoryExportPreset },
>(rows: readonly T[], groupKey: string, groupBy: TenantSeoBundleHistoryExportPresetGroupBy): T[] {
  if (!groupKey || groupBy === 'none') return [...rows];
  return rows.filter(
    (row) => resolveTenantSeoBundleHistoryExportPresetGroupKey(row.preset, groupBy) === groupKey,
  );
}

export function shouldShowTenantSeoBundleHistoryExportPresetGroupNav(
  presetCount: number,
  groupBy: TenantSeoBundleHistoryExportPresetGroupBy,
  labelSearchMin: number,
): boolean {
  return (
    groupBy !== 'none' && shouldShowTenantSeoBundleHistoryExportPresetLabelSearch(presetCount, labelSearchMin)
  );
}

export function formatTenantSeoBundleHistoryExportPresetGroupFilterSummary(
  visibleCount: number,
  totalCount: number,
  groupKey: string,
  groupBy: TenantSeoBundleHistoryExportPresetGroupBy,
): string | null {
  if (!groupKey || groupBy === 'none') return null;
  return `Showing ${visibleCount} of ${totalCount} presets in ${formatTenantSeoBundleHistoryExportPresetGroupLabel(groupKey, groupBy)}`;
}

export function normalizeTenantSeoBundleHistoryExportPresetChipExpandLimit(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_EXPAND_LIMIT;
  }
  return Math.min(
    MAX_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_EXPAND_LIMIT,
    Math.max(MIN_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_EXPAND_LIMIT, Math.round(parsed)),
  );
}

export function normalizeTenantSeoBundleHistoryExportPresetChipJumpAmbiguousLimit(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_JUMP_AMBIGUOUS_LIMIT;
  }
  return Math.min(
    MAX_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_JUMP_AMBIGUOUS_LIMIT,
    Math.max(MIN_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_JUMP_AMBIGUOUS_LIMIT, Math.round(parsed)),
  );
}

export function shouldCollapseTenantSeoBundleHistoryExportPresetChips(
  visibleCount: number,
  expandLimit: number,
  expanded: boolean,
): boolean {
  if (expanded) return false;
  return visibleCount > normalizeTenantSeoBundleHistoryExportPresetChipExpandLimit(expandLimit);
}

export function resolveTenantSeoBundleHistoryExportPresetChipsForDisplay<
  T extends { preset: TenantSeoBundleHistoryExportPreset },
>(rows: readonly T[], expandLimit: number, expanded: boolean): T[] {
  const limit = normalizeTenantSeoBundleHistoryExportPresetChipExpandLimit(expandLimit);
  if (!shouldCollapseTenantSeoBundleHistoryExportPresetChips(rows.length, limit, expanded)) {
    return [...rows];
  }
  return rows.slice(0, limit);
}

export function formatTenantSeoBundleHistoryExportPresetChipExpandSummary(
  visibleCount: number,
  expandLimit: number,
  expanded: boolean,
): string | null {
  const limit = normalizeTenantSeoBundleHistoryExportPresetChipExpandLimit(expandLimit);
  if (!shouldCollapseTenantSeoBundleHistoryExportPresetChips(visibleCount, limit, expanded)) {
    return null;
  }
  return `Showing first ${limit} of ${visibleCount} presets`;
}

export function normalizeTenantSeoBundleHistoryExportPresetChipPageSize(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_SIZE;
  }
  return Math.min(
    MAX_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_SIZE,
    Math.max(MIN_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_SIZE, Math.round(parsed)),
  );
}

export function resolveTenantSeoBundleHistoryExportPresetChipPageCount(
  visibleCount: number,
  expanded: boolean,
  pageSize: number,
): number {
  if (!expanded || visibleCount <= 0) return 1;
  const normalizedPageSize = normalizeTenantSeoBundleHistoryExportPresetChipPageSize(pageSize);
  return Math.max(1, Math.ceil(visibleCount / normalizedPageSize));
}

export function clampTenantSeoBundleHistoryExportPresetChipPageIndex(
  pageIndex: number,
  pageCount: number,
): number {
  const normalizedPageCount = Math.max(1, Math.round(pageCount));
  return Math.min(Math.max(0, Math.round(pageIndex)), normalizedPageCount - 1);
}

export function shouldShowTenantSeoBundleHistoryExportPresetChipPagination(
  visibleCount: number,
  expandLimit: number,
  expanded: boolean,
): boolean {
  return expanded && visibleCount > normalizeTenantSeoBundleHistoryExportPresetChipExpandLimit(expandLimit);
}

export function resolveTenantSeoBundleHistoryExportPresetChipsForDisplayPaginated<
  T extends { preset: TenantSeoBundleHistoryExportPreset },
>(rows: readonly T[], expandLimit: number, expanded: boolean, pageIndex: number, pageSize: number): T[] {
  const normalizedExpandLimit = normalizeTenantSeoBundleHistoryExportPresetChipExpandLimit(expandLimit);
  if (!expanded) {
    if (rows.length > normalizedExpandLimit) {
      return rows.slice(0, normalizedExpandLimit);
    }
    return [...rows];
  }
  const normalizedPageSize = normalizeTenantSeoBundleHistoryExportPresetChipPageSize(pageSize);
  const normalizedPageIndex = clampTenantSeoBundleHistoryExportPresetChipPageIndex(
    pageIndex,
    resolveTenantSeoBundleHistoryExportPresetChipPageCount(rows.length, true, normalizedPageSize),
  );
  const start = normalizedPageIndex * normalizedPageSize;
  return rows.slice(start, start + normalizedPageSize);
}

export function formatTenantSeoBundleHistoryExportPresetChipPageSummary(
  pageIndex: number,
  pageCount: number,
  pageSize: number,
  visibleCount: number,
): string {
  const normalizedPageIndex = clampTenantSeoBundleHistoryExportPresetChipPageIndex(pageIndex, pageCount);
  const normalizedPageSize = normalizeTenantSeoBundleHistoryExportPresetChipPageSize(pageSize);
  const start = normalizedPageIndex * normalizedPageSize + 1;
  const end = Math.min(visibleCount, (normalizedPageIndex + 1) * normalizedPageSize);
  return `Page ${normalizedPageIndex + 1} of ${pageCount} · presets ${start}–${end} of ${visibleCount}`;
}

export function normalizeTenantSeoBundleHistoryExportPresetsDraftForCompare(draftText: string): string {
  return draftText.replace(/\r\n/g, '\n').trimEnd();
}

export function isTenantSeoBundleHistoryExportPresetsDraftDirty(
  draftText: string,
  savedBaselineDraft: string,
): boolean {
  return (
    normalizeTenantSeoBundleHistoryExportPresetsDraftForCompare(draftText) !==
    normalizeTenantSeoBundleHistoryExportPresetsDraftForCompare(savedBaselineDraft)
  );
}

export function formatTenantSeoBundleHistoryExportPresetsDraftDirtySummary(dirty: boolean): string | null {
  return dirty ? 'Unsaved draft changes' : null;
}

export function isTenantSeoBundleHistoryExportPresetsDraftEmpty(draftText: string): boolean {
  return normalizeTenantSeoBundleHistoryExportPresetsDraftForCompare(draftText).length === 0;
}

export const TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_DRAFT_UNSAVED_LEAVE_MESSAGE =
  'You have unsaved export preset draft changes.';

export function shouldWarnOnTenantSeoBundleHistoryExportPresetsDraftLeave(dirty: boolean): boolean {
  return dirty;
}

export type TenantSeoBundleHistoryExportPresetChipJumpPageResult =
  { ok: true; pageIndex: number } | { ok: false; message: string };

export function parseTenantSeoBundleHistoryExportPresetChipJumpPageInput(
  rawInput: string,
  pageCount: number,
): TenantSeoBundleHistoryExportPresetChipJumpPageResult {
  const normalizedPageCount = Math.max(1, Math.round(pageCount));
  const trimmed = rawInput.trim();
  if (!trimmed) {
    return { ok: false, message: 'Enter a page number.' };
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 1) {
    return { ok: false, message: 'Enter a valid page number.' };
  }
  if (parsed > normalizedPageCount) {
    return { ok: false, message: `Page must be between 1 and ${normalizedPageCount}.` };
  }
  return { ok: true, pageIndex: parsed - 1 };
}

export function formatTenantSeoBundleHistoryExportPresetChipJumpPagePlaceholder(pageCount: number): string {
  const normalizedPageCount = Math.max(1, Math.round(pageCount));
  return `1–${normalizedPageCount}`;
}

export function formatTenantSeoBundleHistoryExportPresetsDraftLeaveConfirmMessage(): string {
  return 'You have unsaved export preset draft changes. Discard them and continue?';
}

export function canProceedAfterTenantSeoBundleHistoryExportPresetsDraftLeaveCheck(
  dirty: boolean,
  confirmed: boolean,
): boolean {
  return !dirty || confirmed;
}

export function resolveTenantSeoBundleHistoryExportPresetChipRowIndexByLabel<
  T extends { preset: TenantSeoBundleHistoryExportPreset },
>(rows: readonly T[], labelQuery: string, partialMatchMin = 1): number {
  const matches = listTenantSeoBundleHistoryExportPresetChipPartialMatchIndicesByLabel(
    rows,
    labelQuery,
    partialMatchMin,
  );
  return matches[0] ?? -1;
}

export function listTenantSeoBundleHistoryExportPresetChipPartialMatchIndicesByLabel<
  T extends { preset: TenantSeoBundleHistoryExportPreset },
>(rows: readonly T[], labelQuery: string, partialMatchMin = 1): number[] {
  const trimmed = labelQuery.trim();
  if (!trimmed) return [];
  const lower = trimmed.toLowerCase();
  if (lower.length < Math.max(1, Math.round(partialMatchMin))) return [];
  const partialMatches = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => row.preset.label.toLowerCase().includes(lower));
  partialMatches.sort(
    (left, right) => left.row.preset.label.length - right.row.preset.label.length || left.index - right.index,
  );
  return partialMatches.map(({ index }) => index);
}

export type TenantSeoBundleHistoryExportPresetChipLabelJumpCandidate = {
  rowIndex: number;
  pageIndex: number;
  label: string;
};

export function formatTenantSeoBundleHistoryExportPresetChipLabelJumpAmbiguousSummary(
  query: string,
  visibleCount: number,
  totalCount: number,
): string {
  const trimmed = query.trim();
  if (visibleCount >= totalCount) {
    return `${totalCount} presets match "${trimmed}". Choose one:`;
  }
  return `Showing ${visibleCount} of ${totalCount} presets matching "${trimmed}". Choose one:`;
}

export function formatTenantSeoBundleHistoryExportPresetChipLabelJumpMatchSummary(
  query: string,
  label: string,
  matchType: 'exact' | 'partial',
): string | null {
  if (matchType === 'exact') return null;
  const trimmed = query.trim();
  if (!trimmed) return null;
  return `Partial label match for "${trimmed}" → "${label}"`;
}

export function resolveTenantSeoBundleHistoryExportPresetChipPageIndexForRowIndex(
  rowIndex: number,
  rowCount: number,
  pageSize: number,
): number {
  if (rowIndex < 0 || rowCount <= 0) return 0;
  const normalizedPageSize = normalizeTenantSeoBundleHistoryExportPresetChipPageSize(pageSize);
  const pageCount = resolveTenantSeoBundleHistoryExportPresetChipPageCount(
    rowCount,
    true,
    normalizedPageSize,
  );
  return clampTenantSeoBundleHistoryExportPresetChipPageIndex(
    Math.floor(rowIndex / normalizedPageSize),
    pageCount,
  );
}

export type TenantSeoBundleHistoryExportPresetChipLabelJumpResult =
  | {
      ok: true;
      rowIndex: number;
      pageIndex: number;
      label: string;
      needsExpand: boolean;
      matchType: 'exact' | 'partial';
    }
  | {
      ok: false;
      message: string;
      ambiguous?: boolean;
      needsExpand?: boolean;
      totalMatchCount?: number;
      hasMoreCandidates?: boolean;
      candidates?: readonly TenantSeoBundleHistoryExportPresetChipLabelJumpCandidate[];
    };

export function buildTenantSeoBundleHistoryExportPresetChipLabelJumpCandidatesFromIndices<
  T extends { preset: TenantSeoBundleHistoryExportPreset },
>(
  rows: readonly T[],
  indices: readonly number[],
  pageSize: number,
): TenantSeoBundleHistoryExportPresetChipLabelJumpCandidate[] {
  return indices.map((rowIndex) => ({
    rowIndex,
    label: rows[rowIndex]!.preset.label,
    pageIndex: resolveTenantSeoBundleHistoryExportPresetChipPageIndexForRowIndex(
      rowIndex,
      rows.length,
      pageSize,
    ),
  }));
}

export function resolveTenantSeoBundleHistoryExportPresetChipLabelJumpAmbiguousCandidates<
  T extends { preset: TenantSeoBundleHistoryExportPreset },
>(
  rows: readonly T[],
  labelQuery: string,
  pageSize: number,
  partialMatchMin = 1,
  ambiguousCandidateLimit = DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_JUMP_AMBIGUOUS_LIMIT,
): {
  candidates: readonly TenantSeoBundleHistoryExportPresetChipLabelJumpCandidate[];
  totalMatchCount: number;
  hasMoreCandidates: boolean;
} {
  const partialIndices = listTenantSeoBundleHistoryExportPresetChipPartialMatchIndicesByLabel(
    rows,
    labelQuery,
    partialMatchMin,
  );
  const normalizedLimit =
    normalizeTenantSeoBundleHistoryExportPresetChipJumpAmbiguousLimit(ambiguousCandidateLimit);
  const visibleIndices = partialIndices.slice(0, normalizedLimit);
  return {
    candidates: buildTenantSeoBundleHistoryExportPresetChipLabelJumpCandidatesFromIndices(
      rows,
      visibleIndices,
      pageSize,
    ),
    totalMatchCount: partialIndices.length,
    hasMoreCandidates: partialIndices.length > visibleIndices.length,
  };
}

export function formatTenantSeoBundleHistoryExportPresetChipLabelJumpShowMoreLabel(
  visibleCount: number,
  totalCount: number,
): string {
  return `Show ${totalCount - visibleCount} more matches (${totalCount} total)`;
}

export function buildTenantSeoBundleHistoryExportPresetDraftExpandSessionFingerprint(
  draftText: string,
): string {
  const normalized = normalizeTenantSeoBundleHistoryExportPresetsDraftForCompare(draftText);
  if (!normalized) return '0';
  const lineCount = normalized.split('\n').length;
  return `${lineCount}:${normalized.length}`;
}

export function shouldWriteTenantSeoBundleHistoryExportPresetDraftChipExpandSession(
  draftDirty: boolean,
  persistEnabled: boolean,
): boolean {
  return persistEnabled && !draftDirty;
}

export function resolveTenantSeoBundleHistoryExportPresetChipLabelJumpTarget<
  T extends { preset: TenantSeoBundleHistoryExportPreset },
>(
  rows: readonly T[],
  labelQuery: string,
  expandLimit: number,
  expanded: boolean,
  pageSize: number,
  partialMatchMin = 1,
  ambiguousCandidateLimit = DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_JUMP_AMBIGUOUS_LIMIT,
): TenantSeoBundleHistoryExportPresetChipLabelJumpResult {
  const trimmed = labelQuery.trim();
  if (!trimmed) {
    return { ok: false, message: 'Enter a preset label to jump to.' };
  }
  const exactIndex = rows.findIndex((row) => row.preset.label === trimmed);
  const caseInsensitiveExactIndex =
    exactIndex >= 0
      ? exactIndex
      : rows.findIndex((row) => row.preset.label.toLowerCase() === trimmed.toLowerCase());
  const resolvedExactIndex = exactIndex >= 0 ? exactIndex : caseInsensitiveExactIndex;
  if (resolvedExactIndex >= 0) {
    const label = rows[resolvedExactIndex]!.preset.label;
    const needsExpand = shouldCollapseTenantSeoBundleHistoryExportPresetChips(
      rows.length,
      expandLimit,
      expanded,
    );
    const pageIndex = resolveTenantSeoBundleHistoryExportPresetChipPageIndexForRowIndex(
      resolvedExactIndex,
      rows.length,
      pageSize,
    );
    return {
      ok: true,
      rowIndex: resolvedExactIndex,
      pageIndex,
      label,
      needsExpand,
      matchType: 'exact',
    };
  }
  const partialIndices = listTenantSeoBundleHistoryExportPresetChipPartialMatchIndicesByLabel(
    rows,
    labelQuery,
    partialMatchMin,
  );
  if (partialIndices.length === 0) {
    const minLength = Math.max(1, Math.round(partialMatchMin));
    return {
      ok: false,
      message:
        trimmed.length < minLength
          ? `Enter at least ${minLength} characters for partial preset label jump.`
          : `No preset chip found matching "${trimmed}".`,
    };
  }
  const needsExpand = shouldCollapseTenantSeoBundleHistoryExportPresetChips(
    rows.length,
    expandLimit,
    expanded,
  );
  if (partialIndices.length > 1) {
    const candidateLimit =
      normalizeTenantSeoBundleHistoryExportPresetChipJumpAmbiguousLimit(ambiguousCandidateLimit);
    const visibleIndices = partialIndices.slice(0, candidateLimit);
    const candidates = buildTenantSeoBundleHistoryExportPresetChipLabelJumpCandidatesFromIndices(
      rows,
      visibleIndices,
      pageSize,
    );
    return {
      ok: false,
      ambiguous: true,
      needsExpand,
      candidates,
      totalMatchCount: partialIndices.length,
      hasMoreCandidates: partialIndices.length > candidates.length,
      message: formatTenantSeoBundleHistoryExportPresetChipLabelJumpAmbiguousSummary(
        trimmed,
        candidates.length,
        partialIndices.length,
      ),
    };
  }
  const rowIndex = partialIndices[0]!;
  const label = rows[rowIndex]!.preset.label;
  const pageIndex = resolveTenantSeoBundleHistoryExportPresetChipPageIndexForRowIndex(
    rowIndex,
    rows.length,
    pageSize,
  );
  return { ok: true, rowIndex, pageIndex, label, needsExpand, matchType: 'partial' };
}

export function buildTenantSeoBundleHistoryExportPresetChipPageSessionKey(input: {
  scope: 'saved' | 'draft';
  groupBy: string;
  groupKey: string;
  labelQuery: string;
}): string {
  return [
    'tenant-seo-bundle-export-preset-chip-page',
    input.scope,
    input.groupBy,
    input.groupKey,
    input.labelQuery.trim().toLowerCase(),
  ].join(':');
}

export function readTenantSeoBundleHistoryExportPresetChipPageSession(
  key: string,
  pageCount: number,
  persistEnabled: boolean,
): number | null {
  if (!persistEnabled || typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return null;
    return clampTenantSeoBundleHistoryExportPresetChipPageIndex(parsed, pageCount);
  } catch {
    return null;
  }
}

export function writeTenantSeoBundleHistoryExportPresetChipPageSession(
  key: string,
  pageIndex: number,
  persistEnabled: boolean,
): void {
  if (!persistEnabled || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(key, String(Math.max(0, Math.round(pageIndex))));
  } catch {
    // ignore quota / privacy mode failures
  }
}

export function buildTenantSeoBundleHistoryExportPresetChipExpandSessionKey(input: {
  scope: 'saved' | 'draft';
  groupBy: string;
  groupKey: string;
  labelQuery: string;
  draftFingerprint?: string;
}): string {
  return [
    'tenant-seo-bundle-export-preset-chip-expanded',
    input.scope,
    input.groupBy,
    input.groupKey,
    input.labelQuery.trim().toLowerCase(),
    input.scope === 'draft' ? (input.draftFingerprint ?? '') : '',
  ].join(':');
}

export function readTenantSeoBundleHistoryExportPresetChipExpandedSession(
  key: string,
  persistEnabled: boolean,
): boolean | null {
  if (!persistEnabled || typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (raw === '1') return true;
    if (raw === '0') return false;
    return null;
  } catch {
    return null;
  }
}

export function writeTenantSeoBundleHistoryExportPresetChipExpandedSession(
  key: string,
  expanded: boolean,
  persistEnabled: boolean,
): void {
  if (!persistEnabled || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(key, expanded ? '1' : '0');
  } catch {
    // ignore quota / privacy mode failures
  }
}

export function shouldHandleTenantSeoBundleHistoryExportPresetChipPageKeydown(
  event: Pick<KeyboardEvent, 'key' | 'target'>,
): boolean {
  const target = event.target;
  if (target && typeof target === 'object' && 'tagName' in target) {
    const tag = String((target as { tagName: string }).tagName).toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return false;
    if (
      'isContentEditable' in target &&
      Boolean((target as { isContentEditable: boolean }).isContentEditable)
    ) {
      return false;
    }
  }
  return event.key === 'ArrowLeft' || event.key === 'ArrowRight';
}

export function shouldHandleTenantSeoBundleHistoryExportPresetChipAmbiguousKeydown(
  event: Pick<KeyboardEvent, 'key' | 'target'>,
): boolean {
  const target = event.target;
  if (target && typeof target === 'object' && 'tagName' in target) {
    const tag = String((target as { tagName: string }).tagName).toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return false;
    if (
      'isContentEditable' in target &&
      Boolean((target as { isContentEditable: boolean }).isContentEditable)
    ) {
      return false;
    }
  }
  return (
    event.key === 'ArrowLeft' ||
    event.key === 'ArrowRight' ||
    event.key === 'ArrowUp' ||
    event.key === 'ArrowDown' ||
    event.key === 'Home' ||
    event.key === 'End' ||
    event.key === 'Enter' ||
    event.key === 'Escape'
  );
}

export function clampTenantSeoBundleHistoryExportPresetChipAmbiguousIndex(
  index: number,
  candidateCount: number,
): number {
  if (candidateCount <= 0) return 0;
  return Math.max(0, Math.min(candidateCount - 1, Math.round(index)));
}

export function resolveTenantSeoBundleHistoryExportPresetChipAmbiguousIndexAfterKeydown(
  key: string,
  currentIndex: number,
  candidateCount: number,
): number | null {
  if (candidateCount <= 0) return null;
  const clampedCurrent = clampTenantSeoBundleHistoryExportPresetChipAmbiguousIndex(
    currentIndex,
    candidateCount,
  );
  switch (key) {
    case 'ArrowRight':
    case 'ArrowDown':
      return clampTenantSeoBundleHistoryExportPresetChipAmbiguousIndex(clampedCurrent + 1, candidateCount);
    case 'ArrowLeft':
    case 'ArrowUp':
      return clampTenantSeoBundleHistoryExportPresetChipAmbiguousIndex(clampedCurrent - 1, candidateCount);
    case 'Home':
      return 0;
    case 'End':
      return candidateCount - 1;
    default:
      return null;
  }
}

export function formatTenantSeoBundleHistoryExportPresetChipAmbiguousFocusClass(
  index: number,
  focusIndex: number,
): string {
  return index === focusIndex ? 'ring-2 ring-indigo-400/70 ring-offset-1 ring-offset-transparent' : '';
}

export type TenantSeoBundleHistoryExportPresetChipAmbiguousPickerScope = 'saved' | 'draft';

export function buildTenantSeoBundleHistoryExportPresetChipAmbiguousOptionId(
  scope: TenantSeoBundleHistoryExportPresetChipAmbiguousPickerScope,
  index: number,
): string {
  return `tenant-seo-export-preset-chip-ambiguous-option-${scope}-${index}`;
}

export function resolveTenantSeoBundleHistoryExportPresetChipAmbiguousActiveDescendant(
  scope: TenantSeoBundleHistoryExportPresetChipAmbiguousPickerScope,
  focusIndex: number,
  candidateCount: number,
): string | undefined {
  if (candidateCount <= 0 || focusIndex < 0 || focusIndex >= candidateCount) return undefined;
  return buildTenantSeoBundleHistoryExportPresetChipAmbiguousOptionId(scope, focusIndex);
}

export function formatTenantSeoBundleHistoryExportPresetChipAmbiguousOptionSelected(
  index: number,
  focusIndex: number,
): boolean {
  return index === focusIndex;
}

export function shouldConfirmTenantSeoBundleHistorySearchQueryClear(
  currentQuery: string,
  nextQuery: string,
): boolean {
  return Boolean(currentQuery.trim()) && !nextQuery.trim();
}

export function shouldApplyTenantSeoBundleHistorySearchQuery(
  appliedQuery: string,
  inputQuery: string,
): boolean {
  return appliedQuery.trim() !== inputQuery.trim();
}

export function formatTenantSeoBundleHistorySearchApplyLabel(inputQuery: string): string {
  return inputQuery.trim() ? 'Apply search' : 'Clear search';
}

export const TENANT_SEO_BUNDLE_HISTORY_FILTER_RESULTS_STATUS_ID =
  'tenant-seo-bundle-history-filter-results-status';

export function formatTenantSeoBundleHistoryFilterResultsStatus(
  shownCount: number,
  totalCount: number,
): string {
  if (totalCount <= 0) return 'No history entries.';
  if (shownCount === totalCount) {
    return `${totalCount} history entr${totalCount === 1 ? 'y' : 'ies'} shown.`;
  }
  return `${shownCount} of ${totalCount} history entries shown after filters.`;
}

/** Screen-reader summary of which history filters are currently applied (Phase 290). */
export function formatTenantSeoBundleHistoryActiveFiltersStatus(
  filter: TenantSeoBundleHistoryFilter,
): string {
  const parts = describeTenantSeoBundleHistoryActiveFilters(filter);
  if (parts.length === 0) return 'No history filters active.';
  return `${parts.length} filter${parts.length === 1 ? '' : 's'} active: ${parts.join(', ')}.`;
}

/** Count of currently applied history filters (Phase 291 visible badge). */
export function countTenantSeoBundleHistoryActiveFilters(filter: TenantSeoBundleHistoryFilter): number {
  return describeTenantSeoBundleHistoryActiveFilters(filter).length;
}

/** Compact visible badge label for the active history filter count (Phase 291). */
export function formatTenantSeoBundleHistoryActiveFilterBadge(count: number): string {
  return `${count} active filter${count === 1 ? '' : 's'}`;
}

export function formatTenantSeoBundleHistoryExportPresetChipAmbiguousFocusAnnouncement(
  label: string,
  focusIndex: number,
  candidateCount: number,
): string {
  if (candidateCount <= 0 || focusIndex < 0 || focusIndex >= candidateCount || !label.trim()) {
    return '';
  }
  return `Preset ${focusIndex + 1} of ${candidateCount}: ${label}`;
}

export function shouldSkipTenantSeoBundleHistoryExportPresetChipAmbiguousFocusAnnouncement(
  skipInitialFocusRef: { current: boolean },
  candidateCount: number,
): boolean {
  if (candidateCount <= 0) {
    skipInitialFocusRef.current = false;
    return true;
  }
  if (!skipInitialFocusRef.current) {
    skipInitialFocusRef.current = true;
    return true;
  }
  return false;
}

export function announceTenantSeoBundleHistoryExportPresetChipAmbiguousFocus(
  label: string,
  focusIndex: number,
  candidateCount: number,
): void {
  const message = formatTenantSeoBundleHistoryExportPresetChipAmbiguousFocusAnnouncement(
    label,
    focusIndex,
    candidateCount,
  );
  if (!message || typeof document === 'undefined' || typeof window === 'undefined') return;
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', 'polite');
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0';
  announcement.textContent = message;
  document.body.appendChild(announcement);
  window.setTimeout(() => {
    announcement.remove();
  }, 1000);
}

export function focusTenantSeoBundleHistoryExportPresetChipAmbiguousPicker(
  element: HTMLElement | null,
): void {
  if (!element || typeof window === 'undefined') return;
  window.setTimeout(() => {
    element.focus();
  }, 0);
}

export function scrollTenantSeoBundleHistoryExportPresetChipAmbiguousCandidateIntoView(
  container: HTMLElement | null,
  focusIndex: number,
): void {
  if (!container || focusIndex < 0 || typeof window === 'undefined') return;
  const options = container.querySelectorAll('[role="option"]');
  const target = options.item(focusIndex);
  if (target && 'scrollIntoView' in target) {
    target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}

export function resolveTenantSeoBundleHistoryExportPresetChipPageAfterKeydown(
  key: string,
  pageIndex: number,
  pageCount: number,
): number | null {
  if (key === 'ArrowLeft') {
    return clampTenantSeoBundleHistoryExportPresetChipPageIndex(pageIndex - 1, pageCount);
  }
  if (key === 'ArrowRight') {
    return clampTenantSeoBundleHistoryExportPresetChipPageIndex(pageIndex + 1, pageCount);
  }
  return null;
}

export function resolveTenantSeoBundleHistoryExportPresetsForOrgDraft(
  platformPresets: readonly TenantSeoBundleHistoryExportPreset[],
  limit: number = TENANT_SEO_BUNDLE_HISTORY_EXPORT_ORG_DRAFT_PRESET_LIMIT,
): TenantSeoBundleHistoryExportPreset[] {
  const effectiveLimit = Math.max(1, Math.round(limit));
  return platformPresets.slice(0, effectiveLimit).map((preset) => ({ ...preset }));
}

export function buildTenantSeoBundleHistoryExportPresetsOrgDraftFromPlatform(
  platformPresets: readonly TenantSeoBundleHistoryExportPreset[],
  limit: number = TENANT_SEO_BUNDLE_HISTORY_EXPORT_ORG_DRAFT_PRESET_LIMIT,
): string {
  return formatTenantSeoBundleHistoryExportPresetsDraft(
    resolveTenantSeoBundleHistoryExportPresetsForOrgDraft(platformPresets, limit),
  );
}

export type TenantSeoBundleHistoryExportPresetsOrgDraftMergeResult = {
  draft: string;
  mergedCount: number;
  atLimit: boolean;
  lineCount: number;
};

export function mergeTenantSeoBundleHistoryExportPresetsOrgDraftFromPlatform(
  currentDraft: string,
  platformPresets: readonly TenantSeoBundleHistoryExportPreset[],
  limit: number = TENANT_SEO_BUNDLE_HISTORY_EXPORT_ORG_DRAFT_PRESET_LIMIT,
): TenantSeoBundleHistoryExportPresetsOrgDraftMergeResult {
  const effectiveLimit = Math.max(1, Math.round(limit));
  let draft = currentDraft;
  let mergedCount = 0;
  const existingLabels = new Set(
    parseTenantSeoBundleHistoryExportPresetsDraft(draft, effectiveLimit).map((preset) =>
      preset.label.toLowerCase(),
    ),
  );
  for (const preset of platformPresets) {
    const lineCount = parseTenantSeoBundleHistoryExportPresetsDraft(draft, effectiveLimit).length;
    if (lineCount >= effectiveLimit) break;
    if (existingLabels.has(preset.label.toLowerCase())) continue;
    const line = formatTenantSeoBundleHistoryExportPresetsDraft([preset]);
    const appendResult = appendTenantSeoBundleHistoryExportPresetsDraftLine(draft, line, effectiveLimit);
    if (!appendResult.appended) break;
    draft = appendResult.draft;
    existingLabels.add(preset.label.toLowerCase());
    mergedCount += 1;
  }
  const finalLineCount = parseTenantSeoBundleHistoryExportPresetsDraft(draft, effectiveLimit).length;
  return {
    draft,
    mergedCount,
    atLimit: finalLineCount >= effectiveLimit,
    lineCount: finalLineCount,
  };
}

export function formatTenantSeoBundleHistoryExportPresetsOrgDraftMergeMessage(
  result: TenantSeoBundleHistoryExportPresetsOrgDraftMergeResult,
  limit: number,
): string {
  if (result.mergedCount === 0) {
    return result.atLimit
      ? `Draft preset limit reached (${limit} lines). No platform presets merged.`
      : 'No new platform presets to merge (matching labels already in draft).';
  }
  return `Merged ${result.mergedCount} platform preset${result.mergedCount === 1 ? '' : 's'} into draft (${result.lineCount}/${limit} lines).`;
}

export function countTenantSeoBundleHistoryExportPresetsDraftLines(
  draftText: string,
  limit: number = TENANT_SEO_BUNDLE_HISTORY_EXPORT_ORG_DRAFT_PRESET_LIMIT,
): number {
  return parseTenantSeoBundleHistoryExportPresetsDraft(draftText, limit).length;
}

export type TenantSeoBundleHistoryExportPresetCountsExportContext = {
  groupBy?: TenantSeoBundleHistoryExportPresetGroupBy;
  groupKey?: string;
  groupLabel?: string;
  labelQuery?: string;
  visibleCount?: number;
  totalCount?: number;
  draftPreview?: boolean;
  filtersActive?: boolean;
};

export function buildTenantSeoBundleHistoryExportPresetDraftPreviewExportContext(input: {
  visibleCounts: readonly unknown[];
  totalCounts: readonly unknown[];
  filtersActive: boolean;
  groupBy?: TenantSeoBundleHistoryExportPresetGroupBy;
  groupKey?: string;
  labelQuery?: string;
}): TenantSeoBundleHistoryExportPresetCountsExportContext {
  return {
    ...buildTenantSeoBundleHistoryExportPresetCountsExportContext({
      groupBy: input.groupBy ?? 'none',
      groupKey: input.groupKey,
      labelQuery: input.labelQuery,
      visibleCounts: input.visibleCounts,
      totalCounts: input.totalCounts,
    }),
    draftPreview: true,
    filtersActive: input.filtersActive,
  };
}

export function buildTenantSeoBundleHistoryExportPresetCountsExportContext(input: {
  groupBy: TenantSeoBundleHistoryExportPresetGroupBy;
  groupKey?: string;
  labelQuery?: string;
  visibleCounts: readonly unknown[];
  totalCounts: readonly unknown[];
}): TenantSeoBundleHistoryExportPresetCountsExportContext {
  const groupKey = input.groupKey?.trim() ?? '';
  const labelQuery = input.labelQuery?.trim() ?? '';
  return {
    groupBy: input.groupBy,
    ...(groupKey && input.groupBy !== 'none'
      ? {
          groupKey,
          groupLabel: formatTenantSeoBundleHistoryExportPresetGroupLabel(groupKey, input.groupBy),
        }
      : {}),
    ...(labelQuery ? { labelQuery } : {}),
    visibleCount: input.visibleCounts.length,
    totalCount: input.totalCounts.length,
  };
}

export function formatTenantSeoBundleHistoryExportPresetCountsExportFilename(
  prefix: string,
  extension: 'csv' | 'json',
  context?: TenantSeoBundleHistoryExportPresetCountsExportContext,
): string {
  const slugParts = [prefix];
  if (context?.draftPreview) slugParts.push('draft-preview');
  if (context?.groupKey && context.groupBy && context.groupBy !== 'none') {
    slugParts.push(context.groupKey);
  }
  if (context?.labelQuery) {
    slugParts.push(
      context.labelQuery
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 40),
    );
  }
  return `${slugParts.join('-')}.${extension}`;
}

function tenantSeoBundleHistoryExportPresetCountsExportContextHasFilterScope(
  context: TenantSeoBundleHistoryExportPresetCountsExportContext,
): boolean {
  return Boolean(
    (context.groupBy && context.groupBy !== 'none' && context.groupKey) ||
    context.labelQuery ||
    (context.visibleCount !== undefined &&
      context.totalCount !== undefined &&
      context.visibleCount !== context.totalCount),
  );
}

function serializeTenantSeoBundleHistoryExportPresetCountsExportContextComments(
  context: TenantSeoBundleHistoryExportPresetCountsExportContext,
): string[] {
  const lines: string[] = [];
  if (context.draftPreview) lines.push('# draft_preview: true');
  if (context.filtersActive !== undefined) {
    lines.push(`# filters_active: ${context.filtersActive ? 'true' : 'false'}`);
  }
  if (tenantSeoBundleHistoryExportPresetCountsExportContextHasFilterScope(context)) {
    lines.push('# export_scope: filtered');
  }
  if (context.groupBy && context.groupBy !== 'none') {
    lines.push(`# group_by: ${context.groupBy}`);
  }
  if (context.groupKey) lines.push(`# group_key: ${context.groupKey}`);
  if (context.groupLabel) lines.push(`# group_label: ${context.groupLabel}`);
  if (context.labelQuery) lines.push(`# label_query: ${context.labelQuery}`);
  if (context.visibleCount !== undefined) lines.push(`# visible_count: ${context.visibleCount}`);
  if (context.totalCount !== undefined) lines.push(`# total_count: ${context.totalCount}`);
  return lines;
}

export type TenantSeoBundleHistoryExportPresetsDraftAppendResult = {
  draft: string;
  appended: boolean;
  atLimit: boolean;
  lineCount: number;
};

export function appendTenantSeoBundleHistoryExportPresetsDraftLine(
  currentDraft: string,
  line: string,
  limit: number = TENANT_SEO_BUNDLE_HISTORY_EXPORT_ORG_DRAFT_PRESET_LIMIT,
): TenantSeoBundleHistoryExportPresetsDraftAppendResult {
  const effectiveLimit = Math.max(1, Math.round(limit));
  const trimmedLine = line.trim();
  const currentLines = parseTenantSeoBundleHistoryExportPresetsDraft(currentDraft, effectiveLimit);
  if (!trimmedLine) {
    return {
      draft: currentDraft,
      appended: false,
      atLimit: currentLines.length >= effectiveLimit,
      lineCount: currentLines.length,
    };
  }
  if (currentLines.length >= effectiveLimit) {
    return {
      draft: currentDraft,
      appended: false,
      atLimit: true,
      lineCount: currentLines.length,
    };
  }
  const nextDraft = currentDraft.trim() ? `${currentDraft.trim()}\n${trimmedLine}` : trimmedLine;
  return {
    draft: nextDraft,
    appended: true,
    atLimit: currentLines.length + 1 >= effectiveLimit,
    lineCount: currentLines.length + 1,
  };
}

export function formatTenantSeoBundleHistoryExportPresetsDraftAppendMessage(
  result: TenantSeoBundleHistoryExportPresetsDraftAppendResult,
  limit: number,
  successLabel: string,
): string {
  if (result.appended) {
    return `${successLabel} (${result.lineCount}/${limit} draft lines).`;
  }
  if (result.atLimit) {
    return `Draft preset limit reached (${limit} lines). Remove a line before appending.`;
  }
  return 'Nothing appended to export preset draft.';
}

export function serializeTenantSeoBundleHistoryExportPresetForStorage(
  preset: TenantSeoBundleHistoryExportPreset,
): Record<string, string> {
  return {
    label: preset.label,
    pinned: preset.pinned ?? 'all',
    source: preset.source ?? 'all',
    bundle: preset.bundle ?? 'all',
    snapshot: preset.snapshot ?? 'all',
    pin_note_audit_action: preset.pinNoteAuditAction ?? 'all',
  };
}

export function normalizeTenantSeoBundleHistoryExportPresets(
  value: unknown,
  limit: number = DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PLATFORM_PRESET_LIMIT,
): TenantSeoBundleHistoryExportPreset[] {
  const effectiveLimit = Math.max(1, Math.round(limit));
  const raw =
    value && typeof value === 'object'
      ? ((value as Record<string, unknown>).presets ??
        (value as Record<string, unknown>).export_presets ??
        value)
      : value;
  if (!Array.isArray(raw)) return [...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESETS];
  const presets: TenantSeoBundleHistoryExportPreset[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const label = String(row.label ?? '').trim();
    if (!label) continue;
    const sourceRaw = String(row.source ?? 'all').trim() as TenantSeoBundleHistorySourceFilter;
    const source =
      sourceRaw === 'all' || TENANT_SEO_BUNDLE_HISTORY_SOURCES.includes(sourceRaw as never)
        ? sourceRaw
        : 'all';
    const pinnedRaw = String(row.pinned ?? 'all').trim() as 'all' | 'pinned' | 'unpinned';
    const pinned = pinnedRaw === 'pinned' || pinnedRaw === 'unpinned' ? pinnedRaw : ('all' as const);
    const bundleRaw = String(row.bundle ?? 'all').trim() as TenantSeoBundleHistoryBundleFilter;
    const bundle = bundleRaw === 'with_bundle' || bundleRaw === 'orphan' ? bundleRaw : ('all' as const);
    const snapshotRaw = String(row.snapshot ?? 'all').trim() as TenantSeoBundleHistorySnapshotFilter;
    const snapshot =
      snapshotRaw === 'with_snapshot' || snapshotRaw === 'without_snapshot' ? snapshotRaw : ('all' as const);
    const pinNoteAuditActionRaw = String(
      row.pin_note_audit_action ?? row.pinNoteAuditAction ?? 'all',
    ).trim() as TenantSeoBundleHistoryPinNoteAuditActionFilter;
    const pinNoteAuditAction =
      pinNoteAuditActionRaw === 'all' ||
      pinNoteAuditActionRaw === 'manual' ||
      pinNoteAuditActionRaw === 'bulk_update' ||
      pinNoteAuditActionRaw === 'bulk_by_source' ||
      pinNoteAuditActionRaw === 'bulk_pin' ||
      pinNoteAuditActionRaw === 'bulk_pin_by_source'
        ? pinNoteAuditActionRaw
        : ('all' as const);
    presets.push({ label: label.slice(0, 80), source, pinned, bundle, snapshot, pinNoteAuditAction });
    if (presets.length >= effectiveLimit) break;
  }
  return presets.length > 0 ? presets : [...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESETS];
}

export function resolveTenantSeoBundleHistoryFilterFromExportPreset(
  preset: TenantSeoBundleHistoryExportPreset,
): TenantSeoBundleHistoryFilter {
  return {
    source: preset.source ?? 'all',
    pinned: preset.pinned ?? 'all',
    bundle: preset.bundle ?? 'all',
    snapshot: preset.snapshot ?? 'all',
    pinNoteAuditAction: preset.pinNoteAuditAction ?? 'all',
  };
}

export type TenantSeoBundleHistoryExportTableFilter = {
  source: TenantSeoBundleHistorySourceFilter;
  bundle: TenantSeoBundleHistoryBundleFilter;
  snapshot: TenantSeoBundleHistorySnapshotFilter;
  pinned: 'all' | 'pinned' | 'unpinned';
  pinNoteAuditAction: TenantSeoBundleHistoryPinNoteAuditActionFilter;
};

export function normalizeTenantSeoBundleHistoryExportTableFilter(
  filter: TenantSeoBundleHistoryFilter,
): TenantSeoBundleHistoryExportTableFilter {
  return {
    source: filter.source ?? 'all',
    bundle: filter.bundle ?? 'all',
    snapshot: filter.snapshot ?? 'all',
    pinned: filter.pinned ?? 'all',
    pinNoteAuditAction: filter.pinNoteAuditAction ?? 'all',
  };
}

export function tenantSeoBundleHistoryExportTableFiltersEqual(
  left: TenantSeoBundleHistoryFilter,
  right: TenantSeoBundleHistoryFilter,
): boolean {
  const a = normalizeTenantSeoBundleHistoryExportTableFilter(left);
  const b = normalizeTenantSeoBundleHistoryExportTableFilter(right);
  return (
    a.source === b.source &&
    a.bundle === b.bundle &&
    a.snapshot === b.snapshot &&
    a.pinned === b.pinned &&
    a.pinNoteAuditAction === b.pinNoteAuditAction
  );
}

export function findTenantSeoBundleHistoryExportPresetMatchingFilter(
  presets: readonly TenantSeoBundleHistoryExportPreset[],
  filter: TenantSeoBundleHistoryFilter,
): TenantSeoBundleHistoryExportPreset | undefined {
  return presets.find((preset) => isTenantSeoBundleHistoryExportPresetExactFilterMatch(preset, filter));
}

export function isTenantSeoBundleHistoryExportPresetExactFilterMatch(
  preset: TenantSeoBundleHistoryExportPreset,
  filter: TenantSeoBundleHistoryFilter,
): boolean {
  return tenantSeoBundleHistoryExportTableFiltersEqual(
    filter,
    resolveTenantSeoBundleHistoryFilterFromExportPreset(preset),
  );
}

export function hasTenantSeoBundleHistoryActiveFilters(filter: TenantSeoBundleHistoryFilter): boolean {
  const table = normalizeTenantSeoBundleHistoryExportTableFilter(filter);
  if (
    table.source !== 'all' ||
    table.bundle !== 'all' ||
    table.snapshot !== 'all' ||
    table.pinned !== 'all' ||
    table.pinNoteAuditAction !== 'all'
  ) {
    return true;
  }
  if (filter.query?.trim()) return true;
  return filter.queryScope === 'pin_note' || filter.queryScope === 'pin_note_audit';
}

export type TenantSeoBundleHistoryActiveFilterKey =
  'source' | 'pinned' | 'bundle' | 'snapshot' | 'pinNoteAuditAction' | 'query';

export type TenantSeoBundleHistoryActiveFilterChip = {
  key: TenantSeoBundleHistoryActiveFilterKey;
  label: string;
};

/** Structured list of applied history filters for dismissible chips (Phase 292). */
export function listTenantSeoBundleHistoryActiveFilterChips(
  filter: TenantSeoBundleHistoryFilter,
): TenantSeoBundleHistoryActiveFilterChip[] {
  const chips: TenantSeoBundleHistoryActiveFilterChip[] = [];
  const table = normalizeTenantSeoBundleHistoryExportTableFilter(filter);
  if (table.source !== 'all') {
    chips.push({ key: 'source', label: `source: ${table.source.replaceAll('_', ' ')}` });
  }
  if (table.pinned !== 'all') chips.push({ key: 'pinned', label: table.pinned });
  if (table.bundle !== 'all') chips.push({ key: 'bundle', label: table.bundle.replaceAll('_', ' ') });
  if (table.snapshot !== 'all') {
    chips.push({ key: 'snapshot', label: table.snapshot.replaceAll('_', ' ') });
  }
  if (table.pinNoteAuditAction !== 'all') {
    chips.push({
      key: 'pinNoteAuditAction',
      label: `audit: ${table.pinNoteAuditAction.replaceAll('_', ' ')}`,
    });
  }
  const query = filter.query?.trim();
  if (query) {
    const scopeLabel =
      filter.queryScope === 'pin_note'
        ? 'pin note'
        : filter.queryScope === 'pin_note_audit'
          ? 'pin note audit'
          : 'search';
    chips.push({ key: 'query', label: `${scopeLabel}: "${query}"` });
  }
  return chips;
}

export function describeTenantSeoBundleHistoryActiveFilters(filter: TenantSeoBundleHistoryFilter): string[] {
  return listTenantSeoBundleHistoryActiveFilterChips(filter).map((chip) => chip.label);
}

export function formatTenantSeoBundleHistoryFilterChipRemoveLabel(label: string): string {
  return `Remove ${label} filter`;
}

export function formatTenantSeoBundleHistoryFilterChipRemovedAnnouncement(label: string): string {
  return `Removed ${label} filter.`;
}

/**
 * Flattened setter values for a history filter snapshot — used to restore an
 * undone clear in the org + platform panels (Phase 293).
 */
export type TenantSeoBundleHistoryFilterSetterValues = {
  source: TenantSeoBundleHistoryExportTableFilter['source'];
  pinned: TenantSeoBundleHistoryExportTableFilter['pinned'];
  bundle: TenantSeoBundleHistoryExportTableFilter['bundle'];
  snapshot: TenantSeoBundleHistoryExportTableFilter['snapshot'];
  pinNoteAuditAction: TenantSeoBundleHistoryExportTableFilter['pinNoteAuditAction'];
  query: string;
  pinNotesOnly: boolean;
  pinNoteAuditOnly: boolean;
};

export function resolveTenantSeoBundleHistoryFilterSetterValues(
  filter: TenantSeoBundleHistoryFilter,
): TenantSeoBundleHistoryFilterSetterValues {
  const table = normalizeTenantSeoBundleHistoryExportTableFilter(filter);
  return {
    source: table.source,
    pinned: table.pinned,
    bundle: table.bundle,
    snapshot: table.snapshot,
    pinNoteAuditAction: table.pinNoteAuditAction,
    query: filter.query?.trim() ?? '',
    pinNotesOnly: filter.queryScope === 'pin_note',
    pinNoteAuditOnly: filter.queryScope === 'pin_note_audit',
  };
}

export function formatTenantSeoBundleHistoryFilterUndoLabel(count: number): string {
  return `Undo (restore ${count} filter${count === 1 ? '' : 's'})`;
}

export function formatTenantSeoBundleHistoryFilterRestoredAnnouncement(count: number): string {
  if (count === 0) return 'Restored cleared history filters.';
  return `Restored ${count} history filter${count === 1 ? '' : 's'}.`;
}

export const TENANT_SEO_BUNDLE_HISTORY_FILTER_CHIP_GROUP_LABEL = 'Active history filters';

/** Shareable one-line summary of the applied history filters (Phase 295). */
export function formatTenantSeoBundleHistoryActiveFilterSummary(
  filter: TenantSeoBundleHistoryFilter,
): string {
  const parts = describeTenantSeoBundleHistoryActiveFilters(filter);
  if (parts.length === 0) return 'No history filters active.';
  return `Active history filters: ${parts.join(', ')}.`;
}

export const TENANT_SEO_BUNDLE_HISTORY_FILTER_SUMMARY_COPIED_MESSAGE = 'Copied filter summary to clipboard.';

/**
 * Encode a history filter into a compact, lossless, shareable token
 * (querystring form) so a filter view can be copied and re-applied elsewhere
 * (Phase 296). Only non-default dimensions are included, in a stable key order.
 */
export function encodeTenantSeoBundleHistoryFilterToken(filter: TenantSeoBundleHistoryFilter): string {
  const table = normalizeTenantSeoBundleHistoryExportTableFilter(filter);
  const params = new URLSearchParams();
  if (table.source !== 'all') params.set('source', table.source);
  if (table.pinned !== 'all') params.set('pinned', table.pinned);
  if (table.bundle !== 'all') params.set('bundle', table.bundle);
  if (table.snapshot !== 'all') params.set('snapshot', table.snapshot);
  if (table.pinNoteAuditAction !== 'all') params.set('audit', table.pinNoteAuditAction);
  const query = filter.query?.trim() ?? '';
  if (query) params.set('q', query);
  if (filter.queryScope === 'pin_note' || filter.queryScope === 'pin_note_audit') {
    params.set('scope', filter.queryScope);
  }
  return params.toString();
}

export function decodeTenantSeoBundleHistoryFilterToken(token: string): TenantSeoBundleHistoryFilter {
  const params = new URLSearchParams((token ?? '').trim().replace(/^[?#]/, ''));
  const filter: TenantSeoBundleHistoryFilter = {};
  const source = params.get('source');
  if (source) filter.source = source as TenantSeoBundleHistoryExportTableFilter['source'];
  const pinned = params.get('pinned');
  if (pinned === 'pinned' || pinned === 'unpinned') filter.pinned = pinned;
  const bundle = params.get('bundle');
  if (bundle) filter.bundle = bundle as TenantSeoBundleHistoryExportTableFilter['bundle'];
  const snapshot = params.get('snapshot');
  if (snapshot) filter.snapshot = snapshot as TenantSeoBundleHistoryExportTableFilter['snapshot'];
  const audit = params.get('audit');
  if (audit) {
    filter.pinNoteAuditAction = audit as TenantSeoBundleHistoryExportTableFilter['pinNoteAuditAction'];
  }
  const query = params.get('q');
  if (query) filter.query = query;
  const scope = params.get('scope');
  if (scope === 'pin_note' || scope === 'pin_note_audit') filter.queryScope = scope;
  return filter;
}

export const TENANT_SEO_BUNDLE_HISTORY_FILTER_TOKEN_COPIED_MESSAGE = 'Copied filter token to clipboard.';

export const TENANT_SEO_BUNDLE_HISTORY_FILTER_TOKEN_PROMPT_LABEL = 'Paste a filter token to apply';

export function formatTenantSeoBundleHistoryFilterTokenAppliedAnnouncement(
  filter: TenantSeoBundleHistoryFilter,
): string {
  const count = countTenantSeoBundleHistoryActiveFilters(filter);
  if (count === 0) return 'Applied filter token (no active filters).';
  return `Applied filter token: ${count} filter${count === 1 ? '' : 's'}.`;
}

export const TENANT_SEO_BUNDLE_HISTORY_FILTER_URL_PARAM = 'seoHistoryFilter';

export const TENANT_SEO_BUNDLE_HISTORY_FILTER_LINK_COPIED_MESSAGE =
  'Copied shareable filter link to clipboard.';

/**
 * Build a shareable absolute URL embedding the filter token in the
 * `seoHistoryFilter` query param (Phase 298). Removes the param when no
 * filters are active so the link stays clean.
 */
export function buildTenantSeoBundleHistoryFilterShareUrl(
  baseUrl: string,
  filter: TenantSeoBundleHistoryFilter,
): string {
  let url: URL;
  try {
    url = new URL(baseUrl);
  } catch {
    return baseUrl;
  }
  const token = encodeTenantSeoBundleHistoryFilterToken(filter);
  if (token) {
    url.searchParams.set(TENANT_SEO_BUNDLE_HISTORY_FILTER_URL_PARAM, token);
  } else {
    url.searchParams.delete(TENANT_SEO_BUNDLE_HISTORY_FILTER_URL_PARAM);
  }
  return url.toString();
}

export function extractTenantSeoBundleHistoryFilterTokenFromUrl(url: string): string {
  try {
    return new URL(url).searchParams.get(TENANT_SEO_BUNDLE_HISTORY_FILTER_URL_PARAM)?.trim() ?? '';
  } catch {
    return '';
  }
}

/**
 * Per-tenant localStorage persistence for the active history filter
 * (Phase 300). The key is scoped so different tenants/orgs never clobber each
 * other's remembered view. Storage is injected for testability and all access
 * is wrapped so disabled/quota-exceeded storage degrades gracefully.
 */
export function tenantSeoBundleHistoryFilterStorageKey(scope: string): string {
  const trimmed = (scope ?? '').trim();
  return `tsb:seo-history-filter:${trimmed || 'default'}`;
}

export function writeTenantSeoBundleHistoryFilterToStorage(
  storage: Pick<Storage, 'setItem' | 'removeItem'>,
  scope: string,
  filter: TenantSeoBundleHistoryFilter,
): void {
  const key = tenantSeoBundleHistoryFilterStorageKey(scope);
  try {
    const token = encodeTenantSeoBundleHistoryFilterToken(filter);
    if (token) storage.setItem(key, token);
    else storage.removeItem(key);
  } catch {
    // ignore storage write failures (disabled / quota)
  }
}

export function readTenantSeoBundleHistoryFilterFromStorage(
  storage: Pick<Storage, 'getItem'>,
  scope: string,
): TenantSeoBundleHistoryFilter | null {
  try {
    const token = storage.getItem(tenantSeoBundleHistoryFilterStorageKey(scope));
    if (!token) return null;
    const filter = decodeTenantSeoBundleHistoryFilterToken(token);
    return hasTenantSeoBundleHistoryActiveFilters(filter) ? filter : null;
  } catch {
    return null;
  }
}

export const TENANT_SEO_BUNDLE_HISTORY_FILTER_RESTORED_FROM_STORAGE_MESSAGE =
  'Restored your last history filter view.';

/**
 * Named, per-tenant saved filter views persisted in localStorage (Phase 301).
 * Each view stores a filter token so it round-trips through the same
 * encode/decode path as shareable links.
 */
export type TenantSeoBundleHistorySavedView = { name: string; token: string };

export const TENANT_SEO_BUNDLE_HISTORY_SAVED_VIEWS_LIMIT = 20;
export const TENANT_SEO_BUNDLE_HISTORY_SAVED_VIEWS_LIMIT_MIN = 1;
export const TENANT_SEO_BUNDLE_HISTORY_SAVED_VIEWS_LIMIT_MAX = 100;

export function normalizeTenantSeoBundleHistorySavedViewsLimit(value: unknown): number {
  const parsed =
    typeof value === 'number' ? value : typeof value === 'string' ? Number.parseInt(value, 10) : Number.NaN;
  if (!Number.isFinite(parsed)) return TENANT_SEO_BUNDLE_HISTORY_SAVED_VIEWS_LIMIT;
  return Math.min(
    TENANT_SEO_BUNDLE_HISTORY_SAVED_VIEWS_LIMIT_MAX,
    Math.max(TENANT_SEO_BUNDLE_HISTORY_SAVED_VIEWS_LIMIT_MIN, Math.floor(parsed)),
  );
}

export function tenantSeoBundleHistorySavedViewsStorageKey(scope: string): string {
  const trimmed = (scope ?? '').trim();
  return `tsb:seo-history-saved-views:${trimmed || 'default'}`;
}

export function normalizeTenantSeoBundleHistorySavedViews(value: unknown): TenantSeoBundleHistorySavedView[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const views: TenantSeoBundleHistorySavedView[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const record = item as Record<string, unknown>;
    const name = typeof record.name === 'string' ? record.name.trim() : '';
    const token = typeof record.token === 'string' ? record.token : '';
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    views.push({ name, token });
  }
  return views.sort((a, b) => a.name.localeCompare(b.name));
}

export function upsertTenantSeoBundleHistorySavedView(
  views: TenantSeoBundleHistorySavedView[],
  name: string,
  filter: TenantSeoBundleHistoryFilter,
  limit: number = TENANT_SEO_BUNDLE_HISTORY_SAVED_VIEWS_LIMIT,
): TenantSeoBundleHistorySavedView[] {
  const trimmed = name.trim();
  const normalized = normalizeTenantSeoBundleHistorySavedViews(views);
  if (!trimmed) return normalized;
  const others = normalized.filter((v) => v.name.toLowerCase() !== trimmed.toLowerCase());
  const next = [...others, { name: trimmed, token: encodeTenantSeoBundleHistoryFilterToken(filter) }].sort(
    (a, b) => a.name.localeCompare(b.name),
  );
  return next.slice(0, normalizeTenantSeoBundleHistorySavedViewsLimit(limit));
}

export function removeTenantSeoBundleHistorySavedView(
  views: TenantSeoBundleHistorySavedView[],
  name: string,
): TenantSeoBundleHistorySavedView[] {
  const target = name.trim().toLowerCase();
  return normalizeTenantSeoBundleHistorySavedViews(views).filter((v) => v.name.toLowerCase() !== target);
}

/**
 * Duplicate a saved view under a non-colliding "(copy)" name, preserving its
 * token. Returns the list unchanged when the source is missing or the limit is
 * already reached.
 */
export function duplicateTenantSeoBundleHistorySavedView(
  views: TenantSeoBundleHistorySavedView[],
  name: string,
  limit: number = TENANT_SEO_BUNDLE_HISTORY_SAVED_VIEWS_LIMIT,
): TenantSeoBundleHistorySavedView[] {
  const normalized = normalizeTenantSeoBundleHistorySavedViews(views);
  const cap = normalizeTenantSeoBundleHistorySavedViewsLimit(limit);
  const source = normalized.find((v) => v.name.toLowerCase() === name.trim().toLowerCase());
  if (!source || normalized.length >= cap) return normalized;
  const taken = new Set(normalized.map((v) => v.name.toLowerCase()));
  let candidate = `${source.name} (copy)`;
  let counter = 2;
  while (taken.has(candidate.toLowerCase())) {
    candidate = `${source.name} (copy ${counter})`;
    counter += 1;
  }
  return normalizeTenantSeoBundleHistorySavedViews([...normalized, { name: candidate, token: source.token }]);
}

/**
 * Human-readable "n / limit saved views" label so admins can see how close
 * they are to the configurable cap. Appends "(limit reached)" at the cap.
 */
export function formatTenantSeoBundleHistorySavedViewsCountLabel(
  count: number,
  limit: number = TENANT_SEO_BUNDLE_HISTORY_SAVED_VIEWS_LIMIT,
): string {
  const cap = normalizeTenantSeoBundleHistorySavedViewsLimit(limit);
  const safeCount = Math.max(0, Math.floor(count));
  const suffix = safeCount >= cap ? ' (limit reached)' : '';
  return `${safeCount} / ${cap} saved views${suffix}`;
}

/**
 * Filter saved views by a case-insensitive substring of their name. An empty
 * or whitespace-only query returns the full normalized list.
 */
export function filterTenantSeoBundleHistorySavedViews(
  views: TenantSeoBundleHistorySavedView[],
  query: string,
): TenantSeoBundleHistorySavedView[] {
  const normalized = normalizeTenantSeoBundleHistorySavedViews(views);
  const needle = query.trim().toLowerCase();
  if (!needle) return normalized;
  return normalized.filter((v) => v.name.toLowerCase().includes(needle));
}

/**
 * Overwrite an existing saved view's token (e.g. to capture the current
 * filter back into the view). No-op when the view name does not exist — unlike
 * upsert, this never creates a new view.
 */
export function updateTenantSeoBundleHistorySavedViewToken(
  views: TenantSeoBundleHistorySavedView[],
  name: string,
  token: string,
): TenantSeoBundleHistorySavedView[] {
  const target = name.trim().toLowerCase();
  return normalizeTenantSeoBundleHistorySavedViews(views).map((v) =>
    v.name.toLowerCase() === target ? { name: v.name, token } : v,
  );
}

/** Rename a saved view, preserving its token. A blank or unchanged name is a no-op. */
export function renameTenantSeoBundleHistorySavedView(
  views: TenantSeoBundleHistorySavedView[],
  fromName: string,
  toName: string,
): TenantSeoBundleHistorySavedView[] {
  const normalized = normalizeTenantSeoBundleHistorySavedViews(views);
  const from = fromName.trim().toLowerCase();
  const to = toName.trim();
  const existing = normalized.find((v) => v.name.toLowerCase() === from);
  if (!existing || !to) return normalized;
  const others = normalized.filter(
    (v) => v.name.toLowerCase() !== from && v.name.toLowerCase() !== to.toLowerCase(),
  );
  return normalizeTenantSeoBundleHistorySavedViews([...others, { name: to, token: existing.token }]);
}

export function serializeTenantSeoBundleHistorySavedViews(views: TenantSeoBundleHistorySavedView[]): string {
  return JSON.stringify(normalizeTenantSeoBundleHistorySavedViews(views), null, 2);
}

export function parseTenantSeoBundleHistorySavedViews(json: string): TenantSeoBundleHistorySavedView[] {
  try {
    return normalizeTenantSeoBundleHistorySavedViews(JSON.parse(json));
  } catch {
    return [];
  }
}

/** Merge imported views into existing ones — incoming wins by name — then cap. */
export function mergeTenantSeoBundleHistorySavedViews(
  existing: TenantSeoBundleHistorySavedView[],
  incoming: TenantSeoBundleHistorySavedView[],
  limit: number = TENANT_SEO_BUNDLE_HISTORY_SAVED_VIEWS_LIMIT,
): TenantSeoBundleHistorySavedView[] {
  const byName = new Map<string, TenantSeoBundleHistorySavedView>();
  for (const view of normalizeTenantSeoBundleHistorySavedViews(existing)) {
    byName.set(view.name.toLowerCase(), view);
  }
  for (const view of normalizeTenantSeoBundleHistorySavedViews(incoming)) {
    byName.set(view.name.toLowerCase(), view);
  }
  return normalizeTenantSeoBundleHistorySavedViews([...byName.values()]).slice(
    0,
    normalizeTenantSeoBundleHistorySavedViewsLimit(limit),
  );
}

/**
 * Find the saved view (if any) whose filter matches the supplied filter, so
 * the UI can mark the currently-active view (Phase 304). Both sides are
 * canonicalized through encode(decode(...)) to be order-insensitive.
 */
export function findTenantSeoBundleHistoryMatchingSavedView(
  views: TenantSeoBundleHistorySavedView[],
  filter: TenantSeoBundleHistoryFilter,
): TenantSeoBundleHistorySavedView | null {
  const target = encodeTenantSeoBundleHistoryFilterToken(filter);
  for (const view of normalizeTenantSeoBundleHistorySavedViews(views)) {
    const canonical = encodeTenantSeoBundleHistoryFilterToken(
      decodeTenantSeoBundleHistoryFilterToken(view.token),
    );
    if (canonical === target) return view;
  }
  return null;
}

export function readTenantSeoBundleHistorySavedViews(
  storage: Pick<Storage, 'getItem'>,
  scope: string,
): TenantSeoBundleHistorySavedView[] {
  try {
    const raw = storage.getItem(tenantSeoBundleHistorySavedViewsStorageKey(scope));
    if (!raw) return [];
    return normalizeTenantSeoBundleHistorySavedViews(JSON.parse(raw));
  } catch {
    return [];
  }
}

export function writeTenantSeoBundleHistorySavedViews(
  storage: Pick<Storage, 'setItem' | 'removeItem'>,
  scope: string,
  views: TenantSeoBundleHistorySavedView[],
): void {
  const key = tenantSeoBundleHistorySavedViewsStorageKey(scope);
  try {
    const normalized = normalizeTenantSeoBundleHistorySavedViews(views);
    if (normalized.length === 0) storage.removeItem(key);
    else storage.setItem(key, JSON.stringify(normalized));
  } catch {
    // ignore storage write failures (disabled / quota)
  }
}

export type TenantSeoBundleHistoryFilterTokenValidation = {
  filter: TenantSeoBundleHistoryFilter;
  unknownKeys: string[];
  invalidValueKeys: string[];
};

const TENANT_SEO_BUNDLE_HISTORY_FILTER_TOKEN_KNOWN_KEYS = new Set([
  'source',
  'pinned',
  'bundle',
  'snapshot',
  'audit',
  'q',
  'scope',
]);

/**
 * Decode a filter token while reporting any keys that were ignored — unknown
 * keys or known keys whose values failed enum validation (Phase 297). Lets the
 * apply flow tell the user a pasted token was partially malformed.
 */
export function validateTenantSeoBundleHistoryFilterToken(
  token: string,
): TenantSeoBundleHistoryFilterTokenValidation {
  const params = new URLSearchParams((token ?? '').trim().replace(/^[?#]/, ''));
  const unknownKeys: string[] = [];
  for (const key of params.keys()) {
    if (!TENANT_SEO_BUNDLE_HISTORY_FILTER_TOKEN_KNOWN_KEYS.has(key) && !unknownKeys.includes(key)) {
      unknownKeys.push(key);
    }
  }
  const allowedValues: Record<string, ReadonlySet<string>> = {
    source: new Set(TENANT_SEO_BUNDLE_HISTORY_SOURCES),
    pinned: new Set(['pinned', 'unpinned']),
    bundle: new Set(['with_bundle', 'orphan']),
    snapshot: new Set(['with_snapshot', 'without_snapshot']),
    audit: new Set(['manual', 'bulk_update', 'bulk_by_source', 'bulk_pin', 'bulk_pin_by_source']),
    scope: new Set(['pin_note', 'pin_note_audit']),
  };
  const invalidValueKeys: string[] = [];
  for (const [key, allowed] of Object.entries(allowedValues)) {
    const value = params.get(key);
    if (value !== null && !allowed.has(value)) invalidValueKeys.push(key);
  }
  const filter = decodeTenantSeoBundleHistoryFilterToken(token);
  const tokenKeyToFilterKey: Record<string, keyof TenantSeoBundleHistoryFilter> = {
    source: 'source',
    bundle: 'bundle',
    snapshot: 'snapshot',
    audit: 'pinNoteAuditAction',
    scope: 'queryScope',
  };
  for (const key of invalidValueKeys) {
    const filterKey = tokenKeyToFilterKey[key];
    if (filterKey) delete filter[filterKey];
  }
  return { filter, unknownKeys, invalidValueKeys };
}

function appendTenantSeoBundleHistoryFilterTokenIssues(
  base: string,
  validation: TenantSeoBundleHistoryFilterTokenValidation,
): string {
  const ignored = validation.unknownKeys.length + validation.invalidValueKeys.length;
  if (ignored === 0) return base;
  return `${base} Ignored ${ignored} unrecognized ${ignored === 1 ? 'value' : 'values'}.`;
}

export function formatTenantSeoBundleHistoryFilterTokenAppliedAnnouncementWithIssues(
  validation: TenantSeoBundleHistoryFilterTokenValidation,
): string {
  return appendTenantSeoBundleHistoryFilterTokenIssues(
    formatTenantSeoBundleHistoryFilterTokenAppliedAnnouncement(validation.filter),
    validation,
  );
}

export function formatTenantSeoBundleHistoryFilterTokenOpenedAnnouncement(
  validation: TenantSeoBundleHistoryFilterTokenValidation,
): string {
  const count = countTenantSeoBundleHistoryActiveFilters(validation.filter);
  const base =
    count === 0
      ? 'Opened shared filter link (no active filters).'
      : `Opened shared filter link: ${count} filter${count === 1 ? '' : 's'}.`;
  return appendTenantSeoBundleHistoryFilterTokenIssues(base, validation);
}

/**
 * Resolve the next chip dismiss-button index for roving arrow-key focus
 * (Phase 294). Returns null when the key is not a navigation key or there are
 * no chips, leaving focus untouched.
 */
export function resolveTenantSeoBundleHistoryChipFocusIndex(
  currentIndex: number,
  key: string,
  total: number,
): number | null {
  if (total <= 0) return null;
  switch (key) {
    case 'ArrowRight':
    case 'ArrowDown':
      return (currentIndex + 1) % total;
    case 'ArrowLeft':
    case 'ArrowUp':
      return (currentIndex - 1 + total) % total;
    case 'Home':
      return 0;
    case 'End':
      return total - 1;
    default:
      return null;
  }
}

export function formatTenantSeoBundleHistoryFilterAsExportPresetDraft(
  filter: TenantSeoBundleHistoryFilter,
  label = 'Custom filter',
): string {
  const table = normalizeTenantSeoBundleHistoryExportTableFilter(filter);
  return formatTenantSeoBundleHistoryExportPresetsDraft([{ label, ...table }]);
}

export function formatTenantSeoBundleHistoryFilterAsExportPresetDraftWithMatchScore(
  filter: TenantSeoBundleHistoryFilter,
  options: { label?: string; matchScore?: number } = {},
): string {
  const line = formatTenantSeoBundleHistoryFilterAsExportPresetDraft(
    filter,
    options.label ?? 'Custom filter',
  );
  if (options.matchScore === undefined) return line;
  return `${line}\n# filter_match_score: ${options.matchScore} (${formatTenantSeoBundleHistoryExportPresetMatchScoreLabel(options.matchScore)})`;
}

export function resolveTenantSeoBundleHistoryExportPresetDraftMatchScore(
  presets: readonly TenantSeoBundleHistoryExportPreset[],
  filter: TenantSeoBundleHistoryFilter,
): number | undefined {
  const matches = resolveTenantSeoBundleHistoryExportPresetActiveFilterMatches(presets, filter);
  if (matches.exactMatchScore !== undefined) return matches.exactMatchScore;
  return matches.topMatches[0]?.score;
}

export function resolveTenantSeoBundleHistoryExportPresetTopMatchForAppend(
  presets: readonly TenantSeoBundleHistoryExportPreset[],
  filter: TenantSeoBundleHistoryFilter,
): { preset: TenantSeoBundleHistoryExportPreset; score: number } | undefined {
  const matches = resolveTenantSeoBundleHistoryExportPresetActiveFilterMatches(presets, filter);
  if (matches.exactMatch) return undefined;
  return matches.topMatches[0];
}

export function formatTenantSeoBundleHistoryExportPresetAsDraftLineWithMatchScore(
  preset: TenantSeoBundleHistoryExportPreset,
  filter: TenantSeoBundleHistoryFilter,
): string {
  return formatTenantSeoBundleHistoryFilterAsExportPresetDraftWithMatchScore(
    resolveTenantSeoBundleHistoryFilterFromExportPreset(preset),
    {
      label: preset.label,
      matchScore: scoreTenantSeoBundleHistoryExportPresetFilterMatch(filter, preset),
    },
  );
}

export function summarizeTenantSeoBundleHistoryPinNoteAuditActions(
  history: readonly TenantSeoBundleHistoryEntry[],
): Partial<Record<TenantSeoBundleHistoryPinNoteAuditAction, number>> {
  const counts: Partial<Record<TenantSeoBundleHistoryPinNoteAuditAction, number>> = {};
  for (const entry of history) {
    const latest = getTenantSeoBundleHistoryLatestPinNoteAudit(entry);
    if (!latest) continue;
    counts[latest.action] = (counts[latest.action] ?? 0) + 1;
  }
  return counts;
}

export function summarizePlatformTenantSeoBundleHistoryPinNoteAuditActions(
  rows: readonly PlatformTenantSeoBundleHistoryAuditRow[],
  filter?: TenantSeoBundleHistoryFilter,
): Partial<Record<TenantSeoBundleHistoryPinNoteAuditAction, number>> {
  const counts: Partial<Record<TenantSeoBundleHistoryPinNoteAuditAction, number>> = {};
  for (const row of rows) {
    for (const entry of filterTenantSeoBundleHistoryEntries(row.config.seoBundleHistory, filter)) {
      const latest = getTenantSeoBundleHistoryLatestPinNoteAudit(entry);
      if (!latest) continue;
      counts[latest.action] = (counts[latest.action] ?? 0) + 1;
    }
  }
  return counts;
}

export function filterPlatformTenantSeoBundleHistoryByExportPreset(
  rows: readonly PlatformTenantSeoBundleHistoryAuditRow[],
  preset: TenantSeoBundleHistoryExportPreset,
): PlatformTenantSeoBundleHistoryAuditRow[] {
  return filterPlatformTenantSeoBundleHistoryAuditRows(rows, {
    source: preset.source ?? 'all',
    pinned: preset.pinned ?? 'all',
    bundle: preset.bundle ?? 'all',
    snapshot: preset.snapshot ?? 'all',
    pinNoteAuditAction: preset.pinNoteAuditAction ?? 'all',
  });
}

export function filterTenantSeoBundleHistoryByExportPreset(
  history: readonly TenantSeoBundleHistoryEntry[],
  preset: TenantSeoBundleHistoryExportPreset,
): TenantSeoBundleHistoryEntry[] {
  return filterTenantSeoBundleHistoryEntries(history, {
    source: preset.source ?? 'all',
    pinned: preset.pinned ?? 'all',
    bundle: preset.bundle ?? 'all',
    snapshot: preset.snapshot ?? 'all',
    pinNoteAuditAction: preset.pinNoteAuditAction ?? 'all',
  });
}

export function countTenantSeoBundleHistoryExportPresetEntries(
  history: readonly TenantSeoBundleHistoryEntry[],
  preset: TenantSeoBundleHistoryExportPreset,
): number {
  return filterTenantSeoBundleHistoryByExportPreset(history, preset).length;
}

export function countPlatformTenantSeoBundleHistoryExportPresetEntries(
  rows: readonly PlatformTenantSeoBundleHistoryAuditRow[],
  preset: TenantSeoBundleHistoryExportPreset,
): number {
  return filterPlatformTenantSeoBundleHistoryByExportPreset(rows, preset).reduce(
    (sum, row) => sum + row.config.seoBundleHistory.length,
    0,
  );
}

export function filterTenantSeoBundleHistoryByExportPresetInScope(
  history: readonly TenantSeoBundleHistoryEntry[],
  scopeFilter: TenantSeoBundleHistoryFilter,
  preset: TenantSeoBundleHistoryExportPreset,
): TenantSeoBundleHistoryEntry[] {
  return filterTenantSeoBundleHistoryByExportPreset(
    filterTenantSeoBundleHistoryEntries(history, scopeFilter),
    preset,
  );
}

export function countTenantSeoBundleHistoryExportPresetEntriesInScope(
  history: readonly TenantSeoBundleHistoryEntry[],
  scopeFilter: TenantSeoBundleHistoryFilter,
  preset: TenantSeoBundleHistoryExportPreset,
): number {
  return filterTenantSeoBundleHistoryByExportPresetInScope(history, scopeFilter, preset).length;
}

export function filterPlatformTenantSeoBundleHistoryByExportPresetInScope(
  rows: readonly PlatformTenantSeoBundleHistoryAuditRow[],
  scopeFilter: TenantSeoBundleHistoryFilter,
  preset: TenantSeoBundleHistoryExportPreset,
): PlatformTenantSeoBundleHistoryAuditRow[] {
  return filterPlatformTenantSeoBundleHistoryByExportPreset(
    filterPlatformTenantSeoBundleHistoryAuditRows(rows, scopeFilter),
    preset,
  );
}

export function countPlatformTenantSeoBundleHistoryExportPresetEntriesInScope(
  rows: readonly PlatformTenantSeoBundleHistoryAuditRow[],
  scopeFilter: TenantSeoBundleHistoryFilter,
  preset: TenantSeoBundleHistoryExportPreset,
): number {
  return filterPlatformTenantSeoBundleHistoryByExportPresetInScope(rows, scopeFilter, preset).reduce(
    (sum, row) => sum + row.config.seoBundleHistory.length,
    0,
  );
}

export function summarizeTenantSeoBundleHistoryExportPresetCountsInScope(
  history: readonly TenantSeoBundleHistoryEntry[],
  presets: readonly TenantSeoBundleHistoryExportPreset[],
  scopeFilter: TenantSeoBundleHistoryFilter,
): TenantSeoBundleHistoryExportPresetScopeCount[] {
  return presets.map((preset) => ({
    preset,
    inScopeCount: countTenantSeoBundleHistoryExportPresetEntriesInScope(history, scopeFilter, preset),
    totalCount: countTenantSeoBundleHistoryExportPresetEntries(history, preset),
  }));
}

export type TenantSeoBundleHistoryExportPresetScopeCount = {
  preset: TenantSeoBundleHistoryExportPreset;
  inScopeCount: number;
  totalCount: number;
  matchScore?: number;
};

export const TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_MATCH_MAX_SCORE = 5;

export function summarizePlatformTenantSeoBundleHistoryExportPresetCountsInScope(
  rows: readonly PlatformTenantSeoBundleHistoryAuditRow[],
  presets: readonly TenantSeoBundleHistoryExportPreset[],
  scopeFilter: TenantSeoBundleHistoryFilter,
): TenantSeoBundleHistoryExportPresetScopeCount[] {
  return presets.map((preset) => ({
    preset,
    inScopeCount: countPlatformTenantSeoBundleHistoryExportPresetEntriesInScope(rows, scopeFilter, preset),
    totalCount: countPlatformTenantSeoBundleHistoryExportPresetEntries(rows, preset),
  }));
}

export function filterTenantSeoBundleHistoryExportPresetCountsWithInScopeMatches(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
): TenantSeoBundleHistoryExportPresetScopeCount[] {
  return counts.filter((row) => row.inScopeCount > 0);
}

export function sortTenantSeoBundleHistoryExportPresetCountsInScope(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
  filtersActive: boolean,
  sortByMatchScore = false,
  filter?: TenantSeoBundleHistoryFilter,
): TenantSeoBundleHistoryExportPresetScopeCount[] {
  return [...counts].sort((left, right) => {
    if (filtersActive && filter) {
      const leftExact = isTenantSeoBundleHistoryExportPresetExactFilterMatch(left.preset, filter) ? 1 : 0;
      const rightExact = isTenantSeoBundleHistoryExportPresetExactFilterMatch(right.preset, filter) ? 1 : 0;
      if (rightExact !== leftExact) return rightExact - leftExact;
    }
    const leftCount = filtersActive ? left.inScopeCount : left.totalCount;
    const rightCount = filtersActive ? right.inScopeCount : right.totalCount;
    if (rightCount !== leftCount) return rightCount - leftCount;
    if (sortByMatchScore && filtersActive) {
      const leftScore = left.matchScore ?? 0;
      const rightScore = right.matchScore ?? 0;
      if (rightScore !== leftScore) return rightScore - leftScore;
    }
    return left.preset.label.localeCompare(right.preset.label);
  });
}

export function resolveTenantSeoBundleHistoryExportPresetCountsForDisplay(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
  filtersActive: boolean,
): TenantSeoBundleHistoryExportPresetScopeCount[] {
  const sorted = sortTenantSeoBundleHistoryExportPresetCountsInScope(counts, filtersActive);
  return filtersActive ? filterTenantSeoBundleHistoryExportPresetCountsWithInScopeMatches(sorted) : sorted;
}

export function scoreTenantSeoBundleHistoryExportPresetFilterMatch(
  current: TenantSeoBundleHistoryFilter,
  preset: TenantSeoBundleHistoryExportPreset,
): number {
  const currentTable = normalizeTenantSeoBundleHistoryExportTableFilter(current);
  const presetTable = normalizeTenantSeoBundleHistoryExportTableFilter(
    resolveTenantSeoBundleHistoryFilterFromExportPreset(preset),
  );
  let score = 0;
  const dimensions = [
    currentTable.source,
    currentTable.pinned,
    currentTable.bundle,
    currentTable.snapshot,
    currentTable.pinNoteAuditAction,
  ];
  const presetDimensions = [
    presetTable.source,
    presetTable.pinned,
    presetTable.bundle,
    presetTable.snapshot,
    presetTable.pinNoteAuditAction,
  ];
  for (let index = 0; index < dimensions.length; index += 1) {
    const left = dimensions[index];
    const right = presetDimensions[index];
    if (left === right) score += 1;
    else if (left === 'all' || right === 'all') score += 0.5;
  }
  return score;
}

export function findNearestTenantSeoBundleHistoryExportPreset(
  presets: readonly TenantSeoBundleHistoryExportPreset[],
  filter: TenantSeoBundleHistoryFilter,
): TenantSeoBundleHistoryExportPreset | undefined {
  return rankTenantSeoBundleHistoryExportPresetMatches(presets, filter, 1)[0]?.preset;
}

export function rankTenantSeoBundleHistoryExportPresetMatches(
  presets: readonly TenantSeoBundleHistoryExportPreset[],
  filter: TenantSeoBundleHistoryFilter,
  limit = 3,
  excludeLabels: readonly string[] = [],
): Array<{ preset: TenantSeoBundleHistoryExportPreset; score: number }> {
  const excluded = new Set(excludeLabels);
  return [...presets]
    .map((preset) => ({
      preset,
      score: scoreTenantSeoBundleHistoryExportPresetFilterMatch(filter, preset),
    }))
    .filter((row) => row.score > 0 && !excluded.has(row.preset.label))
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;
      return left.preset.label.localeCompare(right.preset.label);
    })
    .slice(0, Math.max(1, limit));
}

export type TenantSeoBundleHistoryExportPresetActiveFilterMatches = {
  exactMatch?: TenantSeoBundleHistoryExportPreset;
  exactMatchScore?: number;
  secondaryMatches: Array<{ preset: TenantSeoBundleHistoryExportPreset; score: number }>;
  topMatches: Array<{ preset: TenantSeoBundleHistoryExportPreset; score: number }>;
};

export function resolveTenantSeoBundleHistoryExportPresetActiveFilterMatches(
  presets: readonly TenantSeoBundleHistoryExportPreset[],
  filter: TenantSeoBundleHistoryFilter,
  options: { topLimit?: number; secondaryLimit?: number } = {},
): TenantSeoBundleHistoryExportPresetActiveFilterMatches {
  const topLimit = options.topLimit ?? 3;
  const secondaryLimit = options.secondaryLimit ?? 2;
  const exactMatch = findTenantSeoBundleHistoryExportPresetMatchingFilter(presets, filter);
  const exactMatchScore = exactMatch
    ? scoreTenantSeoBundleHistoryExportPresetFilterMatch(filter, exactMatch)
    : undefined;
  if (exactMatch) {
    return {
      exactMatch,
      exactMatchScore,
      secondaryMatches: rankTenantSeoBundleHistoryExportPresetMatches(presets, filter, secondaryLimit, [
        exactMatch.label,
      ]),
      topMatches: [],
    };
  }
  return {
    exactMatch: undefined,
    exactMatchScore: undefined,
    secondaryMatches: [],
    topMatches: rankTenantSeoBundleHistoryExportPresetMatches(presets, filter, topLimit),
  };
}

export function enrichTenantSeoBundleHistoryExportPresetCountsWithMatchScores(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
  filter: TenantSeoBundleHistoryFilter,
): TenantSeoBundleHistoryExportPresetScopeCount[] {
  return counts.map((row) => ({
    ...row,
    matchScore: scoreTenantSeoBundleHistoryExportPresetFilterMatch(filter, row.preset),
  }));
}

export function formatTenantSeoBundleHistoryExportPresetMatchScoreLabel(score: number): string {
  const percent = Math.round((score / TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_MATCH_MAX_SCORE) * 100);
  return `${percent}% match`;
}

export function formatTenantSeoBundleHistoryExportPresetFilterDiffBadge(
  diffs: readonly string[],
): string | undefined {
  if (diffs.length === 0) return undefined;
  return `${diffs.length} diff${diffs.length === 1 ? '' : 's'}`;
}

export function summarizePlatformTenantSeoBundleHistoryExportPresetDraftCountsInScope(
  rows: readonly PlatformTenantSeoBundleHistoryAuditRow[],
  draftText: string,
  scopeFilter: TenantSeoBundleHistoryFilter,
  draftLimit: number = TENANT_SEO_BUNDLE_HISTORY_EXPORT_ORG_DRAFT_PRESET_LIMIT,
): TenantSeoBundleHistoryExportPresetScopeCount[] {
  try {
    const presets = parseTenantSeoBundleHistoryExportPresetsDraft(draftText, draftLimit);
    return summarizePlatformTenantSeoBundleHistoryExportPresetCountsInScope(rows, presets, scopeFilter);
  } catch {
    return [];
  }
}

export function summarizePlatformTenantSeoBundleHistoryExportPresetDraftPreviewCounts(
  rows: readonly PlatformTenantSeoBundleHistoryAuditRow[],
  draftText: string,
  scopeFilter: TenantSeoBundleHistoryFilter,
  filtersActive: boolean,
  draftLimit: number = TENANT_SEO_BUNDLE_HISTORY_EXPORT_ORG_DRAFT_PRESET_LIMIT,
): TenantSeoBundleHistoryExportPresetScopeCount[] {
  const counts = summarizePlatformTenantSeoBundleHistoryExportPresetDraftCountsInScope(
    rows,
    draftText,
    scopeFilter,
    draftLimit,
  );
  const enriched = filtersActive
    ? enrichTenantSeoBundleHistoryExportPresetCountsWithMatchScores(counts, scopeFilter)
    : counts;
  return sortTenantSeoBundleHistoryExportPresetCountsInScope(
    enriched,
    filtersActive,
    filtersActive,
    scopeFilter,
  );
}

export function summarizeTenantSeoBundleHistoryExportPresetDraftCountsInScope(
  history: readonly TenantSeoBundleHistoryEntry[],
  draftText: string,
  scopeFilter: TenantSeoBundleHistoryFilter,
  draftLimit: number = TENANT_SEO_BUNDLE_HISTORY_EXPORT_ORG_DRAFT_PRESET_LIMIT,
): TenantSeoBundleHistoryExportPresetScopeCount[] {
  try {
    const presets = parseTenantSeoBundleHistoryExportPresetsDraft(draftText, draftLimit);
    return summarizeTenantSeoBundleHistoryExportPresetCountsInScope(history, presets, scopeFilter);
  } catch {
    return [];
  }
}

export function summarizeTenantSeoBundleHistoryExportPresetDraftPreviewCounts(
  history: readonly TenantSeoBundleHistoryEntry[],
  draftText: string,
  scopeFilter: TenantSeoBundleHistoryFilter,
  filtersActive: boolean,
): TenantSeoBundleHistoryExportPresetScopeCount[] {
  const counts = summarizeTenantSeoBundleHistoryExportPresetDraftCountsInScope(
    history,
    draftText,
    scopeFilter,
  );
  const enriched = filtersActive
    ? enrichTenantSeoBundleHistoryExportPresetCountsWithMatchScores(counts, scopeFilter)
    : counts;
  return sortTenantSeoBundleHistoryExportPresetCountsInScope(
    enriched,
    filtersActive,
    filtersActive,
    scopeFilter,
  );
}

export function resolveTenantSeoBundleHistoryExportPresetDraftPreviewCountsForDisplay(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
  filtersActive: boolean,
  showZeroMatch: boolean,
): TenantSeoBundleHistoryExportPresetScopeCount[] {
  if (!filtersActive || showZeroMatch) return [...counts];
  return counts.filter((row) => row.inScopeCount > 0);
}

export function resolveTenantSeoBundleHistoryExportPresetSecondaryMatchForApply(
  presets: readonly TenantSeoBundleHistoryExportPreset[],
  filter: TenantSeoBundleHistoryFilter,
): { preset: TenantSeoBundleHistoryExportPreset; score: number } | undefined {
  const matches = resolveTenantSeoBundleHistoryExportPresetActiveFilterMatches(presets, filter);
  if (!matches.exactMatch) return undefined;
  return matches.secondaryMatches[0];
}

export function serializeTenantSeoBundleHistoryExportPresetDraftPreviewCountsCsv(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
  filtersActive: boolean,
  context?: TenantSeoBundleHistoryExportPresetCountsExportContext,
): string {
  const mergedContext: TenantSeoBundleHistoryExportPresetCountsExportContext = {
    ...context,
    draftPreview: true,
    filtersActive,
  };
  return serializeTenantSeoBundleHistoryExportPresetCountsCsv(counts, filtersActive, mergedContext);
}

export function serializeTenantSeoBundleHistoryExportPresetDraftPreviewCountsJson(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
  filtersActive: boolean,
  context?: TenantSeoBundleHistoryExportPresetCountsExportContext,
): string {
  const mergedContext: TenantSeoBundleHistoryExportPresetCountsExportContext = {
    ...context,
    draftPreview: true,
    filtersActive,
  };
  return serializeTenantSeoBundleHistoryExportPresetCountsJson(counts, filtersActive, mergedContext);
}

export function formatTenantSeoBundleHistoryExportPresetScopeCountLabel(
  row: TenantSeoBundleHistoryExportPresetScopeCount,
  filtersActive: boolean,
): string {
  if (filtersActive) return `${row.inScopeCount} in view / ${row.totalCount} total`;
  return `${row.totalCount} entr${row.totalCount === 1 ? 'y' : 'ies'}`;
}

export function formatTenantSeoBundleHistoryExportPresetScopedFilterOptionLabel(
  row: TenantSeoBundleHistoryExportPresetScopeCount,
  filtersActive: boolean,
  options: { isExactMatch?: boolean } = {},
): string {
  const countLabel = formatTenantSeoBundleHistoryExportPresetScopeCountLabel(row, filtersActive);
  const matchLabel =
    filtersActive && row.matchScore !== undefined
      ? ` · ${formatTenantSeoBundleHistoryExportPresetMatchScoreLabel(row.matchScore)}`
      : '';
  const prefix = options.isExactMatch ? 'Exact match · ' : '';
  return `${prefix}${row.preset.label} (${countLabel}${matchLabel})`;
}

export function resolveTenantSeoBundleHistoryExportPresetExactFilterMatchRow(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
  filter: TenantSeoBundleHistoryFilter,
): TenantSeoBundleHistoryExportPresetScopeCount | undefined {
  return counts.find((row) => isTenantSeoBundleHistoryExportPresetExactFilterMatch(row.preset, filter));
}

export function formatTenantSeoBundleHistoryExportPresetScopedFilterPlaceholder(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
  filter: TenantSeoBundleHistoryFilter,
  filtersActive: boolean,
  defaultLabel = 'Choose export preset…',
): string {
  if (!filtersActive) return defaultLabel;
  const exact = resolveTenantSeoBundleHistoryExportPresetExactFilterMatchRow(counts, filter);
  if (!exact) return defaultLabel;
  return `Exact match · ${exact.preset.label}`;
}

export function formatTenantSeoBundleHistoryExportPresetExactMatchHeaderLabel(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
  filter: TenantSeoBundleHistoryFilter,
  filtersActive: boolean,
  prefix: string,
): string | undefined {
  if (!filtersActive) return undefined;
  const exact = resolveTenantSeoBundleHistoryExportPresetExactFilterMatchRow(counts, filter);
  if (!exact) return undefined;
  const countLabel = formatTenantSeoBundleHistoryExportPresetScopeCountLabel(exact, true);
  const matchLabel =
    exact.matchScore !== undefined
      ? ` · ${formatTenantSeoBundleHistoryExportPresetMatchScoreLabel(exact.matchScore)}`
      : '';
  return `${prefix}${exact.preset.label} (${countLabel}${matchLabel})`;
}

export function formatTenantSeoBundleHistoryExportPresetDraftPreviewHeaderLabel(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
  filter: TenantSeoBundleHistoryFilter,
  filtersActive: boolean,
): string | undefined {
  return formatTenantSeoBundleHistoryExportPresetExactMatchHeaderLabel(
    counts,
    filter,
    filtersActive,
    'Draft exact match · ',
  );
}

export function formatTenantSeoBundleHistoryExportPresetSavedChipHeaderLabel(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
  filter: TenantSeoBundleHistoryFilter,
  filtersActive: boolean,
): string | undefined {
  return formatTenantSeoBundleHistoryExportPresetExactMatchHeaderLabel(
    counts,
    filter,
    filtersActive,
    'Saved exact match · ',
  );
}

export function resolveTenantSeoBundleHistoryExportPresetExactMatchForApply(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
  filter: TenantSeoBundleHistoryFilter,
  filtersActive: boolean,
): TenantSeoBundleHistoryExportPreset | undefined {
  if (!filtersActive) return undefined;
  return resolveTenantSeoBundleHistoryExportPresetExactFilterMatchRow(counts, filter)?.preset;
}

export function resolveTenantSeoBundleHistoryExportPresetDraftExactMatchForApply(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
  filter: TenantSeoBundleHistoryFilter,
  filtersActive: boolean,
): TenantSeoBundleHistoryExportPreset | undefined {
  return resolveTenantSeoBundleHistoryExportPresetExactMatchForApply(counts, filter, filtersActive);
}

export function resolveTenantSeoBundleHistoryExportPresetSavedExactMatchForApply(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
  filter: TenantSeoBundleHistoryFilter,
  filtersActive: boolean,
): TenantSeoBundleHistoryExportPreset | undefined {
  return resolveTenantSeoBundleHistoryExportPresetExactMatchForApply(counts, filter, filtersActive);
}

export function isTenantSeoBundleHistoryExportPresetToolbarExportDisabled(
  row: TenantSeoBundleHistoryExportPresetScopeCount,
  filtersActive: boolean,
): boolean {
  return filtersActive && row.inScopeCount === 0;
}

export function isTenantSeoBundleHistoryExportPresetChipQuickExportDisabled(
  row: TenantSeoBundleHistoryExportPresetScopeCount,
  filtersActive: boolean,
): boolean {
  return filtersActive ? row.inScopeCount === 0 : row.totalCount === 0;
}

export function formatTenantSeoBundleHistoryExportPresetChipDomId(label: string): string {
  const slug = label
    .trim()
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `tenant-seo-export-preset-chip-${slug || 'preset'}`;
}

export function scrollToTenantSeoBundleHistoryExportPresetChip(label: string): void {
  if (typeof document === 'undefined') return;
  document.getElementById(formatTenantSeoBundleHistoryExportPresetChipDomId(label))?.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
  });
}

export function formatTenantSeoBundleHistoryExportPresetChipHighlightClass(
  label: string,
  highlightedLabel: string | null,
): string {
  return highlightedLabel === label ? 'ring-2 ring-emerald-400 ring-offset-1 rounded-full' : '';
}

export function jumpToTenantSeoBundleHistoryExportPresetChipWithHighlight(
  label: string,
  setHighlightedLabel: (value: string | null) => void,
  durationMs = 2000,
): void {
  scrollToTenantSeoBundleHistoryExportPresetChip(label);
  setHighlightedLabel(label);
  if (typeof window === 'undefined') return;
  window.setTimeout(() => setHighlightedLabel(null), durationMs);
}

export function formatTenantSeoBundleHistoryExportPresetToolbarExportOptionLabel(
  row: TenantSeoBundleHistoryExportPresetScopeCount,
  format: 'csv' | 'json',
  filtersActive: boolean,
  filter?: TenantSeoBundleHistoryFilter,
): string {
  const { preset } = row;
  const auditSuffix =
    preset.pinNoteAuditAction && preset.pinNoteAuditAction !== 'all'
      ? ` · ${preset.pinNoteAuditAction.replaceAll('_', ' ')}`
      : '';
  const formatSuffix = format.toUpperCase();
  if (filtersActive && filter) {
    const isExactMatch = isTenantSeoBundleHistoryExportPresetExactFilterMatch(preset, filter);
    const scoped = formatTenantSeoBundleHistoryExportPresetScopedFilterOptionLabel(row, true, {
      isExactMatch,
    });
    return `${scoped}${auditSuffix} (${formatSuffix})`;
  }
  const countLabel = `${row.totalCount} entr${row.totalCount === 1 ? 'y' : 'ies'}`;
  return `${preset.label}${auditSuffix} (${formatSuffix} · ${countLabel})`;
}

function escapeTenantSeoBundleHistoryExportPresetCsvCell(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replaceAll('"', '""')}"`;
  return value;
}

export function serializeTenantSeoBundleHistoryExportPresetCountsCsv(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
  filtersActive: boolean,
  context?: TenantSeoBundleHistoryExportPresetCountsExportContext,
): string {
  const includeMatchScore = filtersActive && counts.some((row) => row.matchScore !== undefined);
  const header = filtersActive
    ? includeMatchScore
      ? 'label,pinned,source,bundle,snapshot,pin_note_audit,in_scope_count,total_count,match_score'
      : 'label,pinned,source,bundle,snapshot,pin_note_audit,in_scope_count,total_count'
    : 'label,pinned,source,bundle,snapshot,pin_note_audit,total_count';
  const lines = counts.map((row) => {
    const { preset } = row;
    const cells = [
      preset.label,
      preset.pinned ?? 'all',
      preset.source ?? 'all',
      preset.bundle ?? 'all',
      preset.snapshot ?? 'all',
      preset.pinNoteAuditAction ?? 'all',
    ];
    if (filtersActive) {
      cells.push(String(row.inScopeCount), String(row.totalCount));
      if (includeMatchScore) cells.push(String(row.matchScore ?? ''));
    } else {
      cells.push(String(row.totalCount));
    }
    return cells.map((cell) => escapeTenantSeoBundleHistoryExportPresetCsvCell(cell)).join(',');
  });
  const body = [header, ...lines].join('\n');
  if (!context) return body;
  const comments = serializeTenantSeoBundleHistoryExportPresetCountsExportContextComments(context);
  if (comments.length === 0) return body;
  return `${comments.join('\n')}\n${body}`;
}

export function serializeTenantSeoBundleHistoryExportPresetCountsJson(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
  filtersActive: boolean,
  context?: TenantSeoBundleHistoryExportPresetCountsExportContext,
): string {
  const presets = counts.map((row) => ({
    label: row.preset.label,
    pinned: row.preset.pinned ?? 'all',
    source: row.preset.source ?? 'all',
    bundle: row.preset.bundle ?? 'all',
    snapshot: row.preset.snapshot ?? 'all',
    pin_note_audit: row.preset.pinNoteAuditAction ?? 'all',
    ...(filtersActive
      ? {
          in_scope_count: row.inScopeCount,
          total_count: row.totalCount,
          ...(row.matchScore !== undefined ? { match_score: row.matchScore } : {}),
        }
      : { total_count: row.totalCount }),
  }));
  if (!context) {
    return JSON.stringify(presets, null, 2);
  }
  return JSON.stringify(
    {
      export_context: {
        ...(context.draftPreview ? { draft_preview: true } : {}),
        ...(context.filtersActive !== undefined ? { filters_active: context.filtersActive } : {}),
        ...(context.groupBy ? { group_by: context.groupBy } : {}),
        ...(context.groupKey ? { group_key: context.groupKey } : {}),
        ...(context.groupLabel ? { group_label: context.groupLabel } : {}),
        ...(context.labelQuery ? { label_query: context.labelQuery } : {}),
        ...(context.visibleCount !== undefined ? { visible_count: context.visibleCount } : {}),
        ...(context.totalCount !== undefined ? { total_count: context.totalCount } : {}),
      },
      presets,
    },
    null,
    2,
  );
}

export function describeTenantSeoBundleHistoryExportPresetFilterDiff(
  current: TenantSeoBundleHistoryFilter,
  preset: TenantSeoBundleHistoryExportPreset,
): string[] {
  const currentTable = normalizeTenantSeoBundleHistoryExportTableFilter(current);
  const presetTable = normalizeTenantSeoBundleHistoryExportTableFilter(
    resolveTenantSeoBundleHistoryFilterFromExportPreset(preset),
  );
  const diffs: string[] = [];
  if (currentTable.source !== presetTable.source) {
    diffs.push(
      `source: ${currentTable.source.replaceAll('_', ' ')} → ${presetTable.source.replaceAll('_', ' ')}`,
    );
  }
  if (currentTable.pinned !== presetTable.pinned) {
    diffs.push(`pinned: ${currentTable.pinned} → ${presetTable.pinned}`);
  }
  if (currentTable.bundle !== presetTable.bundle) {
    diffs.push(
      `bundle: ${currentTable.bundle.replaceAll('_', ' ')} → ${presetTable.bundle.replaceAll('_', ' ')}`,
    );
  }
  if (currentTable.snapshot !== presetTable.snapshot) {
    diffs.push(
      `snapshot: ${currentTable.snapshot.replaceAll('_', ' ')} → ${presetTable.snapshot.replaceAll('_', ' ')}`,
    );
  }
  if (currentTable.pinNoteAuditAction !== presetTable.pinNoteAuditAction) {
    diffs.push(
      `audit: ${currentTable.pinNoteAuditAction.replaceAll('_', ' ')} → ${presetTable.pinNoteAuditAction.replaceAll('_', ' ')}`,
    );
  }
  return diffs;
}

export function resolveTenantSeoBundleHistoryExportPresetCountsForChipList(
  counts: readonly TenantSeoBundleHistoryExportPresetScopeCount[],
  filtersActive: boolean,
  showZeroMatch: boolean,
  filter?: TenantSeoBundleHistoryFilter,
): TenantSeoBundleHistoryExportPresetScopeCount[] {
  const sortByMatchScore = filtersActive && counts.some((row) => row.matchScore !== undefined);
  const sorted = sortTenantSeoBundleHistoryExportPresetCountsInScope(
    counts,
    filtersActive,
    sortByMatchScore,
    filter,
  );
  if (!filtersActive || showZeroMatch) return sorted;
  return filterTenantSeoBundleHistoryExportPresetCountsWithInScopeMatches(sorted);
}

export function summarizeTenantSeoBundleHistoryExportPresetCounts(
  rows: readonly PlatformTenantSeoBundleHistoryAuditRow[],
  presets: readonly TenantSeoBundleHistoryExportPreset[],
): Array<{ preset: TenantSeoBundleHistoryExportPreset; count: number }> {
  return presets.map((preset) => ({
    preset,
    count: countPlatformTenantSeoBundleHistoryExportPresetEntries(rows, preset),
  }));
}

export function formatTenantSeoBundleHistoryExportPresetsDraft(
  presets: readonly TenantSeoBundleHistoryExportPreset[],
): string {
  return presets
    .map(
      (preset) =>
        `${preset.label} | ${preset.pinned ?? 'all'} | ${preset.source ?? 'all'} | ${preset.bundle ?? 'all'} | ${preset.snapshot ?? 'all'} | ${preset.pinNoteAuditAction ?? 'all'}`,
    )
    .join('\n');
}

export function parseTenantSeoBundleHistoryExportPresetsDraft(
  text: string,
  limit: number = TENANT_SEO_BUNDLE_HISTORY_EXPORT_ORG_DRAFT_PRESET_LIMIT,
): TenantSeoBundleHistoryExportPreset[] {
  const effectiveLimit = Math.max(1, Math.round(limit));
  const presets: TenantSeoBundleHistoryExportPreset[] = [];
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const parts = trimmed.split('|').map((part) => part.trim());
    const label = parts[0] ?? '';
    if (!label) continue;
    const pinnedRaw = (parts[1] ?? 'all') as 'all' | 'pinned' | 'unpinned';
    const pinned = pinnedRaw === 'pinned' || pinnedRaw === 'unpinned' ? pinnedRaw : ('all' as const);
    const sourceRaw = (parts[2] ?? 'all') as TenantSeoBundleHistorySourceFilter;
    const source =
      sourceRaw === 'all' || TENANT_SEO_BUNDLE_HISTORY_SOURCES.includes(sourceRaw as never)
        ? sourceRaw
        : ('all' as const);
    const bundleRaw = (parts[3] ?? 'all') as TenantSeoBundleHistoryBundleFilter;
    const bundle = bundleRaw === 'with_bundle' || bundleRaw === 'orphan' ? bundleRaw : ('all' as const);
    const snapshotRaw = (parts[4] ?? 'all') as TenantSeoBundleHistorySnapshotFilter;
    const snapshot =
      snapshotRaw === 'with_snapshot' || snapshotRaw === 'without_snapshot' ? snapshotRaw : ('all' as const);
    const pinNoteAuditActionRaw = (
      parts[5] ?? 'all'
    ).trim() as TenantSeoBundleHistoryPinNoteAuditActionFilter;
    const pinNoteAuditAction =
      pinNoteAuditActionRaw === 'all' ||
      pinNoteAuditActionRaw === 'manual' ||
      pinNoteAuditActionRaw === 'bulk_update' ||
      pinNoteAuditActionRaw === 'bulk_by_source' ||
      pinNoteAuditActionRaw === 'bulk_pin' ||
      pinNoteAuditActionRaw === 'bulk_pin_by_source'
        ? pinNoteAuditActionRaw
        : ('all' as const);
    presets.push({ label, pinned, source, bundle, snapshot, pinNoteAuditAction });
    if (presets.length >= effectiveLimit) break;
  }
  return normalizeTenantSeoBundleHistoryExportPresets(presets, effectiveLimit);
}

export type TenantMarketingSeoBundleManifest = {
  sitemap_url_count: number;
  robots_ai_crawler_policy: RobotsAiCrawlerPolicy;
  blocked_ai_crawler_count: number;
  has_llms_txt: boolean;
  llms_disabled_section_count: number;
};

export type TenantMarketingConfigExport = {
  robots_disallow_paths?: string[];
  robots_allow_paths?: string[];
  robots_ai_crawler_policy?: RobotsAiCrawlerPolicy;
  robots_ai_crawler_user_agents?: string[];
  llms_disabled_sections?: string[];
};

export type TenantMarketingSeoBundle = {
  exported_at: string;
  branding: {
    company_name?: string;
    website_url?: string;
    phone?: string;
    email?: string;
  };
  manifest: TenantMarketingSeoBundleManifest;
  files: {
    'robots.txt': string;
    'sitemap.xml': string;
    'llms.txt'?: string;
  };
  seo_overrides: MarketingSeoExportBundle;
  marketing_config: TenantMarketingConfigExport;
};

export const TENANT_SEO_BUNDLE_JSON_FILENAME = 'tenant-seo-bundle.json';

const TENANT_SEO_BUNDLE_ZIP_JSON_CANDIDATES = [TENANT_SEO_BUNDLE_JSON_FILENAME, 'bundle.json'] as const;

export type TenantSeoBundleFileDownload = {
  filename: string;
  content: string;
  mimeType: string;
};

export function exportTenantMarketingConfig(config: PublicMarketingConfig): TenantMarketingConfigExport {
  const marketing: TenantMarketingConfigExport = {};
  if (config.robotsDisallowPaths.length) {
    marketing.robots_disallow_paths = config.robotsDisallowPaths;
  }
  if (config.robotsAllowPaths.length) {
    marketing.robots_allow_paths = config.robotsAllowPaths;
  }
  if (config.robotsAiCrawlerPolicy !== 'allow') {
    marketing.robots_ai_crawler_policy = config.robotsAiCrawlerPolicy;
  }
  if (config.robotsAiCrawlerUserAgents.length) {
    marketing.robots_ai_crawler_user_agents = config.robotsAiCrawlerUserAgents;
  }
  if (config.llmsDisabledSections.length) {
    marketing.llms_disabled_sections = config.llmsDisabledSections;
  }
  return marketing;
}

export function buildTenantMarketingSeoBundle(input: {
  config: PublicMarketingConfig;
  branding: MarketingSeoPreviewBranding;
  llmsTemplate?: string;
  exportedAt?: string;
}): TenantMarketingSeoBundle {
  const { config, branding, llmsTemplate } = input;
  const sitemap = buildTenantMarketingSitemapPreview(config, branding);
  const robotsTxt = buildTenantRobotsTxtPreview(config, branding.websiteUrl);
  const llmsTxt = llmsTemplate
    ? buildTenantLlmsTxtPreview(
        llmsTemplate,
        {
          phone: branding.phone,
          email: branding.email,
          companyName: branding.companyName,
          websiteUrl: branding.websiteUrl,
        },
        { disabledSections: config.llmsDisabledSections },
      )
    : undefined;
  const blockedAiCrawlers = resolveBlockedAiCrawlerUserAgents(config);

  return {
    exported_at: input.exportedAt ?? new Date().toISOString(),
    branding: {
      company_name: branding.companyName,
      website_url: branding.websiteUrl,
      phone: branding.phone,
      email: branding.email,
    },
    manifest: {
      sitemap_url_count: sitemap.urlCount,
      robots_ai_crawler_policy: resolveRobotsAiCrawlerPolicy(config),
      blocked_ai_crawler_count: blockedAiCrawlers.length,
      has_llms_txt: Boolean(llmsTxt?.trim()),
      llms_disabled_section_count: config.llmsDisabledSections.length,
    },
    files: {
      'robots.txt': robotsTxt,
      'sitemap.xml': sitemap.xml,
      ...(llmsTxt?.trim() ? { 'llms.txt': llmsTxt } : {}),
    },
    seo_overrides: exportMarketingSeoOverrides(config),
    marketing_config: exportTenantMarketingConfig(config),
  };
}

export function serializeTenantMarketingSeoBundle(bundle: TenantMarketingSeoBundle): string {
  return JSON.stringify(bundle, null, 2);
}

export function isTenantMarketingSeoBundle(value: unknown): value is TenantMarketingSeoBundle {
  if (!value || typeof value !== 'object') return false;
  const o = value as Record<string, unknown>;
  return (
    typeof o.exported_at === 'string' &&
    o.files !== null &&
    typeof o.files === 'object' &&
    typeof (o.files as Record<string, unknown>)['robots.txt'] === 'string'
  );
}

export function mergeImportedTenantMarketingConfig(
  current: PublicMarketingConfig,
  imported: TenantMarketingConfigExport,
): PublicMarketingConfig {
  const next = { ...current };
  if (Array.isArray(imported.robots_disallow_paths)) {
    next.robotsDisallowPaths = imported.robots_disallow_paths
      .map((path) => String(path).trim())
      .filter(Boolean);
  }
  if (Array.isArray(imported.robots_allow_paths)) {
    next.robotsAllowPaths = imported.robots_allow_paths.map((path) => String(path).trim()).filter(Boolean);
  }
  if (imported.robots_ai_crawler_policy === 'allow' || imported.robots_ai_crawler_policy === 'disallow') {
    next.robotsAiCrawlerPolicy = imported.robots_ai_crawler_policy;
  }
  if (Array.isArray(imported.robots_ai_crawler_user_agents)) {
    next.robotsAiCrawlerUserAgents = imported.robots_ai_crawler_user_agents
      .map((agent) => String(agent).trim())
      .filter(Boolean);
  }
  if (Array.isArray(imported.llms_disabled_sections)) {
    next.llmsDisabledSections = imported.llms_disabled_sections
      .map((section) => String(section).trim())
      .filter(Boolean);
  }
  return next;
}

/** Restore SEO overrides and marketing config from an exported tenant bundle. */
export function mergeImportedTenantMarketingSeoBundle(
  current: PublicMarketingConfig,
  imported: unknown,
): PublicMarketingConfig {
  if (!isTenantMarketingSeoBundle(imported)) {
    throw new Error('Import must be a tenant SEO bundle JSON export');
  }
  let next = mergeImportedMarketingSeoBundle(current, imported.seo_overrides ?? {});
  if (imported.marketing_config && typeof imported.marketing_config === 'object') {
    next = mergeImportedTenantMarketingConfig(next, imported.marketing_config);
  }
  return next;
}

export function listTenantSeoBundleFileDownloads(
  bundle: TenantMarketingSeoBundle,
): TenantSeoBundleFileDownload[] {
  const downloads: TenantSeoBundleFileDownload[] = [
    {
      filename: 'robots.txt',
      content: bundle.files['robots.txt'],
      mimeType: 'text/plain',
    },
    {
      filename: 'sitemap.xml',
      content: bundle.files['sitemap.xml'],
      mimeType: 'application/xml',
    },
  ];
  if (bundle.files['llms.txt']) {
    downloads.push({
      filename: 'llms.txt',
      content: bundle.files['llms.txt'],
      mimeType: 'text/plain',
    });
  }
  return downloads;
}

export type TenantSeoBundleValidationIssue = {
  level: 'error' | 'warning';
  field: string;
  message: string;
};

export type TenantSeoBundleValidationResult = {
  valid: boolean;
  bundle: TenantMarketingSeoBundle | null;
  issues: TenantSeoBundleValidationIssue[];
};

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/** Validate tenant SEO bundle JSON before restore (admin import preview). */
export function validateTenantMarketingSeoBundle(value: unknown): TenantSeoBundleValidationResult {
  const issues: TenantSeoBundleValidationIssue[] = [];

  if (!value || typeof value !== 'object') {
    return {
      valid: false,
      bundle: null,
      issues: [{ level: 'error', field: 'root', message: 'Import must be a JSON object' }],
    };
  }

  const o = value as Record<string, unknown>;

  if (!isNonEmptyString(o.exported_at)) {
    issues.push({
      level: 'error',
      field: 'exported_at',
      message: 'Missing exported_at timestamp',
    });
  } else if (Number.isNaN(Date.parse(o.exported_at))) {
    issues.push({
      level: 'warning',
      field: 'exported_at',
      message: 'exported_at is not a valid ISO timestamp',
    });
  }

  if (!o.files || typeof o.files !== 'object') {
    issues.push({
      level: 'error',
      field: 'files',
      message: 'Missing files object (robots.txt and sitemap.xml required)',
    });
  } else {
    const files = o.files as Record<string, unknown>;
    if (!isNonEmptyString(files['robots.txt'])) {
      issues.push({
        level: 'error',
        field: 'files.robots.txt',
        message: 'robots.txt content is required',
      });
    }
    if (!isNonEmptyString(files['sitemap.xml'])) {
      issues.push({
        level: 'error',
        field: 'files.sitemap.xml',
        message: 'sitemap.xml content is required',
      });
    }
    if (files['llms.txt'] !== undefined && typeof files['llms.txt'] !== 'string') {
      issues.push({
        level: 'error',
        field: 'files.llms.txt',
        message: 'llms.txt must be a string when present',
      });
    }
  }

  if (o.seo_overrides !== undefined && (typeof o.seo_overrides !== 'object' || o.seo_overrides === null)) {
    issues.push({
      level: 'error',
      field: 'seo_overrides',
      message: 'seo_overrides must be an object when present',
    });
  } else if (!o.seo_overrides) {
    issues.push({
      level: 'warning',
      field: 'seo_overrides',
      message: 'No seo_overrides — homepage/hub/landing SEO will not change',
    });
  }

  if (
    o.marketing_config !== undefined &&
    (typeof o.marketing_config !== 'object' || o.marketing_config === null)
  ) {
    issues.push({
      level: 'error',
      field: 'marketing_config',
      message: 'marketing_config must be an object when present',
    });
  } else if (!o.marketing_config) {
    issues.push({
      level: 'warning',
      field: 'marketing_config',
      message: 'No marketing_config — robots/llms settings will not change',
    });
  }

  if (o.manifest !== undefined && (typeof o.manifest !== 'object' || o.manifest === null)) {
    issues.push({
      level: 'warning',
      field: 'manifest',
      message: 'manifest must be an object when present',
    });
  }

  const valid = !issues.some((issue) => issue.level === 'error');
  return {
    valid,
    bundle: valid && isTenantMarketingSeoBundle(value) ? value : null,
    issues,
  };
}

export type TenantSeoBundleImportPreview = {
  exportedAt: string;
  brandingCompanyName: string | null;
  homepageSeoTitle: string | null;
  homepageSeoDescription: string | null;
  hubOverrideCount: number;
  landingOverrideCount: number;
  hasDefaultOgImage: boolean;
  robotsDisallowCount: number;
  robotsAllowCount: number;
  robotsAiCrawlerPolicy: RobotsAiCrawlerPolicy | null;
  blockedAiCrawlerCount: number;
  llmsDisabledSections: string[];
  sitemapUrlCount: number | null;
  fileNames: string[];
};

function countPathSeo(map: unknown): number {
  if (!map || typeof map !== 'object') return 0;
  return Object.keys(map as Record<string, unknown>).length;
}

/** Summarize what a tenant SEO bundle import would apply. */
export function previewTenantSeoBundleImport(bundle: TenantMarketingSeoBundle): TenantSeoBundleImportPreview {
  const seo = bundle.seo_overrides ?? {};
  const marketing = bundle.marketing_config ?? {};
  return {
    exportedAt: bundle.exported_at,
    brandingCompanyName: bundle.branding?.company_name?.trim() || null,
    homepageSeoTitle: seo.homepage_seo_title?.trim() || null,
    homepageSeoDescription: seo.homepage_seo_description?.trim() || null,
    hubOverrideCount: countPathSeo(seo.hub_page_seo),
    landingOverrideCount: countPathSeo(seo.landing_page_seo),
    hasDefaultOgImage: Boolean(seo.default_og_image_url?.trim()),
    robotsDisallowCount: marketing.robots_disallow_paths?.length ?? 0,
    robotsAllowCount: marketing.robots_allow_paths?.length ?? 0,
    robotsAiCrawlerPolicy: marketing.robots_ai_crawler_policy ?? null,
    blockedAiCrawlerCount: marketing.robots_ai_crawler_user_agents?.length ?? 0,
    llmsDisabledSections: marketing.llms_disabled_sections ?? [],
    sitemapUrlCount: bundle.manifest?.sitemap_url_count ?? null,
    fileNames: Object.keys(bundle.files),
  };
}

export type TenantSeoBundleZipEntry = {
  filename: string;
  content: Uint8Array;
};

function encodeZipText(content: string): Uint8Array {
  return new TextEncoder().encode(content);
}

/** Build ZIP archive entries for a tenant SEO bundle. */
export function buildTenantSeoBundleZipEntries(
  bundle: TenantMarketingSeoBundle,
  bundleJson?: string,
): TenantSeoBundleZipEntry[] {
  const json = bundleJson ?? serializeTenantMarketingSeoBundle(bundle);
  const entries: TenantSeoBundleZipEntry[] = [
    { filename: 'robots.txt', content: encodeZipText(bundle.files['robots.txt']) },
    { filename: 'sitemap.xml', content: encodeZipText(bundle.files['sitemap.xml']) },
    { filename: TENANT_SEO_BUNDLE_JSON_FILENAME, content: encodeZipText(json) },
  ];
  if (bundle.files['llms.txt']) {
    entries.push({
      filename: 'llms.txt',
      content: encodeZipText(bundle.files['llms.txt']),
    });
  }
  return entries;
}

/** Build a ZIP blob for browser download (robots, sitemap, llms, bundle JSON). */
export function buildTenantSeoBundleZipBlob(bundle: TenantMarketingSeoBundle, bundleJson?: string): Blob {
  const zipInput: Record<string, Uint8Array> = {};
  for (const entry of buildTenantSeoBundleZipEntries(bundle, bundleJson)) {
    zipInput[entry.filename] = entry.content;
  }
  const zipped = zipSync(zipInput);
  return new Blob([zipped], { type: 'application/zip' });
}

function decodeZipText(content: Uint8Array): string {
  return new TextDecoder().decode(content);
}

function basenameZipEntry(name: string): string {
  const parts = name.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? name;
}

function enrichBundleFilesFromZip(bundle: Record<string, unknown>, unzipped: Record<string, Uint8Array>) {
  const files =
    bundle.files && typeof bundle.files === 'object' ? { ...(bundle.files as Record<string, string>) } : {};
  for (const [name, content] of Object.entries(unzipped)) {
    const base = basenameZipEntry(name);
    if (base === 'robots.txt' && !files['robots.txt']) {
      files['robots.txt'] = decodeZipText(content);
    }
    if (base === 'sitemap.xml' && !files['sitemap.xml']) {
      files['sitemap.xml'] = decodeZipText(content);
    }
    if (base === 'llms.txt' && !files['llms.txt']) {
      files['llms.txt'] = decodeZipText(content);
    }
  }
  if (Object.keys(files).length > 0) {
    bundle.files = files;
  }
}

export type TenantSeoBundleZipParseResult = {
  bundle: unknown;
  zipFiles: string[];
  sourceFile: string | null;
};

/** Extract tenant SEO bundle JSON from a ZIP export archive. */
export function parseTenantSeoBundleZipBytes(bytes: Uint8Array): TenantSeoBundleZipParseResult {
  const unzipped = unzipSync(bytes);
  const zipFiles = Object.keys(unzipped);
  let sourceFile: string | null = null;
  let bundle: unknown = null;

  for (const name of zipFiles) {
    const base = basenameZipEntry(name);
    if ((TENANT_SEO_BUNDLE_ZIP_JSON_CANDIDATES as readonly string[]).includes(base)) {
      bundle = JSON.parse(decodeZipText(unzipped[name]!));
      sourceFile = name;
      break;
    }
  }

  if (!bundle) {
    for (const name of zipFiles) {
      if (!name.endsWith('.json')) continue;
      try {
        const parsed = JSON.parse(decodeZipText(unzipped[name]!));
        if (isTenantMarketingSeoBundle(parsed)) {
          bundle = parsed;
          sourceFile = name;
          break;
        }
      } catch {
        /* try next json entry */
      }
    }
  }

  if (bundle && typeof bundle === 'object') {
    enrichBundleFilesFromZip(bundle as Record<string, unknown>, unzipped);
  }

  return { bundle, zipFiles, sourceFile };
}

/** Read tenant SEO bundle JSON from a browser File (ZIP export). */
export async function parseTenantSeoBundleZipFile(file: File): Promise<TenantSeoBundleZipParseResult> {
  const buffer = await file.arrayBuffer();
  return parseTenantSeoBundleZipBytes(new Uint8Array(buffer));
}

export type TenantSeoBundleImportChange = {
  category: 'homepage' | 'hub' | 'landing' | 'og' | 'robots' | 'llms';
  label: string;
  before?: string;
  after?: string;
};

function pushStringChange(
  changes: TenantSeoBundleImportChange[],
  category: TenantSeoBundleImportChange['category'],
  label: string,
  before: string,
  after: string,
) {
  if (before === after) return;
  changes.push({
    category,
    label,
    before: before || '(empty)',
    after: after || '(empty)',
  });
}

function formatPathList(paths: string[]): string {
  return paths.length ? paths.join(', ') : '(none)';
}

/** Diff current marketing config against a tenant SEO bundle import. */
export function diffTenantSeoBundleImport(
  current: PublicMarketingConfig,
  bundle: TenantMarketingSeoBundle,
): TenantSeoBundleImportChange[] {
  const merged = mergeImportedTenantMarketingSeoBundle(current, bundle);
  const changes: TenantSeoBundleImportChange[] = [];

  pushStringChange(
    changes,
    'homepage',
    'Homepage SEO title',
    current.homepageSeoTitle.trim(),
    merged.homepageSeoTitle.trim(),
  );
  pushStringChange(
    changes,
    'homepage',
    'Homepage SEO description',
    current.homepageSeoDescription.trim(),
    merged.homepageSeoDescription.trim(),
  );
  pushStringChange(
    changes,
    'og',
    'Default og:image',
    current.defaultOgImageUrl.trim(),
    merged.defaultOgImageUrl.trim(),
  );

  const hubPaths = new Set([
    ...Object.keys(current.hubPageSeo),
    ...Object.keys(merged.hubPageSeo),
    ...Object.keys(bundle.seo_overrides?.hub_page_seo ?? {}),
  ]);
  for (const path of [...hubPaths].sort()) {
    const before = current.hubPageSeo[path];
    const after = merged.hubPageSeo[path];
    const beforeLabel = before
      ? [before.title?.trim(), before.description?.trim()].filter(Boolean).join(' · ') || '(empty)'
      : '(none)';
    const afterLabel = after
      ? [after.title?.trim(), after.description?.trim()].filter(Boolean).join(' · ') || '(empty)'
      : '(none)';
    if (beforeLabel !== afterLabel) {
      changes.push({
        category: 'hub',
        label: `Hub ${path}`,
        before: beforeLabel,
        after: afterLabel,
      });
    }
  }

  const landingPaths = new Set([
    ...Object.keys(current.landingPageSeo),
    ...Object.keys(merged.landingPageSeo),
    ...Object.keys(bundle.seo_overrides?.landing_page_seo ?? {}),
  ]);
  for (const path of [...landingPaths].sort()) {
    const before = current.landingPageSeo[path];
    const after = merged.landingPageSeo[path];
    const beforeLabel = before
      ? [before.title?.trim(), before.description?.trim()].filter(Boolean).join(' · ') || '(empty)'
      : '(none)';
    const afterLabel = after
      ? [after.title?.trim(), after.description?.trim()].filter(Boolean).join(' · ') || '(empty)'
      : '(none)';
    if (beforeLabel !== afterLabel) {
      changes.push({
        category: 'landing',
        label: `Landing ${path}`,
        before: beforeLabel,
        after: afterLabel,
      });
    }
  }

  const disallowBefore = [...current.robotsDisallowPaths].sort().join('|');
  const disallowAfter = [...merged.robotsDisallowPaths].sort().join('|');
  if (disallowBefore !== disallowAfter) {
    changes.push({
      category: 'robots',
      label: 'Robots disallow paths',
      before: formatPathList(current.robotsDisallowPaths),
      after: formatPathList(merged.robotsDisallowPaths),
    });
  }

  const allowBefore = [...current.robotsAllowPaths].sort().join('|');
  const allowAfter = [...merged.robotsAllowPaths].sort().join('|');
  if (allowBefore !== allowAfter) {
    changes.push({
      category: 'robots',
      label: 'Robots allow paths',
      before: formatPathList(current.robotsAllowPaths),
      after: formatPathList(merged.robotsAllowPaths),
    });
  }

  if (current.robotsAiCrawlerPolicy !== merged.robotsAiCrawlerPolicy) {
    changes.push({
      category: 'robots',
      label: 'AI crawler policy',
      before: current.robotsAiCrawlerPolicy,
      after: merged.robotsAiCrawlerPolicy,
    });
  }

  const agentsBefore = [...current.robotsAiCrawlerUserAgents].sort().join('|');
  const agentsAfter = [...merged.robotsAiCrawlerUserAgents].sort().join('|');
  if (agentsBefore !== agentsAfter) {
    changes.push({
      category: 'robots',
      label: 'Blocked AI crawlers',
      before: formatPathList(current.robotsAiCrawlerUserAgents),
      after: formatPathList(merged.robotsAiCrawlerUserAgents),
    });
  }

  const llmsBefore = [...current.llmsDisabledSections].sort().join('|');
  const llmsAfter = [...merged.llmsDisabledSections].sort().join('|');
  if (llmsBefore !== llmsAfter) {
    changes.push({
      category: 'llms',
      label: 'Disabled llms.txt sections',
      before: formatPathList(current.llmsDisabledSections),
      after: formatPathList(merged.llmsDisabledSections),
    });
  }

  return changes;
}

/** Load platform default tenant SEO bundle template from app_config. */
export async function fetchPlatformTenantSeoBundleTemplate(): Promise<TenantMarketingSeoBundle | null> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_PLATFORM_SEO_BUNDLE_TEMPLATE_KEY)
      .maybeSingle();
    if (error || !data?.value) return null;
    const validation = validateTenantMarketingSeoBundle(data.value);
    return validation.valid ? validation.bundle : null;
  } catch {
    return null;
  }
}

/** Save platform default tenant SEO bundle template for org onboarding. */
export async function savePlatformTenantSeoBundleTemplate(bundle: TenantMarketingSeoBundle): Promise<void> {
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_PLATFORM_SEO_BUNDLE_TEMPLATE_KEY,
      value: bundle,
      category: 'features',
      data_type: 'json',
      is_public: true,
      description: 'Default tenant SEO bundle template (robots, sitemap, llms, SEO overrides).',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export type TenantSeoBundleFileDiff = {
  filename: 'robots.txt' | 'sitemap.xml' | 'llms.txt';
  changed: boolean;
  beforeBytes: number;
  afterBytes: number;
  beforeLines: number;
  afterLines: number;
  diffPreview: string;
};

function countTextLines(text: string): number {
  if (!text) return 0;
  return text.split('\n').length;
}

function countTextBytes(text: string): number {
  return new TextEncoder().encode(text).byteLength;
}

/** Compact unified diff preview for tenant SEO file changes. */
export function summarizeTenantSeoFileDiff(before: string, after: string, maxLines = 16): string {
  if (before === after) return '';
  const beforeLines = before.split('\n');
  const afterLines = after.split('\n');
  const preview: string[] = [];
  const maxIndex = Math.max(beforeLines.length, afterLines.length);

  for (let index = 0; index < maxIndex && preview.length < maxLines; index += 1) {
    const left = beforeLines[index] ?? '';
    const right = afterLines[index] ?? '';
    if (left === right) continue;
    if (left) preview.push(`- ${left}`);
    if (right) preview.push(`+ ${right}`);
  }

  if (maxIndex > maxLines) preview.push('…');
  return preview.join('\n');
}

export function getTenantSeoConfigFileContentPair(
  beforeConfig: PublicMarketingConfig,
  afterConfig: PublicMarketingConfig,
  branding: MarketingSeoPreviewBranding,
  filename: TenantSeoBundleFileDiff['filename'],
  llmsTemplate?: string,
): { before: string; after: string } {
  const beforeBundle = buildTenantMarketingSeoBundle({
    config: beforeConfig,
    branding,
    llmsTemplate,
  });
  const afterBundle = buildTenantMarketingSeoBundle({
    config: afterConfig,
    branding,
    llmsTemplate,
  });
  return {
    before: beforeBundle.files[filename] ?? '',
    after: afterBundle.files[filename] ?? '',
  };
}

export function diffTenantSeoBundleConfigFiles(
  beforeConfig: PublicMarketingConfig,
  afterConfig: PublicMarketingConfig,
  branding: MarketingSeoPreviewBranding,
  llmsTemplate?: string,
): TenantSeoBundleFileDiff[] {
  const filenames: Array<'robots.txt' | 'sitemap.xml' | 'llms.txt'> = [
    'robots.txt',
    'sitemap.xml',
    'llms.txt',
  ];
  const pair = (filename: TenantSeoBundleFileDiff['filename']) =>
    getTenantSeoConfigFileContentPair(beforeConfig, afterConfig, branding, filename, llmsTemplate);
  return filenames.map((filename) => {
    const { before, after } = pair(filename);
    return {
      filename,
      changed: before !== after,
      beforeBytes: countTextBytes(before),
      afterBytes: countTextBytes(after),
      beforeLines: countTextLines(before),
      afterLines: countTextLines(after),
      diffPreview: summarizeTenantSeoFileDiff(before, after),
    };
  });
}

export function getTenantSeoBundleFileContentPair(
  current: PublicMarketingConfig,
  bundle: TenantMarketingSeoBundle,
  branding: MarketingSeoPreviewBranding,
  filename: TenantSeoBundleFileDiff['filename'],
  llmsTemplate?: string,
): { before: string; after: string } {
  const merged = mergeImportedTenantMarketingSeoBundle(current, bundle);
  const beforeBundle = buildTenantMarketingSeoBundle({
    config: current,
    branding,
    llmsTemplate,
  });
  const afterBundle = buildTenantMarketingSeoBundle({
    config: merged,
    branding,
    llmsTemplate,
  });
  return {
    before: beforeBundle.files[filename] ?? '',
    after: afterBundle.files[filename] ?? '',
  };
}

/** Capture SEO field state before a bundle apply (for rollback). */
export function exportTenantSeoBundleRollbackSnapshot(
  config: PublicMarketingConfig,
): TenantSeoBundleRollbackSnapshot {
  return {
    homepageSeoTitle: config.homepageSeoTitle,
    homepageSeoDescription: config.homepageSeoDescription,
    defaultOgImageUrl: config.defaultOgImageUrl,
    hubPageSeo: { ...config.hubPageSeo },
    landingPageSeo: { ...config.landingPageSeo },
    robotsDisallowPaths: [...config.robotsDisallowPaths],
    robotsAllowPaths: [...config.robotsAllowPaths],
    robotsAiCrawlerPolicy: config.robotsAiCrawlerPolicy,
    robotsAiCrawlerUserAgents: [...config.robotsAiCrawlerUserAgents],
    llmsDisabledSections: [...config.llmsDisabledSections],
  };
}

/** Restore SEO fields from a history rollback snapshot. */
export function restoreTenantSeoBundleRollbackSnapshot(
  current: PublicMarketingConfig,
  snapshot: TenantSeoBundleRollbackSnapshot,
): PublicMarketingConfig {
  return {
    ...current,
    homepageSeoTitle: snapshot.homepageSeoTitle,
    homepageSeoDescription: snapshot.homepageSeoDescription,
    defaultOgImageUrl: snapshot.defaultOgImageUrl,
    hubPageSeo: { ...snapshot.hubPageSeo },
    landingPageSeo: { ...snapshot.landingPageSeo },
    robotsDisallowPaths: [...snapshot.robotsDisallowPaths],
    robotsAllowPaths: [...snapshot.robotsAllowPaths],
    robotsAiCrawlerPolicy: snapshot.robotsAiCrawlerPolicy,
    robotsAiCrawlerUserAgents: [...snapshot.robotsAiCrawlerUserAgents],
    llmsDisabledSections: [...snapshot.llmsDisabledSections],
  };
}

/** Roll back marketing config to the snapshot stored on a history entry. */
export function rollbackTenantSeoBundleHistoryEntry(
  current: PublicMarketingConfig,
  entry: TenantSeoBundleHistoryEntry,
  options?: { maxEntries?: number; suggestedAuditNote?: string; suggestedAuditLabel?: string },
): PublicMarketingConfig {
  if (!entry.rollbackSnapshot) {
    throw new Error('This history entry has no rollback snapshot');
  }
  const restored = restoreTenantSeoBundleRollbackSnapshot(current, entry.rollbackSnapshot);
  const restoredWithHistory = { ...restored, seoBundleHistory: current.seoBundleHistory };
  const rollbackBundle = buildTenantMarketingSeoBundle({
    config: restoredWithHistory,
    branding: {
      companyName: entry.brandingCompanyName ?? 'Tenant',
    },
    exportedAt: entry.exportedAt,
  });
  const changeCount = diffTenantSeoBundleImport(current, rollbackBundle).length;
  return recordTenantSeoBundleHistory(restoredWithHistory, {
    bundle: rollbackBundle,
    source: 'rollback',
    changeCount,
    rollbackSnapshot: exportTenantSeoBundleRollbackSnapshot(current),
    maxEntries: options?.maxEntries,
    suggestedAuditNote: options?.suggestedAuditNote,
    suggestedAuditLabel: options?.suggestedAuditLabel,
  });
}

/** Parse stored applied bundle from a history entry. */
export function parseHistoryEntryAppliedBundle(
  entry: TenantSeoBundleHistoryEntry,
): TenantMarketingSeoBundle | null {
  if (!entry.appliedBundle) return null;
  const validation = validateTenantMarketingSeoBundle(entry.appliedBundle);
  return validation.valid && validation.bundle ? validation.bundle : null;
}

/** Re-apply a stored bundle from history (deeper restore than rollback snapshot). */
export function reapplyTenantSeoBundleHistoryEntry(
  current: PublicMarketingConfig,
  entry: TenantSeoBundleHistoryEntry,
  options?: { maxEntries?: number; suggestedAuditNote?: string; suggestedAuditLabel?: string },
): PublicMarketingConfig {
  const bundle = parseHistoryEntryAppliedBundle(entry);
  if (!bundle) {
    throw new Error('This history entry has no valid applied bundle');
  }
  const changes = diffTenantSeoBundleImport(current, bundle);
  const merged = mergeImportedTenantMarketingSeoBundle(current, bundle);
  return recordTenantSeoBundleHistory(merged, {
    bundle,
    source: 'reapply',
    changeCount: changes.length,
    rollbackSnapshot: exportTenantSeoBundleRollbackSnapshot(current),
    maxEntries: options?.maxEntries,
    suggestedAuditNote: options?.suggestedAuditNote,
    suggestedAuditLabel: options?.suggestedAuditLabel,
  });
}

/** Diff a history rollback snapshot against the current live config. */
export function diffTenantSeoBundleSnapshotWithCurrent(
  current: PublicMarketingConfig,
  snapshot: TenantSeoBundleRollbackSnapshot,
): TenantSeoBundleSnapshotDiffRow[] {
  return diffTenantSeoBundleRollbackSnapshots(snapshot, exportTenantSeoBundleRollbackSnapshot(current));
}

export type PlatformTenantSeoBundleHistoryAuditRow = {
  organizationId: string;
  organizationName: string;
  organizationSlug: string;
  config: PublicMarketingConfig;
};

/** Platform super-admin: list tenant SEO bundle apply history across workspaces. */
export async function fetchPlatformTenantSeoBundleHistoryAudit(
  organizationId?: string | null,
): Promise<PlatformTenantSeoBundleHistoryAuditRow[]> {
  const { data, error } = await supabase.rpc('platform_list_tenant_seo_bundle_history_audit', {
    p_org_id: organizationId ?? null,
  });
  if (error) throw error;

  const platformPartial = await fetchPlatformPublicMarketingDefault();
  const rows = (data ?? []) as Array<{
    organization_id: string;
    organization_name: string;
    organization_slug: string;
    public_marketing_config: unknown;
  }>;

  return rows
    .map((row) => {
      const config = resolvePublicMarketingConfig(
        parsePublicMarketingConfig(row.public_marketing_config),
        platformPartial,
      );
      if (!config.seoBundleHistory.length) return null;
      return {
        organizationId: row.organization_id,
        organizationName: row.organization_name,
        organizationSlug: row.organization_slug,
        config,
      } satisfies PlatformTenantSeoBundleHistoryAuditRow;
    })
    .filter((row): row is PlatformTenantSeoBundleHistoryAuditRow => row !== null);
}

/** Flatten platform audit rows for CSV export. */
export function serializePlatformTenantSeoBundleHistoryAuditCsv(
  rows: readonly PlatformTenantSeoBundleHistoryAuditRow[],
): string {
  const header = [
    'organization_id',
    'organization_name',
    'organization_slug',
    'applied_at',
    'exported_at',
    'source',
    'change_count',
    'audit_label',
    'audit_note',
    'pinned',
    'pin_note',
    'pin_note_audit_count',
    'latest_pin_note_audit_action',
    'latest_pin_note_audit_at',
    'pin_note_audit_json',
    'has_rollback_snapshot',
    'has_applied_bundle',
  ];
  const lines: string[] = [];
  for (const row of rows) {
    for (const entry of row.config.seoBundleHistory) {
      const latestPinNoteAudit = getTenantSeoBundleHistoryLatestPinNoteAudit(entry);
      lines.push(
        [
          row.organizationId,
          row.organizationName,
          row.organizationSlug,
          entry.appliedAt,
          entry.exportedAt,
          entry.source,
          String(entry.changeCount),
          entry.auditLabel ?? formatTenantSeoBundleHistoryAuditLabel(entry.source, entry.changeCount),
          entry.auditNote ?? '',
          entry.pinned ? 'yes' : 'no',
          entry.pinNote ?? '',
          String(entry.pinNoteAudit?.length ?? 0),
          latestPinNoteAudit?.action ?? '',
          latestPinNoteAudit?.updatedAt ?? '',
          serializeTenantSeoBundleHistoryPinNoteAuditJson(entry),
          entry.rollbackSnapshot ? 'yes' : 'no',
          entry.appliedBundle ? 'yes' : 'no',
        ]
          .map((cell) => escapeCsvCell(cell))
          .join(','),
      );
    }
  }
  return [header.join(','), ...lines].join('\n');
}

/** Flatten platform audit rows for JSON export. */
export function serializePlatformTenantSeoBundleHistoryAuditJson(
  rows: readonly PlatformTenantSeoBundleHistoryAuditRow[],
): string {
  return JSON.stringify(
    rows.map((row) => ({
      organization_id: row.organizationId,
      organization_name: row.organizationName,
      organization_slug: row.organizationSlug,
      seo_bundle_history: JSON.parse(serializeTenantSeoBundleHistoryJson(row.config.seoBundleHistory)),
    })),
    null,
    2,
  );
}

export type PlatformTenantSeoBundleHistoryPruneResult = {
  organizationId: string;
  removedCount: number;
};

export type PlatformTenantSeoBundleHistoryManualPruneResult = {
  removedCount: number;
};

/** Platform super-admin: run orphan prune immediately (logs trigger_source=manual). */
export async function runPlatformTenantSeoBundleHistoryPrune(): Promise<PlatformTenantSeoBundleHistoryManualPruneResult> {
  const { data, error } = await supabase.rpc('platform_run_tenant_seo_bundle_history_prune');
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  const removedCount = Number(
    (row as Record<string, unknown> | null)?.removed_count ??
      (row as Record<string, unknown> | null)?.removedCount ??
      0,
  );
  return { removedCount: Number.isFinite(removedCount) ? Math.max(0, removedCount) : 0 };
}

/** Platform super-admin: update audit label/note on a tenant history entry. */
export async function platformUpdateTenantSeoBundleHistoryAudit(input: {
  organizationId: string;
  entryIndex: number;
  auditLabel?: string | null;
  auditNote?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('platform_update_tenant_seo_bundle_history_audit', {
    p_org_id: input.organizationId,
    p_entry_index: input.entryIndex,
    p_audit_label: input.auditLabel === undefined ? null : input.auditLabel,
    p_audit_note: input.auditNote === undefined ? null : input.auditNote,
  });
  if (error) throw error;
}

/** Platform super-admin: bulk update audit label on multiple history entries for one org. */
export async function platformBulkUpdateTenantSeoBundleHistoryAudit(input: {
  organizationId: string;
  entryIndices: readonly number[];
  auditLabel?: string | null;
  auditNote?: string | null;
}): Promise<number> {
  const { data, error } = await supabase.rpc('platform_bulk_update_tenant_seo_bundle_history_audit', {
    p_org_id: input.organizationId,
    p_entry_indices: [...input.entryIndices],
    p_audit_label: input.auditLabel === undefined ? null : input.auditLabel,
    p_audit_note: input.auditNote === undefined ? null : input.auditNote,
  });
  if (error) throw error;
  const updated = Number(data ?? 0);
  return Number.isFinite(updated) ? Math.max(0, updated) : 0;
}

/** Platform super-admin: toggle or set pinned state on a tenant history entry. */
export async function platformToggleTenantSeoBundleHistoryPin(input: {
  organizationId: string;
  entryIndex: number;
  pinned?: boolean | null;
  pinNote?: string | null;
}): Promise<boolean> {
  const { data, error } = await supabase.rpc('platform_toggle_tenant_seo_bundle_history_pin', {
    p_org_id: input.organizationId,
    p_entry_index: input.entryIndex,
    p_pinned: input.pinned ?? null,
    p_pin_note: input.pinNote === undefined ? null : input.pinNote,
  });
  if (error) throw error;
  return data === true;
}

/** Platform super-admin: bulk pin tenant history entries. */
export async function platformBulkPinTenantSeoBundleHistory(input: {
  organizationId: string;
  entryIndices: readonly number[];
  pinNote?: string | null;
  auditAction?: TenantSeoBundleHistoryPinNoteAuditAction;
}): Promise<number> {
  const { data, error } = await supabase.rpc('platform_bulk_pin_tenant_seo_bundle_history', {
    p_org_id: input.organizationId,
    p_entry_indices: [...input.entryIndices],
    p_pin_note: input.pinNote ?? null,
    p_pin_note_audit_action: input.auditAction ?? 'bulk_pin',
  });
  if (error) throw error;
  const updated = Number(data ?? 0);
  return Number.isFinite(updated) ? Math.max(0, updated) : 0;
}

/** Platform super-admin: update pin note on a pinned tenant history entry. */
export async function platformUpdateTenantSeoBundleHistoryPinNote(input: {
  organizationId: string;
  entryIndex: number;
  pinNote: string | null;
  auditAction?: TenantSeoBundleHistoryPinNoteAuditAction;
}): Promise<void> {
  const { error } = await supabase.rpc('platform_update_tenant_seo_bundle_history_pin_note', {
    p_org_id: input.organizationId,
    p_entry_index: input.entryIndex,
    p_pin_note: input.pinNote,
    p_pin_note_audit_action: input.auditAction ?? 'manual',
  });
  if (error) throw error;
}

/** Platform super-admin: bulk unpin tenant history entries. */
export async function platformBulkUnpinTenantSeoBundleHistory(input: {
  organizationId: string;
  entryIndices: readonly number[];
}): Promise<number> {
  const { data, error } = await supabase.rpc('platform_bulk_unpin_tenant_seo_bundle_history', {
    p_org_id: input.organizationId,
    p_entry_indices: [...input.entryIndices],
  });
  if (error) throw error;
  const updated = Number(data ?? 0);
  return Number.isFinite(updated) ? Math.max(0, updated) : 0;
}

/** Platform super-admin: set or clear per-org org-pin override. */
export async function platformUpdateTenantSeoBundleHistoryOrgPinOverride(input: {
  organizationId: string;
  orgPinOverride: boolean | null;
}): Promise<void> {
  const { error } = await supabase.rpc('platform_update_tenant_seo_bundle_history_org_pin_override', {
    p_org_id: input.organizationId,
    p_org_pin_override: input.orgPinOverride,
  });
  if (error) throw error;
}

/** Platform super-admin: set or clear per-org pin-note-required override. */
export async function platformUpdateTenantSeoBundleHistoryPinNoteRequiredOverride(input: {
  organizationId: string;
  pinNoteRequiredOverride: boolean | null;
}): Promise<void> {
  const { error } = await supabase.rpc(
    'platform_update_tenant_seo_bundle_history_pin_note_required_override',
    {
      p_org_id: input.organizationId,
      p_pin_note_required_override: input.pinNoteRequiredOverride,
    },
  );
  if (error) throw error;
}

/** Platform super-admin: bulk update pin note on pinned tenant history entries. */
export async function platformBulkUpdateTenantSeoBundleHistoryPinNote(input: {
  organizationId: string;
  entryIndices: readonly number[];
  pinNote: string | null;
  auditAction?: TenantSeoBundleHistoryPinNoteAuditAction;
}): Promise<number> {
  const { data, error } = await supabase.rpc('platform_bulk_update_tenant_seo_bundle_history_pin_note', {
    p_org_id: input.organizationId,
    p_entry_indices: [...input.entryIndices],
    p_pin_note: input.pinNote,
    p_pin_note_audit_action: input.auditAction ?? 'bulk_update',
  });
  if (error) throw error;
  const updated = Number(data ?? 0);
  return Number.isFinite(updated) ? Math.max(0, updated) : 0;
}

/** Platform super-admin: apply resolved pin notes per entry source on pinned rows. */
export async function platformBulkApplyTenantSeoBundleHistoryPinNoteBySource(input: {
  organizationId: string;
  entries: ReadonlyArray<{
    index: number;
    source: TenantSeoBundleHistoryEntry['source'];
    changeCount: number;
  }>;
  orgConfig: PublicMarketingConfig;
  platformPinNotes: TenantSeoBundleHistorySourcePinNotes;
  defaultPinNote?: string;
}): Promise<number> {
  if (input.entries.length === 0) return 0;
  const groups = new Map<string, number[]>();
  for (const entry of input.entries) {
    const pinNote = resolveTenantSeoBundleHistoryPinNote(
      entry.source,
      input.platformPinNotes,
      input.orgConfig.seoBundleHistoryDefaultPinNotesBySource,
      input.defaultPinNote,
      entry.changeCount,
    );
    if (!pinNote) continue;
    const existing = groups.get(pinNote);
    if (existing) existing.push(entry.index);
    else groups.set(pinNote, [entry.index]);
  }
  let updated = 0;
  for (const [pinNote, indices] of groups) {
    updated += await platformBulkUpdateTenantSeoBundleHistoryPinNote({
      organizationId: input.organizationId,
      entryIndices: indices,
      pinNote,
      auditAction: 'bulk_by_source',
    });
  }
  return updated;
}

/** Platform super-admin: bulk pin filtered entries with per-source pin notes. */
export async function platformBulkPinTenantSeoBundleHistoryBySource(input: {
  organizationId: string;
  entries: ReadonlyArray<{
    index: number;
    source: TenantSeoBundleHistoryEntry['source'];
    changeCount: number;
  }>;
  orgConfig: PublicMarketingConfig;
  platformPinNotes: TenantSeoBundleHistorySourcePinNotes;
  defaultPinNote?: string;
}): Promise<number> {
  if (input.entries.length === 0) return 0;
  const groups = new Map<string, number[]>();
  for (const entry of input.entries) {
    const pinNote = resolveTenantSeoBundleHistoryPinNote(
      entry.source,
      input.platformPinNotes,
      input.orgConfig.seoBundleHistoryDefaultPinNotesBySource,
      input.defaultPinNote,
      entry.changeCount,
    );
    if (!pinNote) continue;
    const existing = groups.get(pinNote);
    if (existing) existing.push(entry.index);
    else groups.set(pinNote, [entry.index]);
  }
  let updated = 0;
  for (const [pinNote, indices] of groups) {
    updated += await platformBulkPinTenantSeoBundleHistory({
      organizationId: input.organizationId,
      entryIndices: indices,
      pinNote,
      auditAction: 'bulk_pin_by_source',
    });
  }
  return updated;
}

/** Platform super-admin: apply resolved audit notes/labels per entry source. */
export async function platformBulkApplyTenantSeoBundleHistoryAuditBySource(input: {
  organizationId: string;
  entries: ReadonlyArray<{
    index: number;
    source: TenantSeoBundleHistoryEntry['source'];
    changeCount: number;
  }>;
  orgConfig: PublicMarketingConfig;
  platformNotes: TenantSeoBundleHistorySourceAuditNotes;
  platformLabels: TenantSeoBundleHistorySourceAuditLabels;
  applyNote?: boolean;
  applyLabel?: boolean;
}): Promise<number> {
  if (input.entries.length === 0) return 0;
  const applyNote = input.applyNote !== false;
  const applyLabel = input.applyLabel === true;
  if (!applyNote && !applyLabel) return 0;

  const groups = new Map<string, { indices: number[]; auditNote?: string; auditLabel?: string }>();
  for (const entry of input.entries) {
    const auditNote = applyNote
      ? resolveTenantSeoBundleHistoryAuditNote(
          entry.source,
          input.platformNotes,
          input.orgConfig.seoBundleHistoryDefaultAuditNotes,
        )
      : undefined;
    const auditLabel = applyLabel
      ? resolveTenantSeoBundleHistoryAuditLabel(
          entry.source,
          entry.changeCount,
          input.platformLabels,
          input.orgConfig.seoBundleHistoryDefaultAuditLabels,
        )
      : undefined;
    const key = `${auditNote ?? ''}\0${auditLabel ?? ''}`;
    const existing = groups.get(key);
    if (existing) {
      existing.indices.push(entry.index);
    } else {
      groups.set(key, {
        indices: [entry.index],
        ...(auditNote ? { auditNote } : {}),
        ...(auditLabel ? { auditLabel } : {}),
      });
    }
  }

  let updated = 0;
  for (const group of groups.values()) {
    updated += await platformBulkUpdateTenantSeoBundleHistoryAudit({
      organizationId: input.organizationId,
      entryIndices: group.indices,
      auditNote: group.auditNote,
      auditLabel: group.auditLabel,
    });
  }
  return updated;
}

/** Platform super-admin: prune tenant SEO bundle history (orphans + retention cap). */
export async function platformPruneTenantSeoBundleHistory(input?: {
  organizationId?: string | null;
  dropEntriesWithoutAppliedBundle?: boolean;
  maxEntries?: number;
}): Promise<PlatformTenantSeoBundleHistoryPruneResult[]> {
  const { data, error } = await supabase.rpc('platform_prune_tenant_seo_bundle_history', {
    p_org_id: input?.organizationId ?? null,
    p_drop_without_applied_bundle: input?.dropEntriesWithoutAppliedBundle ?? true,
    p_max_entries: normalizeTenantSeoBundleHistoryMaxEntries(input?.maxEntries),
  });
  if (error) throw error;
  return ((data ?? []) as Array<{ organization_id: string; removed_count: number }>).map((row) => ({
    organizationId: row.organization_id,
    removedCount: row.removed_count,
  }));
}

/** Diff generated tenant SEO files before vs after bundle restore. */
export function diffTenantSeoBundleFiles(
  current: PublicMarketingConfig,
  bundle: TenantMarketingSeoBundle,
  branding: MarketingSeoPreviewBranding,
  llmsTemplate?: string,
): TenantSeoBundleFileDiff[] {
  const merged = mergeImportedTenantMarketingSeoBundle(current, bundle);
  const beforeBundle = buildTenantMarketingSeoBundle({
    config: current,
    branding,
    llmsTemplate,
  });
  const afterBundle = buildTenantMarketingSeoBundle({
    config: merged,
    branding,
    llmsTemplate,
  });

  const filenames: Array<'robots.txt' | 'sitemap.xml' | 'llms.txt'> = [
    'robots.txt',
    'sitemap.xml',
    'llms.txt',
  ];

  return filenames.map((filename) => {
    const before = beforeBundle.files[filename] ?? '';
    const after = afterBundle.files[filename] ?? '';
    return {
      filename,
      changed: before !== after,
      beforeBytes: countTextBytes(before),
      afterBytes: countTextBytes(after),
      beforeLines: countTextLines(before),
      afterLines: countTextLines(after),
      diffPreview: summarizeTenantSeoFileDiff(before, after),
    };
  });
}

export const MAX_TENANT_SEO_BUNDLE_HISTORY = 5;
export const DEFAULT_TENANT_SEO_BUNDLE_HISTORY_MAX = MAX_TENANT_SEO_BUNDLE_HISTORY;
export const MIN_TENANT_SEO_BUNDLE_HISTORY_MAX = 1;
export const TENANT_SEO_BUNDLE_HISTORY_MAX_LIMIT = 20;

export function normalizeTenantSeoBundleHistoryMaxEntries(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_MAX;
  return Math.min(
    TENANT_SEO_BUNDLE_HISTORY_MAX_LIMIT,
    Math.max(MIN_TENANT_SEO_BUNDLE_HISTORY_MAX, Math.round(parsed)),
  );
}

/** Resolve effective history retention: org override wins over platform default. */
export function resolveTenantSeoBundleHistoryMaxEntries(
  platformMax: number,
  orgOverride?: number | null,
): number {
  if (orgOverride !== undefined && orgOverride !== null && Number.isFinite(orgOverride)) {
    return normalizeTenantSeoBundleHistoryMaxEntries(orgOverride);
  }
  return normalizeTenantSeoBundleHistoryMaxEntries(platformMax);
}

export function formatTenantSeoBundleHistoryAuditLabel(
  source: TenantSeoBundleHistoryEntry['source'],
  changeCount: number,
): string {
  const sourceLabel = source.replaceAll('_', ' ');
  return `${sourceLabel} · ${changeCount} change${changeCount === 1 ? '' : 's'}`;
}

export function updateTenantSeoBundleHistoryAuditNote(
  config: PublicMarketingConfig,
  entryIndex: number,
  auditNote: string,
): PublicMarketingConfig {
  const note = auditNote.trim().slice(0, 500);
  const history = config.seoBundleHistory.map((entry, index) => {
    if (index !== entryIndex) return entry;
    if (!note) {
      const { auditNote: _removed, ...rest } = entry;
      return rest;
    }
    return { ...entry, auditNote: note };
  });
  return { ...config, seoBundleHistory: history };
}

export function updateTenantSeoBundleHistoryAuditLabel(
  config: PublicMarketingConfig,
  entryIndex: number,
  auditLabel: string,
): PublicMarketingConfig {
  const label = auditLabel.trim();
  const history = config.seoBundleHistory.map((entry, index) => {
    if (index !== entryIndex) return entry;
    if (!label) {
      const { auditLabel: _removed, ...rest } = entry;
      return rest;
    }
    return { ...entry, auditLabel: label };
  });
  return { ...config, seoBundleHistory: history };
}

export async function fetchTenantSeoBundleHistoryMaxEntries(): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_MAX_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) {
      return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_MAX;
    }
    if (typeof data.value === 'number') {
      return normalizeTenantSeoBundleHistoryMaxEntries(data.value);
    }
    if (typeof data.value === 'object' && data.value !== null) {
      const maxEntries = (data.value as Record<string, unknown>).max_entries;
      return normalizeTenantSeoBundleHistoryMaxEntries(maxEntries);
    }
    return normalizeTenantSeoBundleHistoryMaxEntries(data.value);
  } catch {
    return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_MAX;
  }
}

export async function saveTenantSeoBundleHistoryMaxEntries(maxEntries: number): Promise<void> {
  const normalized = normalizeTenantSeoBundleHistoryMaxEntries(maxEntries);
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_MAX_KEY,
      value: { max_entries: normalized },
      category: 'features',
      data_type: 'json',
      is_public: false,
      description: 'Maximum SEO bundle apply history entries retained per tenant organization.',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export async function fetchTenantSeoBundleHistorySavedViewsLimit(): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_SAVED_VIEWS_LIMIT_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) {
      return TENANT_SEO_BUNDLE_HISTORY_SAVED_VIEWS_LIMIT;
    }
    if (typeof data.value === 'object' && data.value !== null) {
      const limit = (data.value as Record<string, unknown>).limit;
      return normalizeTenantSeoBundleHistorySavedViewsLimit(limit);
    }
    return normalizeTenantSeoBundleHistorySavedViewsLimit(data.value);
  } catch {
    return TENANT_SEO_BUNDLE_HISTORY_SAVED_VIEWS_LIMIT;
  }
}

export async function saveTenantSeoBundleHistorySavedViewsLimit(limit: number): Promise<void> {
  const normalized = normalizeTenantSeoBundleHistorySavedViewsLimit(limit);
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_SAVED_VIEWS_LIMIT_KEY,
      value: { limit: normalized },
      category: 'features',
      data_type: 'json',
      is_public: false,
      description: 'Maximum named saved SEO bundle history filter views retained per tenant scope.',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

/** Supabase table backing cross-device, per-organization saved history filter views. */
export const TENANT_SEO_HISTORY_SAVED_VIEWS_TABLE = 'tenant_seo_history_saved_views';

/** Shown when a saved-view change persisted locally but the Supabase sync failed. */
export const TENANT_SEO_HISTORY_SAVED_VIEWS_CLOUD_SYNC_FAILED_MESSAGE =
  'Saved on this device, but cloud sync failed — it will sync next time you reload.';

/** Raw row shape returned by the saved-views table (only the fields we read). */
export interface TenantSeoHistorySavedViewRow {
  name?: unknown;
  token?: unknown;
}

/** Pure mapper from a DB row to the in-app saved-view shape (testable without a client). */
export function mapTenantSeoHistorySavedViewRow(
  row: TenantSeoHistorySavedViewRow,
): TenantSeoBundleHistorySavedView {
  return {
    name: typeof row.name === 'string' ? row.name : String(row.name ?? ''),
    token: typeof row.token === 'string' ? row.token : '',
  };
}

/**
 * Fetch an organization's saved views from Supabase (cross-device). Returns a
 * normalized list; on any error returns an empty list so callers can fall back
 * to localStorage.
 */
export async function fetchTenantSeoHistorySavedViewsFromSupabase(
  organizationId: string,
): Promise<TenantSeoBundleHistorySavedView[]> {
  if (!organizationId) return [];
  try {
    const { data, error } = await supabase
      .from(TENANT_SEO_HISTORY_SAVED_VIEWS_TABLE)
      .select('name, token')
      .eq('organization_id', organizationId);
    if (error || !Array.isArray(data)) return [];
    return normalizeTenantSeoBundleHistorySavedViews(
      data.map((row) => mapTenantSeoHistorySavedViewRow(row as TenantSeoHistorySavedViewRow)),
    );
  } catch {
    return [];
  }
}

/**
 * Insert or update one saved view for an organization. The table has a
 * case-insensitive unique index on (organization_id, lower(name)), so we look
 * up an existing row by name first and update it, otherwise insert.
 */
export async function upsertTenantSeoHistorySavedViewToSupabase(
  organizationId: string,
  view: TenantSeoBundleHistorySavedView,
): Promise<void> {
  if (!organizationId) return;
  const name = view.name.trim();
  if (!name) return;
  const { data: existing, error: lookupError } = await supabase
    .from(TENANT_SEO_HISTORY_SAVED_VIEWS_TABLE)
    .select('id')
    .eq('organization_id', organizationId)
    .ilike('name', name)
    .maybeSingle();
  if (lookupError) throw lookupError;
  if (existing?.id) {
    const { error } = await supabase
      .from(TENANT_SEO_HISTORY_SAVED_VIEWS_TABLE)
      .update({ name, token: view.token })
      .eq('id', existing.id);
    if (error) throw error;
    return;
  }
  const { error } = await supabase
    .from(TENANT_SEO_HISTORY_SAVED_VIEWS_TABLE)
    .insert({ organization_id: organizationId, name, token: view.token });
  if (error) throw error;
}

/** Delete one saved view (by case-insensitive name) for an organization. */
export async function deleteTenantSeoHistorySavedViewFromSupabase(
  organizationId: string,
  name: string,
): Promise<void> {
  if (!organizationId || !name.trim()) return;
  const { error } = await supabase
    .from(TENANT_SEO_HISTORY_SAVED_VIEWS_TABLE)
    .delete()
    .eq('organization_id', organizationId)
    .ilike('name', name.trim());
  if (error) throw error;
}

export async function fetchTenantSeoBundleHistoryPruneCronEnabled(): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_PRUNE_CRON_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) return false;
    if (typeof data.value === 'boolean') return data.value;
    if (typeof data.value === 'object' && data.value !== null) {
      const enabled = (data.value as Record<string, unknown>).enabled;
      if (typeof enabled === 'boolean') return enabled;
    }
    return String(data.value).replace(/"/g, '').trim().toLowerCase() === 'true';
  } catch {
    return false;
  }
}

export async function saveTenantSeoBundleHistoryPruneCronEnabled(enabled: boolean): Promise<void> {
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_PRUNE_CRON_KEY,
      value: { enabled },
      category: 'features',
      data_type: 'json',
      is_public: false,
      description: 'When true, pg_cron prunes orphan tenant SEO bundle history weekly.',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export async function fetchTenantSeoBundleHistoryPinPolicy(): Promise<TenantSeoBundleHistoryPinPolicy> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_PIN_POLICY_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) {
      return { ...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_PIN_POLICY };
    }
    return normalizeTenantSeoBundleHistoryPinPolicy(data.value);
  } catch {
    return { ...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_PIN_POLICY };
  }
}

export async function saveTenantSeoBundleHistoryPinPolicy(
  policy: TenantSeoBundleHistoryPinPolicy,
): Promise<void> {
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_PIN_POLICY_KEY,
      value: {
        org_pin_enabled: policy.orgPinEnabled,
        platform_pin_enabled: policy.platformPinEnabled,
        pin_note_required: policy.pinNoteRequired,
      },
      category: 'features',
      data_type: 'json',
      is_public: false,
      description:
        'Controls whether org admins and platform super-admins can pin SEO bundle history entries.',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export async function fetchTenantSeoBundleHistoryDefaultPinNote(): Promise<string> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_DEFAULT_PIN_NOTE_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) return '';
    if (typeof data.value === 'string') return normalizeTenantSeoBundleHistoryPinNote(data.value);
    if (typeof data.value === 'object' && data.value !== null) {
      return normalizeTenantSeoBundleHistoryPinNote(
        (data.value as Record<string, unknown>).note ?? (data.value as Record<string, unknown>).default_note,
      );
    }
    return '';
  } catch {
    return '';
  }
}

export async function saveTenantSeoBundleHistoryDefaultPinNote(note: string): Promise<void> {
  const normalized = normalizeTenantSeoBundleHistoryPinNote(note);
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_DEFAULT_PIN_NOTE_KEY,
      value: { note: normalized },
      category: 'features',
      data_type: 'json',
      is_public: false,
      description: 'Default pin note applied when bulk-pinning tenant SEO bundle history entries.',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export async function fetchTenantSeoBundleHistoryPinNotesBySource(): Promise<TenantSeoBundleHistorySourcePinNotes> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_PIN_NOTE_BY_SOURCE_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) {
      return { ...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_PIN_NOTE_BY_SOURCE };
    }
    return normalizeTenantSeoBundleHistoryPinNotesBySource(data.value);
  } catch {
    return { ...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_PIN_NOTE_BY_SOURCE };
  }
}

export async function saveTenantSeoBundleHistoryPinNotesBySource(
  pinNotes: TenantSeoBundleHistorySourcePinNotes,
): Promise<void> {
  const normalized = normalizeTenantSeoBundleHistoryPinNotesBySource({ pin_notes_by_source: pinNotes });
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_PIN_NOTE_BY_SOURCE_KEY,
      value: { pin_notes_by_source: normalized },
      category: 'features',
      data_type: 'json',
      is_public: false,
      description: 'Default pin notes keyed by tenant SEO bundle history source.',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export async function fetchTenantSeoBundleHistoryExportPresets(): Promise<
  TenantSeoBundleHistoryExportPreset[]
> {
  try {
    const [{ data, error }, platformPresetLimit] = await Promise.all([
      supabase
        .from('app_config')
        .select('value')
        .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESETS_KEY)
        .maybeSingle(),
      fetchTenantSeoBundleHistoryExportPlatformPresetLimit(),
    ]);
    if (error || data?.value === undefined || data?.value === null) {
      return normalizeTenantSeoBundleHistoryExportPresets(
        DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESETS,
        platformPresetLimit,
      );
    }
    return normalizeTenantSeoBundleHistoryExportPresets(data.value, platformPresetLimit);
  } catch {
    return [...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESETS];
  }
}

export async function saveTenantSeoBundleHistoryExportPresets(
  presets: TenantSeoBundleHistoryExportPreset[],
  platformPresetLimit?: number,
): Promise<void> {
  const limit =
    platformPresetLimit === undefined
      ? await fetchTenantSeoBundleHistoryExportPlatformPresetLimit()
      : normalizeTenantSeoBundleHistoryExportPlatformPresetLimit(platformPresetLimit);
  const normalized = normalizeTenantSeoBundleHistoryExportPresets(presets, limit);
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESETS_KEY,
      value: {
        presets: normalized.map((preset) => serializeTenantSeoBundleHistoryExportPresetForStorage(preset)),
      },
      category: 'features',
      data_type: 'json',
      is_public: false,
      description: 'Pinned + source export presets for platform tenant SEO bundle history audit.',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export async function fetchTenantSeoBundleHistoryExportPlatformPresetLimit(): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PLATFORM_PRESET_LIMIT_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) {
      return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PLATFORM_PRESET_LIMIT;
    }
    return normalizeTenantSeoBundleHistoryExportPlatformPresetLimit(data.value);
  } catch {
    return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PLATFORM_PRESET_LIMIT;
  }
}

export async function saveTenantSeoBundleHistoryExportPlatformPresetLimit(limit: number): Promise<void> {
  const normalized = normalizeTenantSeoBundleHistoryExportPlatformPresetLimit(limit);
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PLATFORM_PRESET_LIMIT_KEY,
      value: normalized,
      category: 'features',
      data_type: 'number',
      is_public: false,
      description: 'Max tenant SEO bundle history export presets loaded/saved from platform app_config.',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export async function fetchTenantSeoBundleHistoryExportPresetLabelSearchMin(): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_LABEL_SEARCH_MIN_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) {
      return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_LABEL_SEARCH_MIN;
    }
    return normalizeTenantSeoBundleHistoryExportPresetLabelSearchMin(data.value);
  } catch {
    return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_LABEL_SEARCH_MIN;
  }
}

export async function saveTenantSeoBundleHistoryExportPresetLabelSearchMin(min: number): Promise<void> {
  const normalized = normalizeTenantSeoBundleHistoryExportPresetLabelSearchMin(min);
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_LABEL_SEARCH_MIN_KEY,
      value: normalized,
      category: 'features',
      data_type: 'number',
      is_public: false,
      description: 'Preset count threshold for showing label search in tenant SEO bundle history export UI.',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export async function fetchTenantSeoBundleHistoryExportPresetGroupBy(): Promise<TenantSeoBundleHistoryExportPresetGroupBy> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_GROUP_BY_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) {
      return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_GROUP_BY;
    }
    return normalizeTenantSeoBundleHistoryExportPresetGroupBy(data.value);
  } catch {
    return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_GROUP_BY;
  }
}

export async function saveTenantSeoBundleHistoryExportPresetGroupBy(
  groupBy: TenantSeoBundleHistoryExportPresetGroupBy,
): Promise<void> {
  const normalized = normalizeTenantSeoBundleHistoryExportPresetGroupBy(groupBy);
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_GROUP_BY_KEY,
      value: normalized,
      category: 'features',
      data_type: 'string',
      is_public: false,
      description:
        'Dimension for grouping export preset chips in tenant SEO bundle history panels (platform super-admin).',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export async function fetchTenantSeoBundleHistoryExportPresetChipExpandLimit(): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_EXPAND_LIMIT_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) {
      return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_EXPAND_LIMIT;
    }
    return normalizeTenantSeoBundleHistoryExportPresetChipExpandLimit(data.value);
  } catch {
    return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_EXPAND_LIMIT;
  }
}

export async function saveTenantSeoBundleHistoryExportPresetChipExpandLimit(limit: number): Promise<void> {
  const normalized = normalizeTenantSeoBundleHistoryExportPresetChipExpandLimit(limit);
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_EXPAND_LIMIT_KEY,
      value: normalized,
      category: 'features',
      data_type: 'number',
      is_public: false,
      description: 'Max export preset chips shown before expand toggle in tenant SEO bundle history panels.',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export async function fetchTenantSeoBundleHistoryExportPresetChipJumpAmbiguousLimit(): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_JUMP_AMBIGUOUS_LIMIT_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) {
      return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_JUMP_AMBIGUOUS_LIMIT;
    }
    return normalizeTenantSeoBundleHistoryExportPresetChipJumpAmbiguousLimit(data.value);
  } catch {
    return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_JUMP_AMBIGUOUS_LIMIT;
  }
}

export async function saveTenantSeoBundleHistoryExportPresetChipJumpAmbiguousLimit(
  limit: number,
): Promise<void> {
  const normalized = normalizeTenantSeoBundleHistoryExportPresetChipJumpAmbiguousLimit(limit);
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_JUMP_AMBIGUOUS_LIMIT_KEY,
      value: normalized,
      category: 'features',
      data_type: 'number',
      is_public: false,
      description:
        'Max ambiguous export preset label jump candidates shown before show-more in tenant SEO bundle history panels.',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export async function fetchTenantSeoBundleHistoryExportPresetChipPageSize(): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_SIZE_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) {
      return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_SIZE;
    }
    return normalizeTenantSeoBundleHistoryExportPresetChipPageSize(data.value);
  } catch {
    return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_SIZE;
  }
}

export async function saveTenantSeoBundleHistoryExportPresetChipPageSize(pageSize: number): Promise<void> {
  const normalized = normalizeTenantSeoBundleHistoryExportPresetChipPageSize(pageSize);
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_SIZE_KEY,
      value: normalized,
      category: 'features',
      data_type: 'number',
      is_public: false,
      description:
        'Page size for paginated export preset chips when expanded in tenant SEO bundle history panels.',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export function normalizeTenantSeoBundleHistoryExportPresetChipPagePersistEnabled(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 'true' || value === 1 || value === '1') return true;
  if (value === 'false' || value === 0 || value === '0') return false;
  return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_PERSIST_ENABLED;
}

export async function fetchTenantSeoBundleHistoryExportPresetChipPagePersistEnabled(): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_PERSIST_ENABLED_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) {
      return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_PERSIST_ENABLED;
    }
    return normalizeTenantSeoBundleHistoryExportPresetChipPagePersistEnabled(data.value);
  } catch {
    return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_PERSIST_ENABLED;
  }
}

export async function saveTenantSeoBundleHistoryExportPresetChipPagePersistEnabled(
  enabled: boolean,
): Promise<void> {
  const normalized = normalizeTenantSeoBundleHistoryExportPresetChipPagePersistEnabled(enabled);
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_EXPORT_PRESET_CHIP_PAGE_PERSIST_ENABLED_KEY,
      value: normalized,
      category: 'features',
      data_type: 'boolean',
      is_public: false,
      description:
        'Persist expanded export preset chip page index per filter context in browser session storage.',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export async function fetchTenantSeoBundleHistoryAuditLabelPresets(): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_PRESETS_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) {
      return [...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_PRESETS];
    }
    return normalizeTenantSeoBundleHistoryAuditLabelPresets(data.value);
  } catch {
    return [...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_PRESETS];
  }
}

export async function saveTenantSeoBundleHistoryAuditLabelPresets(presets: string[]): Promise<void> {
  const normalized = normalizeTenantSeoBundleHistoryAuditLabelPresets(presets);
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_PRESETS_KEY,
      value: { presets: normalized },
      category: 'features',
      data_type: 'json',
      is_public: false,
      description: 'Quick-apply audit label presets for tenant SEO bundle history (platform super-admin).',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export async function fetchTenantSeoBundleHistoryAuditNotePlaceholder(): Promise<string> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_PLACEHOLDER_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) {
      return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_PLACEHOLDER;
    }
    return normalizeTenantSeoBundleHistoryAuditNotePlaceholder(data.value);
  } catch {
    return DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_PLACEHOLDER;
  }
}

export async function saveTenantSeoBundleHistoryAuditNotePlaceholder(placeholder: string): Promise<void> {
  const normalized = normalizeTenantSeoBundleHistoryAuditNotePlaceholder(placeholder);
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_PLACEHOLDER_KEY,
      value: { placeholder: normalized },
      category: 'features',
      data_type: 'json',
      is_public: false,
      description: 'Placeholder hint for tenant SEO bundle history audit notes (platform super-admin).',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export async function fetchTenantSeoBundleHistoryAuditNotesBySource(): Promise<TenantSeoBundleHistorySourceAuditNotes> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_BY_SOURCE_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) {
      return { ...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_BY_SOURCE };
    }
    return normalizeTenantSeoBundleHistoryAuditNotesBySource(data.value);
  } catch {
    return { ...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_BY_SOURCE };
  }
}

export async function saveTenantSeoBundleHistoryAuditNotesBySource(
  notes: TenantSeoBundleHistorySourceAuditNotes,
): Promise<void> {
  const normalized = normalizeTenantSeoBundleHistoryAuditNotesBySource({ notes_by_source: notes });
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_AUDIT_NOTE_BY_SOURCE_KEY,
      value: { notes_by_source: normalized },
      category: 'features',
      data_type: 'json',
      is_public: false,
      description: 'Default audit notes keyed by tenant SEO bundle history source (platform super-admin).',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export async function fetchTenantSeoBundleHistoryAuditLabelsBySource(): Promise<TenantSeoBundleHistorySourceAuditLabels> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_BY_SOURCE_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) {
      return { ...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_BY_SOURCE };
    }
    return normalizeTenantSeoBundleHistoryAuditLabelsBySource(data.value);
  } catch {
    return { ...DEFAULT_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_BY_SOURCE };
  }
}

export async function saveTenantSeoBundleHistoryAuditLabelsBySource(
  labels: TenantSeoBundleHistorySourceAuditLabels,
): Promise<void> {
  const normalized = normalizeTenantSeoBundleHistoryAuditLabelsBySource({ labels_by_source: labels });
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_TENANT_SEO_BUNDLE_HISTORY_AUDIT_LABEL_BY_SOURCE_KEY,
      value: { labels_by_source: normalized },
      category: 'features',
      data_type: 'json',
      is_public: false,
      description: 'Default audit labels keyed by tenant SEO bundle history source (platform super-admin).',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

export type ScheduledJobLastRun = {
  completedAt: string | null;
  status: string | null;
  errorMessage: string | null;
};

export async function fetchTenantSeoBundleHistoryPruneCronRuns(
  limit = 15,
  triggerSource?: string | null,
): Promise<ScheduledJobRunLogRow[]> {
  return fetchScheduledJobRunList(
    TENANT_SEO_BUNDLE_HISTORY_PRUNE_JOB_KEY,
    limit,
    triggerSource?.trim() || null,
  );
}

export async function exportTenantSeoBundleHistoryPruneCronRuns(
  limit = 500,
  triggerSource?: string | null,
): Promise<ScheduledJobRunLogRow[]> {
  return exportScheduledJobRunLog(
    TENANT_SEO_BUNDLE_HISTORY_PRUNE_JOB_KEY,
    null,
    null,
    limit,
    triggerSource?.trim() || null,
  );
}

export function serializeTenantSeoBundleHistoryPruneCronRunsCsv(
  runs: readonly ScheduledJobRunLogRow[],
): string {
  const header = [
    'completed_at',
    'status',
    'trigger_source',
    'removed_count',
    'max_entries',
    'error_message',
  ];
  const lines = runs.map((row) => {
    const details = row.details && typeof row.details === 'object' ? row.details : {};
    const meta = details as Record<string, unknown>;
    return [
      row.completed_at ?? '',
      row.status ?? '',
      row.trigger_source ?? '',
      String(meta.removed_count ?? ''),
      String(meta.platform_max_entries ?? meta.max_entries ?? ''),
      row.error_message ?? '',
    ]
      .map((cell) => escapeCsvCell(String(cell)))
      .join(',');
  });
  return [header.join(','), ...lines].join('\n');
}

export function serializeTenantSeoBundleHistoryPruneCronRunsJson(
  runs: readonly ScheduledJobRunLogRow[],
): string {
  return JSON.stringify(runs, null, 2);
}

export async function fetchTenantSeoBundleHistoryPruneCronLastRun(): Promise<ScheduledJobLastRun | null> {
  try {
    const { data, error } = await supabase.rpc('saas_get_scheduled_job_last_run', {
      p_job_key: 'tenant_seo_bundle_history_prune',
    });
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    if (!row || typeof row !== 'object') return null;
    const record = row as Record<string, unknown>;
    return {
      completedAt: String(record.completed_at ?? record.completedAt ?? '').trim() || null,
      status: String(record.status ?? '').trim() || null,
      errorMessage: String(record.error_message ?? record.errorMessage ?? '').trim() || null,
    };
  } catch {
    return null;
  }
}

export function countTenantSeoBundleHistoryOrphans(history: readonly TenantSeoBundleHistoryEntry[]): number {
  return history.filter((entry) => !entry.appliedBundle).length;
}

export function pruneTenantSeoBundleHistory(
  config: PublicMarketingConfig,
  options?: {
    maxEntries?: number;
    dropEntriesWithoutAppliedBundle?: boolean;
  },
): { config: PublicMarketingConfig; removedCount: number } {
  const maxEntries = normalizeTenantSeoBundleHistoryMaxEntries(options?.maxEntries);
  const before = config.seoBundleHistory.length;
  let nextHistory = [...config.seoBundleHistory];
  if (options?.dropEntriesWithoutAppliedBundle) {
    nextHistory = nextHistory.filter(
      (entry) => isTenantSeoBundleHistoryEntryPinned(entry) || Boolean(entry.appliedBundle),
    );
  }
  nextHistory = trimTenantSeoBundleHistoryEntries(nextHistory, maxEntries);
  return {
    config: { ...config, seoBundleHistory: nextHistory },
    removedCount: before - nextHistory.length,
  };
}

/** Preview config + generated file diff before rolling back to a history snapshot. */
export function previewTenantSeoBundleRollback(
  current: PublicMarketingConfig,
  entry: TenantSeoBundleHistoryEntry,
  branding: MarketingSeoPreviewBranding,
  llmsTemplate?: string,
): {
  configDiff: TenantSeoBundleSnapshotDiffRow[];
  fileDiff: TenantSeoBundleFileDiff[];
} {
  if (!entry.rollbackSnapshot) {
    return { configDiff: [], fileDiff: [] };
  }
  const restored = restoreTenantSeoBundleRollbackSnapshot(current, entry.rollbackSnapshot);
  return {
    configDiff: diffTenantSeoBundleSnapshotWithCurrent(current, entry.rollbackSnapshot),
    fileDiff: diffTenantSeoBundleConfigFiles(current, restored, branding, llmsTemplate),
  };
}

/** Preview config + generated file diff before re-applying a stored history bundle. */
export function previewTenantSeoBundleReapply(
  current: PublicMarketingConfig,
  entry: TenantSeoBundleHistoryEntry,
  branding: MarketingSeoPreviewBranding,
  llmsTemplate?: string,
): {
  bundle: TenantMarketingSeoBundle | null;
  configDiff: TenantSeoBundleImportChange[];
  fileDiff: TenantSeoBundleFileDiff[];
} {
  const bundle = parseHistoryEntryAppliedBundle(entry);
  if (!bundle) {
    return { bundle: null, configDiff: [], fileDiff: [] };
  }
  return {
    bundle,
    configDiff: diffTenantSeoBundleImport(current, bundle),
    fileDiff: diffTenantSeoBundleFiles(current, bundle, branding, llmsTemplate),
  };
}

export function recordTenantSeoBundleHistory(
  config: PublicMarketingConfig,
  input: {
    bundle: TenantMarketingSeoBundle;
    source: TenantSeoBundleHistoryEntry['source'];
    changeCount: number;
    appliedAt?: string;
    rollbackSnapshot?: TenantSeoBundleRollbackSnapshot;
    maxEntries?: number;
    auditLabel?: string;
    auditNote?: string;
    suggestedAuditNote?: string;
    suggestedAuditLabel?: string;
  },
): PublicMarketingConfig {
  const resolvedAuditNote = input.auditNote?.trim() || input.suggestedAuditNote?.trim() || '';
  const entry: TenantSeoBundleHistoryEntry = {
    appliedAt: input.appliedAt ?? new Date().toISOString(),
    exportedAt: input.bundle.exported_at,
    source: input.source,
    changeCount: input.changeCount,
    auditLabel:
      input.auditLabel?.trim() ||
      input.suggestedAuditLabel?.trim() ||
      formatTenantSeoBundleHistoryAuditLabel(input.source, input.changeCount),
    ...(resolvedAuditNote ? { auditNote: resolvedAuditNote.slice(0, 500) } : {}),
    ...(input.bundle.branding?.company_name?.trim()
      ? { brandingCompanyName: input.bundle.branding.company_name.trim() }
      : {}),
    ...(input.rollbackSnapshot ? { rollbackSnapshot: input.rollbackSnapshot } : {}),
    appliedBundle: { ...input.bundle } as unknown as Record<string, unknown>,
  };
  const limit = normalizeTenantSeoBundleHistoryMaxEntries(input.maxEntries);
  const history = trimTenantSeoBundleHistoryEntries([entry, ...config.seoBundleHistory], limit);
  return { ...config, seoBundleHistory: history };
}

export async function fetchAutoApplyTenantSeoBundleTemplateEnabled(): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PUBLIC_MARKETING_AUTO_APPLY_SEO_BUNDLE_TEMPLATE_KEY)
      .maybeSingle();
    if (error || data?.value === undefined || data?.value === null) return false;
    if (typeof data.value === 'boolean') return data.value;
    if (typeof data.value === 'object' && data.value !== null) {
      const enabled = (data.value as Record<string, unknown>).enabled;
      if (typeof enabled === 'boolean') return enabled;
    }
    const raw = String(data.value).trim().toLowerCase();
    return raw === 'true' || raw === '1';
  } catch {
    return false;
  }
}

export async function saveAutoApplyTenantSeoBundleTemplateEnabled(enabled: boolean): Promise<void> {
  const { error } = await supabase.from('app_config').upsert(
    {
      key: PUBLIC_MARKETING_AUTO_APPLY_SEO_BUNDLE_TEMPLATE_KEY,
      value: { enabled },
      category: 'features',
      data_type: 'json',
      is_public: false,
      description: 'Auto-apply platform tenant SEO bundle template when a new organization signs up.',
    },
    { onConflict: 'key' },
  );
  if (error) throw error;
}

/** Apply platform SEO bundle template to a new org (when auto-apply is enabled). */
export async function applyPlatformTenantSeoBundleTemplateToOrg(
  organizationId: string,
): Promise<{ applied: boolean; changeCount: number }> {
  const [enabled, template, platformPartial, maxEntries, platformAuditNotes, platformAuditLabels] =
    await Promise.all([
      fetchAutoApplyTenantSeoBundleTemplateEnabled(),
      fetchPlatformTenantSeoBundleTemplate(),
      fetchPlatformPublicMarketingDefault(),
      fetchTenantSeoBundleHistoryMaxEntries(),
      fetchTenantSeoBundleHistoryAuditNotesBySource(),
      fetchTenantSeoBundleHistoryAuditLabelsBySource(),
    ]);
  if (!enabled || !template) return { applied: false, changeCount: 0 };

  const { data: settings, error: readError } = await supabase
    .from('company_settings')
    .select('public_marketing_config')
    .eq('organization_id', organizationId)
    .maybeSingle();
  if (readError) throw readError;

  const current = resolvePublicMarketingConfig(
    parsePublicMarketingConfig(settings?.public_marketing_config),
    platformPartial,
  );
  const changeCount = diffTenantSeoBundleImport(current, template).length;
  const rollbackSnapshot = exportTenantSeoBundleRollbackSnapshot(current);
  let next = mergeImportedTenantMarketingSeoBundle(current, template);
  next = recordTenantSeoBundleHistory(next, {
    bundle: template,
    source: 'org_signup',
    changeCount,
    rollbackSnapshot,
    maxEntries,
    suggestedAuditNote: resolveTenantSeoBundleHistoryAuditNote(
      'org_signup',
      platformAuditNotes,
      current.seoBundleHistoryDefaultAuditNotes,
    ),
    suggestedAuditLabel: resolveTenantSeoBundleHistoryAuditLabel(
      'org_signup',
      changeCount,
      platformAuditLabels,
      current.seoBundleHistoryDefaultAuditLabels,
    ),
  });

  const { error: writeError } = await supabase
    .from('company_settings')
    .update({ public_marketing_config: serializePublicMarketingConfig(next) })
    .eq('organization_id', organizationId);
  if (writeError) throw writeError;

  return { applied: true, changeCount };
}

export type TenantSeoBundleSnapshotDiffRow = {
  field: string;
  left: string;
  right: string;
};

function formatHubLandingSnapshot(map: TenantSeoBundleRollbackSnapshot['hubPageSeo']): string {
  const paths = Object.keys(map).sort();
  if (!paths.length) return '(none)';
  return paths
    .map((path) => {
      const entry = map[path];
      const title = entry?.title?.trim() ?? '';
      const description = entry?.description?.trim() ?? '';
      return `${path}: ${[title, description].filter(Boolean).join(' · ') || '(empty)'}`;
    })
    .join('; ');
}

function pushSnapshotStringDiff(
  rows: TenantSeoBundleSnapshotDiffRow[],
  field: string,
  left: string,
  right: string,
) {
  if (left === right) return;
  rows.push({
    field,
    left: left || '(empty)',
    right: right || '(empty)',
  });
}

/** Diff two rollback snapshots (e.g. compare history entry states). */
export function diffTenantSeoBundleRollbackSnapshots(
  left: TenantSeoBundleRollbackSnapshot,
  right: TenantSeoBundleRollbackSnapshot,
): TenantSeoBundleSnapshotDiffRow[] {
  const rows: TenantSeoBundleSnapshotDiffRow[] = [];
  pushSnapshotStringDiff(
    rows,
    'Homepage SEO title',
    left.homepageSeoTitle.trim(),
    right.homepageSeoTitle.trim(),
  );
  pushSnapshotStringDiff(
    rows,
    'Homepage SEO description',
    left.homepageSeoDescription.trim(),
    right.homepageSeoDescription.trim(),
  );
  pushSnapshotStringDiff(
    rows,
    'Default og:image',
    left.defaultOgImageUrl.trim(),
    right.defaultOgImageUrl.trim(),
  );
  pushSnapshotStringDiff(
    rows,
    'Hub overrides',
    formatHubLandingSnapshot(left.hubPageSeo),
    formatHubLandingSnapshot(right.hubPageSeo),
  );
  pushSnapshotStringDiff(
    rows,
    'Landing overrides',
    formatHubLandingSnapshot(left.landingPageSeo),
    formatHubLandingSnapshot(right.landingPageSeo),
  );
  pushSnapshotStringDiff(
    rows,
    'Robots disallow paths',
    formatPathList(left.robotsDisallowPaths),
    formatPathList(right.robotsDisallowPaths),
  );
  pushSnapshotStringDiff(
    rows,
    'Robots allow paths',
    formatPathList(left.robotsAllowPaths),
    formatPathList(right.robotsAllowPaths),
  );
  if (left.robotsAiCrawlerPolicy !== right.robotsAiCrawlerPolicy) {
    rows.push({
      field: 'AI crawler policy',
      left: left.robotsAiCrawlerPolicy,
      right: right.robotsAiCrawlerPolicy,
    });
  }
  pushSnapshotStringDiff(
    rows,
    'Blocked AI crawlers',
    formatPathList(left.robotsAiCrawlerUserAgents),
    formatPathList(right.robotsAiCrawlerUserAgents),
  );
  pushSnapshotStringDiff(
    rows,
    'Disabled llms.txt sections',
    formatPathList(left.llmsDisabledSections),
    formatPathList(right.llmsDisabledSections),
  );
  return rows;
}

function escapeCsvCell(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

export function serializeTenantSeoBundleHistoryPinNoteAuditJson(entry: TenantSeoBundleHistoryEntry): string {
  if (!entry.pinNoteAudit || entry.pinNoteAudit.length === 0) return '';
  return JSON.stringify(
    entry.pinNoteAudit.map((row) => ({
      updated_at: row.updatedAt,
      pin_note: row.pinNote,
      action: row.action,
    })),
  );
}

/** Export SEO bundle apply history for audit/download. */
export function serializeTenantSeoBundleHistoryCsv(history: readonly TenantSeoBundleHistoryEntry[]): string {
  const header = [
    'applied_at',
    'exported_at',
    'source',
    'change_count',
    'audit_label',
    'audit_note',
    'pinned',
    'pin_note',
    'pin_note_audit_count',
    'latest_pin_note_audit_action',
    'latest_pin_note_audit_at',
    'pin_note_audit_json',
    'branding_company_name',
    'has_rollback_snapshot',
    'has_applied_bundle',
    'snapshot_homepage_title',
    'snapshot_hub_override_count',
    'snapshot_landing_override_count',
  ];
  const lines = history.map((entry) => {
    const latestPinNoteAudit = getTenantSeoBundleHistoryLatestPinNoteAudit(entry);
    return [
      entry.appliedAt,
      entry.exportedAt,
      entry.source,
      String(entry.changeCount),
      entry.auditLabel ?? formatTenantSeoBundleHistoryAuditLabel(entry.source, entry.changeCount),
      entry.auditNote ?? '',
      entry.pinned ? 'yes' : 'no',
      entry.pinNote ?? '',
      String(entry.pinNoteAudit?.length ?? 0),
      latestPinNoteAudit?.action ?? '',
      latestPinNoteAudit?.updatedAt ?? '',
      serializeTenantSeoBundleHistoryPinNoteAuditJson(entry),
      entry.brandingCompanyName ?? '',
      entry.rollbackSnapshot ? 'yes' : 'no',
      entry.appliedBundle ? 'yes' : 'no',
      entry.rollbackSnapshot?.homepageSeoTitle ?? '',
      entry.rollbackSnapshot ? String(Object.keys(entry.rollbackSnapshot.hubPageSeo).length) : '',
      entry.rollbackSnapshot ? String(Object.keys(entry.rollbackSnapshot.landingPageSeo).length) : '',
    ]
      .map((cell) => escapeCsvCell(cell))
      .join(',');
  });
  return [header.join(','), ...lines].join('\n');
}

export function serializeTenantSeoBundleHistoryJson(history: readonly TenantSeoBundleHistoryEntry[]): string {
  return JSON.stringify(
    history.map((entry) => ({
      applied_at: entry.appliedAt,
      exported_at: entry.exportedAt,
      source: entry.source,
      change_count: entry.changeCount,
      audit_label:
        entry.auditLabel ?? formatTenantSeoBundleHistoryAuditLabel(entry.source, entry.changeCount),
      ...(entry.auditNote ? { audit_note: entry.auditNote } : {}),
      ...(entry.pinned ? { pinned: true } : {}),
      ...(entry.pinNote ? { pin_note: entry.pinNote } : {}),
      ...(entry.pinNoteAudit && entry.pinNoteAudit.length > 0
        ? {
            pin_note_audit: entry.pinNoteAudit.map((row) => ({
              updated_at: row.updatedAt,
              pin_note: row.pinNote,
              action: row.action,
            })),
          }
        : {}),
      ...(entry.brandingCompanyName ? { branding_company_name: entry.brandingCompanyName } : {}),
      ...(entry.rollbackSnapshot
        ? {
            rollback_snapshot: {
              homepage_seo_title: entry.rollbackSnapshot.homepageSeoTitle,
              homepage_seo_description: entry.rollbackSnapshot.homepageSeoDescription,
              default_og_image_url: entry.rollbackSnapshot.defaultOgImageUrl,
              hub_page_seo: entry.rollbackSnapshot.hubPageSeo,
              landing_page_seo: entry.rollbackSnapshot.landingPageSeo,
              robots_disallow_paths: entry.rollbackSnapshot.robotsDisallowPaths,
              robots_allow_paths: entry.rollbackSnapshot.robotsAllowPaths,
              robots_ai_crawler_policy: entry.rollbackSnapshot.robotsAiCrawlerPolicy,
              robots_ai_crawler_user_agents: entry.rollbackSnapshot.robotsAiCrawlerUserAgents,
              llms_disabled_sections: entry.rollbackSnapshot.llmsDisabledSections,
            },
          }
        : {}),
      ...(entry.appliedBundle ? { applied_bundle: entry.appliedBundle } : {}),
    })),
    null,
    2,
  );
}
